// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { OrderParamsContainer } from "./cache/OrderParams/OrderParamsContainer.js";
import { PanelNames } from "../Controls/UtilsClasses/FactoryConstants.js";
import { CustomErrorClass, ErrorInformationStorage } from "./ErrorInformationStorage.js";
import { CustomEvent } from "../Utils/CustomEvents.js";
import { Connection } from "./Connection.js";
import { FINISH_BLOCK_TRANSFER } from "../Utils/CommonConst.js";
import { Periods } from "../Utils/History/TFInfo.ts";
import { AssetTradingMode } from "../Utils/Asset/AssetConst.js";
import { OptionPutCall } from "../Utils/Instruments/OptionPutCall.js";
import { HistoricalBM } from "./cache/HistoricalBM.js";
import { DirectInstrumentMessage, DirectPropertyMessage } from "../Utils/DirectMessages/DirectMessagesImport.ts";
import { DirectQuoteMessage } from "../Utils/DirectMessages/DirectMessagesImport.ts";
import { DirectReportMessage } from "../Utils/DirectMessages/DirectMessagesImport.ts";
import { DirectRiskWarningMessage } from "../Utils/DirectMessages/DirectMessagesImport.ts";
import { DirectMessageCode } from "../Utils/DirectMessages/DirectMessagesImport.ts";
import { OptionContract } from "../Utils/Instruments/OptionContract.js";
import { OperationType } from "../Utils/Trading/OperationType.ts";
import { OrderType } from "../Utils/Trading/OrderType.js";
import { ProductType } from "../Utils/Instruments/ProductType.js";
import { MarginTypes } from "../Utils/Instruments/MarginTypes.js";
import { PriceLimitMeasure } from "../Utils/Instruments/PriceLimitMeasure.js";
import { InstrDateSettings } from "../Utils/Instruments/InstrDateSettings.js";
import { VariableTick } from "../Utils/Instruments/VariableTick.js";
import { OptionTradingStyle } from "../Utils/Instruments/OptionTradingStyle.js";
import { CloseAccountResponseStatus, DirectPropertyOwner, MarginModes, SubscribeType } from "../Utils/Enums/Constants.js";
import { OrderTif, TIF } from "../Utils/Trading/OrderTif.js";
import { InstrumentTypes } from "../Utils/Instruments/InstrumentType.js";
import { OrderStatus } from "../Utils/Trading/OrderStatus.js";
import { OrderExecutionType } from "../Utils/Trading/OrderExecutionType.js";
import { AccountType } from "../Utils/Account/AccountType.js";
import { EventType } from "./cache/Event.js";
import { NewTrade } from "./cache/NewTrade.js";
import { HistoryType } from "../Utils/History/HistoryType.ts";
import { MathUtils } from "../Utils/MathUtils.js";
import { Statement } from "./cache/Statement.js"
import { AccountOperationReport } from "./cache/AccountOperationReport.js";
import { CommissionPlan } from "./cache/Commissions/CommissionPlan.js";
import { News } from "./cache/News.js"
import { OrderHistory } from "./cache/OrderHistory.js"
import { BrokerMessageResponseType } from "../Utils/Notifications/BrokerMessageResponseType.js";
import { Idea, TradingSystem } from "./cache/Idea.js"
import { StrategySubscriptionStatus } from "../Utils/TradingIdeas/StrategySubscriptionStatus.js";
import { NotificationsCounterChangedEvent } from "../Utils/Notifications/NotificationsCounterChangedEvent.js";
import { SpreadPlan } from "./cache/SpreadPlan.js";
import { EventCache } from "./cache/EventCache.js";
import { Route } from "./cache/Route.js";
import { InstrumentBalancer, UserBalancer, UserBalancesAssetBalanceFields } from "./InstrumentBalancer.js";
import { SourceCollection } from "./cache/SourceCollection.js";
import { SessionsContainer, TradeSessionStatus } from "../Utils/Session/Sessions.ts";
import { InstrumentType } from "./cache/InstrumentType.js";
import { CrossratesPlans } from "./cache/CrossratesPlans.js";
import { CustodialPlans } from "./cache/CustodialPlans.js";
import { CrossRateCache } from "./cache/CrossRateCache.js";
import { CountryCache } from "./cache/CountryCache.js";
import { Resources } from "./properties/Resources.js";
import { QuickTableComparingType } from "../Utils/QuickTableMisc/QuickTableComparingType.js";
import { RulesSet } from "../Utils/Rules/RulesSet.js";
import { PriceFormatter } from "../Utils/Instruments/PriceFormatter.js";
import { Asset, AssetType } from "./cache/Asset.js";
import { Account } from "./cache/Account.ts";
import { AccountFeature } from "./cache/AccountEnums.ts";
import { User } from "./cache/User.ts";
import { AlgorithmCache } from "./cache/AlgorithmCache.js";
import { InstrumentSpecificType } from "../Utils/Instruments/InstrumentSpecificType.js";
import { QuoteCache } from "./cache/QuoteCache.js";
import { GeneralSettings } from "../Utils/GeneralSettings/GeneralSettings.js";
import { Order } from "./cache/Order.js";
import { Position, ServerCalculation } from "./cache/Position.js";
import { RiskPlan } from "./cache/RiskPlan.js";
import { InstrumentUtils } from "../Utils/Instruments/InstrumentUtils.js";
import { Instrument } from "./cache/Instrument.js";
import { AllowedReport } from "./cache/AllowedReport.js";
import { brokerMessageScreenHandler, messageBoxHandler, riskWarningMessageScreenHandler, snapshotScreenHandler, workspaceManagerHandler } from "../Utils/AppHandlers.js";
import { PortfolioCache } from "../Portfolio/PortfolioCache.js";
import { TradingLockUtils } from "../Utils/TradingLockUtils.js";
import { OrderExecutor } from "./Trading/OrderExecutor.js";
import { ExternalLinksCache } from "./cache/ExternalLinksCache.js";
import { EntitlementManager } from "./cache/EntitlementManager.js";
import { AlertManager } from "./cache/AlertManager.js";
import { TextMessageType } from "../Utils/Notifications/TextMessageType.js";
import { RulesCache } from "./cache/RulesCache.js";
import { ApplicationInfo } from "./ApplicationInfo.js";
import { IsAllowed } from "./IsAllowed.js";
import { LocalStorage } from "./LocalStorage.js";
import { PlacedFrom } from "../Utils/Trading/PlacedFrom.js";
import { FundingRateMarkupPlans } from "./cache/FundingRateMarkupPlans.js";

let _DataCache = function ()
{
    new IsAllowed(this);
    ErrorInformationStorage.SetDataCache(this);

    // Events
    this.OnAddAccount = new CustomEvent();
    this.OnUpdateAccount = new CustomEvent();
    this.OnRemoveAccount = new CustomEvent();

    this.OnAddInstrument = new CustomEvent();
    this.OnUpdateInstrument = new CustomEvent();

    this.OnAddPosition = new CustomEvent();
    this.OnUpdatePosition = new CustomEvent();
    this.OnRemovePosition = new CustomEvent();
    this.OnPositionExerciseStatusChanged = new CustomEvent();

    //CorporateAction
    this.OnAddCorporateAction = new CustomEvent();
    this.OnUpdateCorporateAction = new CustomEvent();
    this.OnRemoveCorporateAction = new CustomEvent();

    this.OnAddOrder = new CustomEvent();
    this.OnUpdateOrder = new CustomEvent();
    this.OnRemoveOrder = new CustomEvent();
    this.OnActivateOrder = new CustomEvent();

    this.OnAddSLOrderToPosition = new CustomEvent();
    this.OnRemoveSLOrderFromPosition = new CustomEvent();

    this.OnAddTPOrderToPosition = new CustomEvent();
    this.OnRemoveTPOrderFromPosition = new CustomEvent();

    this.OnUpdateAssetEvent = new CustomEvent();

    this.OnAddNewTradeEvent = new CustomEvent();
    this.OnRemovedTradeEvent = new CustomEvent();

    // TODO. Rename if necessary.
    this.OnReportMessage = new CustomEvent()
    this.OnMessageForTicketViewer = new CustomEvent()

    this.AddOrderHistoryEvent = new CustomEvent()
    this.RemovedOrderHistoryEvent = new CustomEvent()

    this.orderHistoryArray = []
    this.OrderHistoryReportCompleted = false;

    this.filledOrdersArray = []
    this.FilledOrdersReportCompleted = false;

    this.StatementArray = []
    this.StatementReportCompleted = false;

    this.MamSummaryArray = [];
    this.MamSummaryReportCompleted = false;

    this.realTimeFilledOrdersArray = []

    this.ExistingInstrumentTypesSet = {}

    this.reportDict = {}
    this.OrderDict = new Object();
    this.PositionDict = new Object();
    this.PositionDictCorporateAction = new Object();
    this.Instruments = new Object();
    this.InstrumentTradableIDChache = new Object();
    this.InstrumentTradableIDChacheContiniousContract = new Object();
    this.InstrumentsArray = new Array();
    this.InstrumentsByTradableIdCache = new Object();
    this.InstrumentsTableByIDs = new Object();
    this.InstrumentsContiniousContractIDS = new Object();
    this.InstrumentsByTradingSessionsId = new Object(); // {ID:[...]}
    this.InstrumentsBySourceRouteIdTradableId = new Object();   // список инструментов для которых инструмент с TradableId:RouteId является DataSource инструментом
    this.Accounts = new Object();
    this.SessionDict = new Object();
    this.Loaded = false;
    //+++++ нужно поправить
    this.FAllowedHistoryTypes = new Array(HistoryType.BID, HistoryType.LAST, HistoryType.ASK);
    this.CrossRateCache = new CrossRateCache(this);

    this.BrendingRules = null;

    this.assetCollection = new Object();

    //chart place order default settings
    this.TIF = new Array();
    this.TIF_Limit_And_Stop_Limit = new Array();
    this.TIF_Stop = new Array();
    //chart place order default settings

    // TODO. cleanup.
    this.UserLogin = '';

    this.NonFixedList = true;
    this.OrderParameterContainer = new OrderParamsContainer();

    /// <summary>
    /// Кеш котировок
    /// </summary>
    this.FQuoteCache = new QuoteCache(this);
    this.FQuoteCache.onReloadHistory = this.quoteCache_OnReload.bind(this);

    //for order placing etc
    this.FOrderExecutor = new OrderExecutor(this, this.FQuoteCache);

    /// <summary>
    /// ONLY TRADABLE routes
    /// </summary>
    this.routes = new Object();
    /// <summary>
    /// ONLY INFO routes
    /// </summary>
    this.infoRoutes = new Object();

    this.newTradesDict = new Object();
    this.Exchanges = new Object();
    this.TradingExchanges = new Array();
    this.tradeSessionStatusCache = new Object();

    this.riskPlanDict = {};
    this.customListsDict = {};
    this.lastRiskPlanItem = null;
    RiskPlan.showProductType = false;
    this.instrTypes = {};

    this.deferredMessages = {};
    this.deferredInsReq = {};

    //
    this.OnReintialize = new CustomEvent();

    this.instrNames = new Object();

    this.InstrumentBalancerTable = new Object();
    //this.userBalanserTable = new Object();

    this.baseCurrency = 'USD';          // валюта сервера
    this.userBaseCurrency = 'USD';      // валюта юзера
    this.filledOrdersCurrency = 'USD';  // валюта юзера для панели filldOrder

    this._userHasSameCurrencyOnAllAccounts = null;

    this.LoadedResolve = null;

    this.EmptyUserBalancer = new UserBalancer(null);

    //TODO?
    this.ContractsCache = new Object();
    this._cachedInsListByContractID = new Object();

    this.InstrumentsByIdCache = new Object();


    this.SpreadPlans = new Object();
    this.SpreadPlan = null;

    this.FLogin = null;
    this.LoginUser = null;

    this.AggregatedInstruments = new Object();
    //this.cachedInstrumentsWR = null;

    this.PortfolioCache = new PortfolioCache(this);
    this.AlgorithmCache = new AlgorithmCache(this);

    this.CommissionPlans = new Object();
    this.OnCommissionPlanUpdate = new CustomEvent();

    this.CrossratesPlans = new CrossratesPlans(this);    //#101199

    this.RulesCache = new RulesCache(this);

    this.CompanyName = null;
    this.BrokerInformation = null;
    this.PoweredByName = null;
    this.PoweredByURL = null;
    this.BrandingVisibleInstrumentLogo = false;

    this.ExternalLinksCache = new ExternalLinksCache();

    this.SubscribedToNewsRoute = null;
    this.News = new Object();
    this.OnAddNewsEvent = new CustomEvent();
    this.OnRemoveNewsEvent = new CustomEvent();
    this.userAndConnectionNameEncryptedByMD5 = null;

    this.tradingSystems = new Object();
    this.tradingIdeas = new Object();
    this.OnTradingIdeaAdd = new CustomEvent();
    this.OnTradingIdeaCancel = new CustomEvent();
    this.OnTradingIdeaFirstOpen = new CustomEvent();
    this.OnTradingSignalRequestSend = new CustomEvent();
    this.OnTradingSystemsChange = new CustomEvent();
    this.OnTradingSystemUnsubscribe = new CustomEvent();

    this.OnSnapshotResponseReceived = new CustomEvent();

    this.OnProcessBrokerMessage = new CustomEvent();

    this.OnBMCounterMsgReceived = new CustomEvent()

    this.CountryCache = new CountryCache();
    this.CountryCache.InitImages();

    this.AlertManager = new AlertManager(this);
    this.EntitlementManager = new EntitlementManager(this);

    this.CustodialPlans = new CustodialPlans();
    this.ActiveUser = null;//TODO без понятия что с этим делать
    this._userGroups = new Object();
    this.Users = new Object();

    this.FundingRateMarkupPlans = new FundingRateMarkupPlans();

    this.OnTradingSessionStatusUpdated = new CustomEvent();
    this.productTypeEnabilityCache = new Object();

    this.SourceCollection = new SourceCollection();

    this.OnRecalculatedBalances = new CustomEvent();

    //TODO  MainAccountNew
    this.MainAccountNew = null;

    this.closeAccountApproved = false;
    this.closeAccountRequestID = null;
    this.OnChangeCloseAccountRequestID = new CustomEvent();
};

_DataCache.prototype.cleanup = function ()
{
    // Data start to load
    this.Loaded = false;

    this.CustodialPlans.Clear();
    this.AlertManager.Clear();          // тут должна произойти отписка алертами от котировок, это должно случиться раньше чем будут очищены инструменты иначе не удастся отправить корректный запрос на отписку
    this.EntitlementManager.Clear();
    this.FundingRateMarkupPlans.Clear();

    // lost clean
    this.UserLogin = '';
    this.instrNames = new Object();
    this.InstrumentsByIdCache = new Object();

    this.FLogin = null;
    this.LoginUser = null;

    this.orderHistoryArray = []
    this.OrderHistoryReportCompleted = false;
    this.filledOrdersArray = []
    this.FilledOrdersReportCompleted = false;
    this.StatementArray = []
    this.StatementReportCompleted = false;
    this.MamSummaryArray = [];
    this.MamSummaryReportCompleted = false;
    this.realTimeFilledOrdersArray = []

    this.ExistingInstrumentTypesSet = {}
    this.reportDict = {}
    this.OrderDict = new Object();
    this.newTradesDict = new Object();
    this.PositionDict = new Object();
    this.PositionDictCorporateAction = new Object();
    this.Instruments = new Object();
    this.InstrumentTradableIDChache = new Object();
    this.InstrumentTradableIDChacheContiniousContract = new Object();
    this.InstrumentsByTradableIdCache = new Object();
    this.InstrumentsArray = new Array();
    this.InstrumentsTableByIDs = new Object();
    this.InstrumentsContiniousContractIDS = new Object();
    this.InstrumentsByTradingSessionsId = new Object();
    this.InstrumentsBySourceRouteIdTradableId = new Object();
    this.Accounts = new Object();
    this.SessionDict = new Object();
    //+++++ нужно поправить
    this.FAllowedHistoryTypes = new Array(HistoryType.BID, HistoryType.LAST, HistoryType.ASK, HistoryType.BIDASK_AVG);
    this.TIF = new Array();
    this.TIF_Limit_And_Stop_Limit = new Array();
    this.TIF_Stop = new Array();

    this.CrossRateCache.Clear();
    EventCache.Clear();
    this.FQuoteCache.clear();
    this.PortfolioCache.Clear();
    this.AlgorithmCache.Clear();
    this.RulesCache.Clear();

    this.routes = new Object();
    this.infoRoutes = new Object();
    this.Exchanges = new Object();
    this.TradingExchanges.length = 0;
    this.tradeSessionStatusCache = new Object();

    this.assetCollection = new Object();

    this.riskPlanDict = {}
    this.customListsDict = {}
    this.lastRiskPlanItem = null;
    RiskPlan.showProductType = false;
    this.deferredMessages = {};
    this.deferredInsReq = {};

    this.instrTypes = new Object();
    //
    //this.OnReintialize = new CustomEvent();

    this.InstrumentBalancerTable = new Object();
    //this.userBalanserTable = new Object();

    this.ContractsCache = new Object();
    this._cachedInsListByContractID = new Object();

    this.BrendingRules = null;
    this.SpreadPlans = new Object();
    this.SpreadPlan = null;

    this.AggregatedInstruments = new Object();
    //this.cachedInstrumentsWR = null;

    this.CommissionPlans = new Object();

    this.CrossratesPlans.Clear();

    this.CompanyName = null;
    this.BrokerInformation = null;
    this.PoweredByName = null;
    this.PoweredByURL = null;
    this.BrandingVisibleInstrumentLogo = false;

    this.ExternalLinksCache.Clear();

    this.News = new Object();
    this.SubscribedToNewsRoute = null;
    this.userAndConnectionNameEncryptedByMD5 = null;

    this.tradingSystems = new Object();
    this.tradingIdeas = new Object();

    this._userGroups = new Object();
    this.Users = new Object();
    this.productTypeEnabilityCache = new Object();

    this._userHasSameCurrencyOnAllAccounts = null;

    this.closeAccountRequestID = null;
    this.closeAccountApproved = false;
};

///+++++++
_DataCache.prototype.getInstrumentByName = function (name)
{
    if (!name)
        return null;

    var filter = name.toUpperCase();

    var ans = this.Instruments[filter];
    if (ans)
        return ans;

    //For ContiniousContract searching
    let splited = filter.split("|")
    if (splited.length > 1)
    {
        filter = splited[0]

        var ans = this.Instruments[filter];
        if (ans)
            return ans;

        let splitedFilter = filter.split(InstrumentUtils.SEPARATOR);
        if (splitedFilter.length > 1)
        {
            let Id = splited[1]
            let Route = splitedFilter[1];
            return this.getInstrumentByInstrumentIdAndRoute(Id, Route)
        }
    }
    return null;
}
_DataCache.prototype.getInstrumentByInstrumentIdAndRoute = function (Id, Route)
{
    let tmp = this.InstrumentsContiniousContractIDS[Id]
    if (!tmp)
        return null;
    tmp = tmp[Route]
    if (!tmp)
        return null;

    return tmp.CurrentContiniousContract;
}

// Direct vendor scheme.
_DataCache.prototype.getInstrument = function (symbol, route)
{
    return this.getInstrumentByName(InstrumentUtils.GetFullName(symbol, route));
};

_DataCache.prototype.getInstrumentByTradable_ID = function (Tradable_ID, route)
{
    let routeGroup = this.InstrumentsTableByIDs[route];
    if (routeGroup)
        return routeGroup[Tradable_ID] || null;
    else
        return null;
};

_DataCache.prototype.getInstrumentByTradable_ID_first = function (Tradable_ID)
{
    // Сначала ищем торговый
    for (let insK in this.InstrumentsTableByIDs)
    {
        let route = this.getRouteById(insK)
        if (route.IsTradable)
        {
            let ins = this.InstrumentsTableByIDs[insK][Tradable_ID];
            if (ins)
                return ins
        }
    }
    // Сначала ищем потом инфо
    for (let insK in this.InstrumentsTableByIDs)
    {
        let route = this.getRouteById(insK)
        if (route.IsInfo)
        {
            let ins = this.InstrumentsTableByIDs[insK][Tradable_ID];
            if (ins)
                return ins
        }
    }
    return null;
};

_DataCache.prototype.getCachedInstrumentDict = function ()
{
    return this.Instruments
}

_DataCache.prototype.getInstrumentsByTradableID = function (tradableID)
{
    if (!tradableID || !this.InstrumentsByTradableIdCache[tradableID])
        return []

    return this.InstrumentsByTradableIdCache[tradableID]
}

_DataCache.prototype.getAnyInstrument = function ()
{
    let interiorID = null

    for (interiorID in this.Instruments)
        break

    return this.Instruments[interiorID]
}

_DataCache.prototype.getAccounts = function ()
{
    return this.Accounts
}

_DataCache.prototype.getNumberOfAccounts = function ()
{
    let result = 0;
    for (let accID in this.Accounts)
        result++;

    return result;
}

_DataCache.prototype.GetAccountById = function (id)
{
    if (!this.Accounts || !id)
        return null;

    if (this.Accounts[id])
        return this.Accounts[id];

    //TODO remove mb
    var keys = Object.keys(this.Accounts);
    var i;
    var len = keys.length;

    for (i = 0; i < len; i++)
    {
        var key = keys[i];
        if (key.toUpperCase() === id.toUpperCase())
            return this.Accounts[key];
    }
    return null;
}

_DataCache.prototype.GetAccountByIdOrName = function (fullAccString)
{
    if (!this.Accounts || !fullAccString)
        return null;

    let byId = this.GetAccountById(fullAccString);
    if (byId)
        return byId;

    var keys = Object.keys(this.Accounts);
    var i;
    var len = keys.length;

    let filter = fullAccString.toUpperCase();
    for (i = 0; i < len; i++)
    {
        var key = keys[i];
        var acc = this.Accounts[key];
        if (acc.FullAccString.toUpperCase() === filter)
            return acc;
    }

    return null;
}

_DataCache.prototype.GetAccountByNumber = function (fullAccString)
{
    if (!this.Accounts || !fullAccString)
        return null;

    let acc = this.Accounts[fullAccString]

    if (!acc)
        acc = null;

    return acc
}

_DataCache.prototype.GetAssetById = function (id)
{
    return this.assetCollection[id] || null
}

_DataCache.prototype.GetAssetByName = function (name)
{
    var list = Object.keys(this.assetCollection);

    for (let i = 0, len = list.length; i < len; i++)
    {
        let value = this.assetCollection[list[i]];
        if (name === value.Name)
            return value;
    }
    return null;
}

_DataCache.prototype.getAssetDict = function ()
{
    return this.assetCollection
}

//#region Direct

_DataCache.prototype.NewMessage = function (message)
{
    switch (message.Code)
    {
        case DirectMessageCode.CODE_ORDER_HISTORY:
            this.addOrderHistoryMessage(message)
            break
        case DirectMessageCode.CODE_ACCOUNT_STATUS:
            this.HandleAccountStatusMessageDirect(message);
            break;
        case DirectMessageCode.CODE_PROPERTY:
            this.HandlePropertyMessageDirect(message);
            break;
        case DirectMessageCode.CODE_INSTRUMENT:
            this.HandleInstrumentMessageDirect(message);
            break;
        case DirectMessageCode.CODE_OPEN_POSITION:
            this.HandlePositionMessageDirect(message);
            break;
        case DirectMessageCode.CODE_CLOSE_POSITION:
            this.HandleClosePositionMessageDirect(message);
            break;
        case DirectMessageCode.CODE_OPEN_ORDER:
            this.HandleOpenOrderMessageDirect(message);
            break;
        case DirectMessageCode.CODE_CANCEL_ORDER:
        case DirectMessageCode.CODE_REMOVE_ORDER:
            this.TryRemoveOrder(message.OrderId)
            break;
        case DirectMessageCode.CODE_SESSION:
            this.HandleSessionMessageDirect(message);
            break;
        case DirectMessageCode.CODE_ASSET_TYPE_MESSAGE:
            this.HandleDirectAssetMessage(message);
            break;
        case DirectMessageCode.CODE_CROSS_RATE:
            this.HandleCrossRateMessageDirect(message);
            break;
        case DirectMessageCode.CODE_ROUTE:
            this.AddRoute(message);
            break;
        case DirectMessageCode.CODE_XML_REPORT:
            this.HandleReportMessageDirect(message);
            break;
        case DirectMessageCode.CODE_REPORT_TYPE_MESSAGE:
            this.handleReportTypeMessage(message)
            break
        case DirectMessageCode.CODE_QUOTE_1:
        case DirectMessageCode.CODE_QUOTE_2:
        case DirectMessageCode.CODE_QUOTE_3:
        case DirectMessageCode.CODE_QUOTE_INSTRUMENT_DAY_BAR:
        case DirectMessageCode.CODE_INSTRUMENT_PRICES:
            this.handleQuoteMessage(message)
            break
        case DirectMessageCode.CODE_TRADE:
            this.AddNewTrade(message);
            break;
        case DirectMessageCode.CODE_PFIX_RISK_PLAN_MESSAGE:
            this.addRiskPlan(message)
            break
        case DirectMessageCode.CODE_TRADE_SESSION_STATUS_MESSAGE:
            this.processTradeSessionStatusMessage(message)
            break
        case DirectMessageCode.CODE_CROSSRATES_PLAN_MESSAGE:
            this.ProcessCrossratesPlanMessage(message)
            break
        case DirectMessageCode.CODE_INSTRUMENT_TYPE:
            this.AddInstrumentType(message);
            break;
        case DirectMessageCode.CODE_SPREAD_PLAN_MESSAGE:
            this.AddSpreadPlan(message);
            break;
        case DirectMessageCode.CODE_PFIX_TRADE_INSTRUMENT:
            this.ProcessInstrumentUpdateMessage(message);
            break;
        case DirectMessageCode.CODE_PFIX_TRADE_SESSION_SWITCH:
            this.ProcessTradeSessionSwitchMessage(message);
            break;
        case DirectMessageCode.CODE_BROKER_MESSAGE:
            this.ProcessBrokerMessage(message);
            break;
        case DirectMessageCode.CODE_COMMISSION_PLAN_MESSAGE:
            this.ProcessCommissionPlanMessage(message);
            break;
        case DirectMessageCode.CODE_TRADING_SYSTEM_MESSAGE:
            this.ProcessTradingSystemMessage(message);
            break;
        case DirectMessageCode.CODE_TRADING_SYSTEM_REMOVE_MESSAGE:
            this.ProcessTradingSystemRemoveMessage(message);
            break;
        case DirectMessageCode.CODE_TRADING_SIGNAL_MESSAGE:
            this.ProcessTradingSignalMessage(message);
            break;
        case DirectMessageCode.CODE_TRADING_SIGNAL_SEEN_MESSAGE:
            this.ProcessTradingSignalSeenMessage(message);
            break;
        case DirectMessageCode.CODE_TRADING_SIGNAL_CANCEL_MESSAGE:
            this.ProcessTradingSignalCancelMessage(message);
            break;
        case DirectMessageCode.CODE_SUBSCRIPTION_STRATEGY_MESSAGE:
            this.ProcessSubscriptionStrategyMessage(message);
            break;
        case DirectMessageCode.CODE_PFIX_EXTERNAL_LINK_MESSAGE:
        case DirectMessageCode.CODE_PFIX_EXTERNAL_LINK_REMOVE_MESSAGE:
            this.ExternalLinksCache.NewMessage(message)
            break;
        case DirectMessageCode.CODE_PFIX_CUSTODIAL_PLAN_MESSAGE:
            this.ProcessCustodialPlanMessage(message);
            break;
        case DirectMessageCode.CODE_RISK_WARN_MESSAGE:
            this.ProcessRiskRuleWarningMessge(message);
            break;

        case DirectMessageCode.CODE_ALERT_MESSAGE:
        case DirectMessageCode.CODE_REPLACE_ALERT_MESSAGE:
        case DirectMessageCode.CODE_CONTROL_ALERT_COMAND_MESSAGE:
            this.AlertManager.NewMessage(message)
        case DirectMessageCode.CODE_NEWS_SUBSCRIBE_RESPONSE:
            this.ProcessNewsSubscribeResponse(message);
            break;
        case DirectMessageCode.CODE_NEWS_MESSAGE:
            this.ProcessNewsMessage(message);
            break;

        case DirectMessageCode.CODE_USER_GROUP:
            this.handleUserGroupMSG(message)
            break
        case DirectMessageCode.CODE_GET_SNAPSHOT_RESPONSE:
            this.ProcessSnapshotResponse(message);
            break;
        case DirectMessageCode.CODE_SUBSCRIBE_RESPONSE_MESSAGE:
            this.ProcesSubscribeResponseMessage(message);
            break;
        case DirectMessageCode.CODE_PFIX_PRODUCT_MESSAGE:
        case DirectMessageCode.CODE_PFIX_PRODUCT_DOCUMENT_RESP:
        case DirectMessageCode.CODE_PFIX_PRODUCT_SUBSCRIPTION_MANAGEMENT_RESP:
            this.EntitlementManager.NewMessage(message)
            break;
        case DirectMessageCode.CODE_SOURCE_LIST:
            this.ProcessSourceMappingMessage(message);
            break;
        case DirectMessageCode.CODE_PORTFOLIO_STATISTICS:
        case DirectMessageCode.CODE_PORTFOLIO_MODEL:
        case DirectMessageCode.CODE_PORTFOLIO_SYSTEM:
        case DirectMessageCode.CODE_PORTFOLIO_INVESTOR:
        case DirectMessageCode.CODE_PORTFOLIO_REALTIME_STATISTICS:
            if (this.PortfolioCache)
                this.PortfolioCache.NewMessage(message);
            break;
        case DirectMessageCode.CODE_PFIX_ALGORITHM_SUBSCRIBE_MESSAGE:
        case DirectMessageCode.CODE_PFIX_ALGORITHMS_AVAILABLE_MESSAGE:
        case DirectMessageCode.CODE_PFIX_ALGORITHM_MARKET_CONSENSUS_MESSAGE:
        case DirectMessageCode.CODE_PFIX_ALGORITHM_GAINERS_LOSERS_MESSAGE:
        case DirectMessageCode.CODE_PFIX_ALGORITHM_HIGH_LOW_MESSAGE:
        case DirectMessageCode.CODE_PFIX_ALGORITHM_TOP_VOLUME_MESSAGE:
        case DirectMessageCode.CODE_PFIX_ALGORITHM_HISTORICAL_GAINER_LOSER_TOPS_MESSAGE:
            if (this.AlgorithmCache)
                this.AlgorithmCache.NewMessage(message);
            break;
        case DirectMessageCode.BM_COUNTER_MESSAGE:
            this.ProcessBMCounterMessage(message)
            break
        case DirectMessageCode.CODE_PRICE_LIMITS_MESSAGE:
            this.ProcessPriceLimitsMessage(message)
            break

        case DirectMessageCode.CUSTOM_LIST_MESSAGE:
            this.processCustomLists(message)
            break

        case DirectMessageCode.CODE_PFIX_FUNDING_RATE_MARKUP_PLAN_MESSAGE:
            this.ProcessFundingRateMarkupPlanMessage(message);
            break;

        case DirectMessageCode.CODE_PFIX_CLOSE_ACCOUNT_RESPONSE_MESSAGE:
            this.ProcessCloseAccountResponseMessage(message);
            break;
    }
};

_DataCache.prototype.processCustomLists = function (message)
{
    if (!message)
        return;

    let dict = this.customListsDict
    dict[message.Id] = message;
}

_DataCache.prototype.addRiskPlan = function (msg)
{
    let id = msg.Id
    let riskPlanDict = this.riskPlanDict
    let riskPlan = null
    if (!riskPlanDict.hasOwnProperty(id))
        riskPlan = riskPlanDict[id] = new RiskPlan()
    else
        riskPlan = riskPlanDict[id]

    if (riskPlan)
        riskPlan.update(this, msg)
    else
        console.log("no riskPlan")

    this.lastRiskPlanItem = riskPlan

    for (let i = 0; i < msg.Data.length; i++)
        this.productTypeEnabilityCache[msg.Data[i].Item.ProductType] = true;
}

_DataCache.prototype.AddInstrumentType = function (msg)
{
    let type = new InstrumentType(msg, this)
    this.instrTypes[type.TypeId] = type;
}

_DataCache.prototype.getInstrumentTypeById = function (typeId)
{
    let type = this.instrTypes[typeId];
    return type || null;
}

_DataCache.prototype.FindInstrumentTypes = function (typeId, list)
{
    list.push(typeId);
    try
    {
        for (let type in this.instrTypes)
        {
            type = this.instrTypes[type]
            if (type.SuperTypeId == typeId)
                this.FindInstrumentTypes(type.Id, list);
        }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
    }
}

_DataCache.prototype.processTradeSessionStatusMessage = function (msg)
{
    let tradeSessionStatusCache = this.tradeSessionStatusCache
    let sessionStatus = tradeSessionStatusCache[msg.Id]
    if (sessionStatus)
    {
        sessionStatus.update(msg)

        this.OnTradingSessionStatusUpdated.Raise(sessionStatus)
    }
    else
    {
        sessionStatus = new TradeSessionStatus(msg)
        tradeSessionStatusCache[sessionStatus.Id] = sessionStatus
    }
}

_DataCache.prototype.handleQuoteMessage = function (msg)
{
    this.FQuoteCache.newQuoteMessage(msg)
}

// TODO. Rename.
_DataCache.prototype.HandleReportMessageDirect = function (msg)
{
    if (Resources.isHidden('reports.' + msg.Name))
        return

    this.OnReportMessage.Raise(msg)
    EventCache.Add(EventCache.FromReportMessage(msg))
};

_DataCache.prototype.HandleDirectAssetMessage = function (assetMessage)
{
    var asset = null;
    if (this.assetCollection.hasOwnProperty(assetMessage.Id))
    {
        asset = this.assetCollection[assetMessage.Id];
        asset.FillByMessage(assetMessage);
    }
    else
    {
        asset = new Asset(this, assetMessage);
        this.assetCollection[assetMessage.Id] = asset;
    }

    this.OnUpdateAssetEvent.Raise(this, asset);
};

_DataCache.prototype.HandleCrossRateMessageDirect = function (msg)
{
    this.CrossRateCache.Update(msg);
};

_DataCache.prototype.HandleSessionMessageDirect = function (msg)
{
    let sessContainer = this.GetSession(msg.Id) || this.AddSession(msg);
    if (!sessContainer) return;

    sessContainer.CurrentSessionId = msg.CurrentSessionId;
    sessContainer.UseIntradayMarginCoef = msg.UseIntradayMarginCoef;
    sessContainer.TimeZoneOffset = msg.TimeZoneOffset;
    sessContainer.BlockTrading = msg.BlockTrading;
    sessContainer.ServerBeginDayDate = msg.ServerBeginDayDate;
    sessContainer.TimeOffset = msg.TimeOffset;

    if (msg.Sessions)
        sessContainer.Sessions = msg.Sessions;

    if (this.Loaded)
    {
        let arr = this.Instruments;
        for (let key in arr)
        {                                                // Apply session to instruments
            let item = arr[key];
            if (item.TradingSessionsId == sessContainer.Id)
            {
                item.TradingSessionsList = sessContainer.Sessions;
                item.CurrentTradingSession = sessContainer.GetSessionByID(sessContainer.CurrentSessionId);
                item.BlockTradingBySession = sessContainer.BlockTrading;
                this.OnUpdateInstrument.Raise(item);
            }
        }

        GeneralSettings.SettingsChangedRaise();
    }
};

_DataCache.prototype.handleReportTypeMessage = function (msg)
{
    if (msg.accessType & AllowedReport.AccessType.User)
        this.createAllowedReport(
            AllowedReport.REPORT_KEY_PREFIX + msg.name, msg.id, msg.parameters)

    if (msg.accessType & AllowedReport.AccessType.Technical)
        this.createAllowedReport(
            AllowedReport.TECHNICAL_REPORT_KEY_PREFIX + msg.name, msg.id, msg.parameters)
}

// TODO.
_DataCache.prototype.HandlePropertyMessageDirect = function (propertyMessage)
{
    if (propertyMessage.Name === FINISH_BLOCK_TRANSFER)
    {
        if (this.NeedRedirectToPortfolio())    // doc. раздел 3) -> третий пункт с белым кружочко:  https://docs.google.com/document/d/1Tnb1LqXehcolTHU2ZEOywAuHLcYOXWA3Grsjddu0C-E/edit#heading=h.9o7dj749n3zi
            return

        if (Connection.vendor)  // эта проверка необходима для работы Unit-тестов
            this.FLogin = Connection.vendor.login
        this.LoginUser = this.getUserByLogin(this.FLogin);
        //TODO  MainAccountNew
        if (this.LoginUser && this.LoginUser.Accounts.length)
            this.MainAccountNew = this.LoginUser.Accounts[0]
        else
            this.MainAccountNew = null;

        this.Loaded = true

        this.AlertManager.AlertsVisible = !Resources.isHidden("screen.Alerts.visibility") && this.isAllowedForMyUser(RulesSet.FUNCTION_ALERTS, null);
        this.AlertManager.ProcessDeferredMessages();

        //кешируем инструменты в массив.

        if (!this.NonFixedList)
        {
            let keys = Object.keys(this.Instruments);
            this.InstrumentsArray = keys.map(function (value)
            {
                return this.Instruments[value];
            }.bind(this));
        }

        this.ExternalLinksCache.OnLoaded()

        this.LinkPlansAndAccounts();

        this.OnReintialize.Raise()

        this.ReSubscribe()

        this.ProcessDeferredMessages(null)

        if (this.LoadedResolve)
            this.LoadedResolve()

        this.LoadedResolve = null

        this.cacheUserHasSameCurrencyOnAllAccounts();
        this.userBaseCurrency = this.getUserBaseCurrency()      // TODO нужно вызывать после кадждого открытия/закрытия позиций для выполнения пункта 4 из правил получения валюты аккаунта https://ibb.co/HVtD0gw
    }

    if (propertyMessage.Name === RulesSet.FUNCTION_NON_FIXED_LIST)
    {
        this.NonFixedList = propertyMessage.Value === 'true'
    }
    else if (propertyMessage.Name === RulesSet.AVAILABLE_NUMBER_OF_SNAPSHOT_REQUESTS ||
        propertyMessage.Name === RulesSet.VALIDATE_MAX_LOT_CLOSE_ORDER)
    {
        // вероятно выглядет немного костыльно, но спасибо серверу решили выделить 
        //AVAILABLE_NUMBER_OF_SNAPSHOT_REQUESTS от остальных 
        //и это не ложится на нашу схему с рулами 
        //потому ручками чуть правим Name и Value под общую схему
        this.RulesCache.AddRule(propertyMessage.OwnerType, propertyMessage.OwnerID,
            propertyMessage.Name, propertyMessage.Value);
    }
    else if (propertyMessage.OwnerID == DirectPropertyOwner.FeautreFlags)
    {
        this.RulesCache.AddFeachureFlag(propertyMessage.Name, propertyMessage.Value);
    }
    else if (propertyMessage.Name === 'RULE')
    {
        // раздельный ресурсоемкий рул
        let ownerID = propertyMessage.OwnerID;
        let ownerType = propertyMessage.OwnerType;
        let ruleKey = propertyMessage.Value;
        let ruleValue = '1';

        let parts = ruleKey.split('=');
        if (parts.length === 2)
        {
            ruleKey = parts[0];
            ruleValue = parts[1];
        }

        if (ruleKey == RulesSet.FUNCTION_BASE_CURRENCY)
        {
            this.baseCurrency = ruleValue;
        }
        else if (ruleKey === RulesSet.VALUE_RISK_ID && this.MainAccountNew)
        {
            if (this.MainAccountNew.AcctNumber === ownerID.toString())
            {
                this.lastRiskPlanItem = this.TryGetRiskPlan(ruleValue);
                this.lastRiskPlanItem.applyToInstrument();
            }
        }
        else
        {
            this.RulesCache.AddRule(ownerType, ownerID, ruleKey, ruleValue);
        }

        // TODO.
        // needCallIsAllowedChanged = true
    }
    else if (propertyMessage.Name === RulesSet.FUNCTION_USE_ONE_TIME_PASSWORD)
    {
        TradingLockUtils.TradingLock.LockTradingByPassword = true;
        TradingLockUtils.TradingLock.updateTradingLock(true);
    }
}

_DataCache.prototype.createAllowedReport = function (name, id, parameters)
{
    let report = new AllowedReport(name, id, parameters)
    this.reportDict[name] = report
}

_DataCache.prototype.UpdateAccountsCommissionPlan = function ()
{
    for (let acc in this.Accounts)
    {
        acc = this.Accounts[acc];
        acc.CommissionPlan = this.TryGetCommissionPlan(acc.CommisionId);
    }
}

_DataCache.prototype.LinkPlansAndAccounts = function ()
{
    var mainAccount = this.LoginUser != null ? this.LoginUser.Accounts[0] : null;
    var hasRiskPlan = mainAccount != null && mainAccount.RiskPlan != null;
    for (let acc in this.Accounts)
    {
        acc = this.Accounts[acc];
        acc.SpreadPlan = this.TryGetPlan(acc.SpreadPlanId);
        if (acc.CommissionPlan === null)
            acc.CommissionPlan = this.TryGetCommissionPlan(acc.CommisionId);
        if (acc.CrossratesPlan === null)
            acc.CrossratesPlan = this.TryGetCrossratesPlan(acc.CrossratesPlanId);
        if (acc.RiskPlan === null)
            acc.RiskPlan = this.TryGetRiskPlan(acc.RiskPlanId);
        if (acc.SwapPlan === null)
            acc.SwapPlan = this.TryGetSwapPlan(acc.SwapPlanId);
        if (acc.ChargingPlan === null)
            acc.ChargingPlan = this.TryGetChargingPlan(acc.ChargingPlanId);
    }
    if (mainAccount && mainAccount.RiskPlan)
        this.ApplyRisksForInstrument(mainAccount.RiskPlan);
}

_DataCache.prototype.ApplyRisksForInstrument = function (rplan)
{
    try
    {
        let mass = this.getCachedInstruments();
        for (let i = 0; i < mass.length; i++)
        {
            let instrument = mass[i];
            instrument.RiskSettings.RiskPlanSettings = rplan.GetRisksForInstrument(instrument.GetInteriorID());
        }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        console.log(ex);
    }
}

_DataCache.prototype.StartLoadingWaiting = function ()
{
    return new Promise(function (resolve, reject)
    {
        this.LoadedResolve = resolve;
    }.bind(this));
}

_DataCache.prototype.HandleOpenOrderMessageDirect = function (msg)
{
    this.TryRemoveFilledOrder(msg) ||
        this.TryAddOrderFromMessage(msg) ||
        this.TryUpdateOrderFromMessage(msg)
}

_DataCache.prototype.TryRemoveOrder = function (orderNumber)
{
    if (!orderNumber) return false;

    let order = this.OrderDict[orderNumber];
    if (!order) return;

    this.TryRemoveClosingOrderFromPosition(order);

    order.Dispose();
    delete this.OrderDict[orderNumber];

    this.OnRemoveOrder.Raise(order);
};

// TODO. Rename.
_DataCache.prototype.TryRemoveFilledOrder = function (openOrderMessage)
{
    if (openOrderMessage.OrderStatus === OrderStatus.REMOVED)
    {
        let removedOrder = this.getOrderById(openOrderMessage.OrderNumber)
        let removedPosition = false;
        if (removedOrder)
            this.TryRemoveOrder(removedOrder.OrderNumber)
        else
        {
            this.RemovedTradeHistory(openOrderMessage.OrderNumber)
            this.OnRemovedTradeEvent.Raise();
            removedPosition = true;
        }

        return !!removedOrder || removedPosition;
    }

    if (
        openOrderMessage.Quantity <= 0 ||
        openOrderMessage.Quantity !== openOrderMessage.SharesFilled)
    {
        return false
    }

    //ордер полностью заполнен - удаляем
    let filledOrder = this.getOrderById(openOrderMessage.OrderNumber)
    if (!filledOrder)
    {
        return true
    }

    filledOrder.LastUpdateTime = new Date(openOrderMessage.LastUpdateTime)
    if (openOrderMessage.OrderStatus !== null)
    {
        filledOrder.Status = openOrderMessage.OrderStatus
    }

    // TODO. Wrong method call?
    this.TryRemoveOrder(filledOrder.OrderNumber/*, true, filledOrder.LastUpdateTime*/)
    return true
}

_DataCache.prototype.TryAddOrderFromMessage = function (msg)
{
    let orderKey = msg.OrderNumber;

    if (this.OrderDict[orderKey])
        return false;

    var instrument = this.getInstrumentByTradable_ID(msg.InstrumentTradableID, msg.Route),
        account = this.GetAccountById(msg.Account);
    if (!instrument)// || !account)
    {
        this.AddDeferredMessage(msg);
        return false;
    }
    if (!account)
        return false;

    let order = new Order(
        this,
        this.FQuoteCache,
        {
            OrderNumber: msg.OrderNumber,
            OrderType: msg.OrderType,
            Amount: msg.Quantity,
            Price: msg.Price,
            BuySell: msg.BuySell,
            TimeInForce: msg.TimeInForce,
            UTCDateTime: msg.UTCDateTime,
            Active: msg.Active,
            Route: msg.Route,
            Account: account,
            Instrument: instrument,
            ProductType: msg.ProductType,
            DisclosedQty: msg.DisClosedQuantity,
            Leverage: msg.Leverage,
            ServerCalculation: null
        });

    if (msg.StopLimit != null)
        order.StopLimit = msg.StopLimit

    order.ExpireAt = msg.ExpireTime;
    order.SharesFilled = msg.SharesFilled;
    order.EverageFilledPrice = msg.EverageFilledPrice;
    order.OrderGroupId = msg.OrderGroupId;
    order.PositionId = msg.PositionId;
    order.Exchange = msg.Exchange;
    order.TrStopOffset = msg.TrStopOffset;

    order.BoundTo = msg.boundToOrderId

    if (msg.OrderStatus !== null)
        order.Status = msg.OrderStatus;

    order.RealActive = msg.RealActive;

    order.TakeProfitPriceValue = msg.TakeProfitPriceValue;
    order.StopLossPriceValue = msg.StopLossPriceValue;
    order.StopLossLimitPriceValue = isNaN(order.StopLossPriceValue) ? null : msg.StopLossLimit;

    order.TakeProfitPriceType = msg.TakeProfitPriceType;
    order.StopLossPriceType = msg.StopLossPriceType;

    order.TriggerSL = msg.TriggerSL;
    order.TriggerTP = msg.TriggerTP;
    order.TriggerSLTP = msg.TriggerSLTP;

    order.Replaceable = msg.Replaceable;

    order.ExternalOrderId = msg.ExternalOrderId;
    order.ExternalStatus = msg.ExternalStatus;

    this.OrderDict[orderKey] = order;

    order.IsActivated = msg.IsActivated

    order.Comment = msg.Comment;

    if (order.Active)
        this.OnAddOrder.Raise(order)
    else
        this.TryAssignClosingOrderToPosition(order)

    return true;
};

_DataCache.prototype.getFirstTradableInstrument = function ()
{
    let instruments = this.Instruments;

    for (let ins_id in instruments)
    {
        let ins = instruments[ins_id];
        if (ins.RouteIsTradable())
            return ins;
    }

    return null;
};

_DataCache.prototype.TryUpdateOrderFromMessage = function (msg)
{
    let order = this.OrderDict[msg.OrderNumber];
    if (!order) return false;

    if (msg.Quantity != null)
        order.Amount = msg.Quantity;
    if (msg.DisClosedQuantity != null)
        order.DisclosedQty = msg.DisClosedQuantity;
    if (msg.TimeInForce != null)
        order.TimeInForce = msg.TimeInForce;
    if (msg.Price != null)
        order.Price = msg.Price;
    if (msg.OrderType != null)
        order.OrderType = msg.OrderType;
    if (msg.UTCDateTime != null)
        order.UTCDateTime = msg.UTCDateTime;
    if (msg.SharesFilled != null)
        order.SharesFilled = msg.SharesFilled;
    if (msg.EverageFilledPrice != null)
        order.EverageFilledPrice = msg.EverageFilledPrice;
    if (msg.ExpireTime != null)
        order.ExpireAt = msg.ExpireTime;
    if (msg.TrStopOffset != null)
        order.TrStopOffset = msg.TrStopOffset;

    if (msg.StopLimit != null)
        order.StopLimit = msg.StopLimit

    if (msg.TakeProfitPriceValue != null)
        order.TakeProfitPriceValue = msg.TakeProfitPriceValue;
    if (msg.StopLossPriceValue != null)
        order.StopLossPriceValue = msg.StopLossPriceValue;
    order.StopLossLimitPriceValue = isNaN(order.StopLossPriceValue) ? null : msg.StopLossLimit;

    if (msg.TakeProfitPriceType != null)
        order.TakeProfitPriceType = msg.TakeProfitPriceType;
    if (msg.StopLossPriceType != null)
        order.StopLossPriceType = msg.StopLossPriceType;

    // if (msg.TriggerSL != null)   // можна скинути існуючі тригери видаляючи SL/TP у ордера
    order.TriggerSL = msg.TriggerSL;
    // if (msg.TriggerTP != null)
    order.TriggerTP = msg.TriggerTP;
    // if (msg.TriggerSLTP != null)
    order.TriggerSLTP = msg.TriggerSLTP;

    if (msg.ExternalOrderId != null)
        order.ExternalOrderId = msg.ExternalOrderId;
    if (msg.ExternalStatus != null)
        order.ExternalStatus = msg.ExternalStatus;

    if (msg.PositionId != null)
        order.PositionId = msg.PositionId;
    if (msg.boundToOrderId != null)
        order.BoundTo = msg.boundToOrderId

    if (msg.OrderStatus !== null)
        order.Status = msg.OrderStatus;

    if (msg.Leverage)
        order.Leverage = msg.Leverage;

    order.IsActivated = msg.IsActivated

    if (order.IsActivated)
        this.OnActivateOrder.Raise(order)

    if (order.Active)
        this.OnUpdateOrder.Raise(order)
    else
        this.TryAssignClosingOrderToPosition(order)

    return true;
};

_DataCache.prototype.HandleAccountStatusMessageDirect = function (accountStatusMessage)
{
    var accKey = accountStatusMessage.BstrAccount;
    var accFromStorage = this.Accounts[accKey];
    if (accountStatusMessage.Status === 2)
    {
        if (accFromStorage)
        {
            delete this.Accounts[accKey];
            this.OnRemoveAccount.Raise(accFromStorage);
        }
    }
    else
    {
        if (accFromStorage)
        {
            accFromStorage.FillAccount(accountStatusMessage);
            this.OnUpdateAccount.Raise(accFromStorage);
        }
        else
        {
            var newAcc = new Account(this, accountStatusMessage);
            this.Accounts[accKey] = newAcc;
            this.OnAddAccount.Raise(newAcc);
        }
    }
    this.AccountsCount = Object.keys(this.Accounts).length;
};

_DataCache.prototype.handleUserGroupMSG = function (mess)
{
    this._userGroups[mess.GroupId] = mess;
}

_DataCache.prototype.GetUserGroupById = function (id)
{
    return this._userGroups[id] || null;
}

_DataCache.prototype.CreateOrUpdateUser = function (mess, acc)
{
    let user = acc.User;
    if (user === null)
    {
        user = this.getUserByUserId(mess.UserID);
        // Не было юзера
        if (user === null)
        {
            //fixed by nicky 
            // старый сервер, такой как интеграл.нет не присылает UserLogin и UserID
            // валится огромное число обработанных и нет нуллреференсов
            let login = mess.UserLogin;
            let userPin = mess.UserID !== null ? mess.UserID : mess.Pin;
            user = new User(userPin, mess.Role, mess.Status, login, mess.ClientType, mess.Email, mess.GroupId);
            this.Users[mess.UserID] = user;
        }
        acc.User = user
    }
    else
    {
        //user.LockedByLogin = lockedBy;
        if (mess.Status !== null)
            user.Status = mess.Status;
        if (mess.ClientType !== null)
            user.ClientType = mess.ClientType;
        if (mess.GroupId !== null)
            user.GroupId = mess.GroupId;
    }
}

_DataCache.prototype.getUserByUserId = function (id)
{
    return this.Users[id] || null;
}

// TODO. Refactor.
_DataCache.prototype.HandleInstrumentMessageDirect = function (mess)
{
    if (!mess || !mess.Name || !mess.RoutesSupported)
    {
        console.log('Error creating instrument from empty instrument message.');
        return;
    }

    this.AddExchangeToCache(mess.TradingExchange);

    var hasExp = mess.InstrDateSettingsList && mess.InstrDateSettingsList.length;
    if (!hasExp &&
        (mess.InstrType === InstrumentTypes.OPTIONS ||
            mess.InstrType === InstrumentTypes.FUTURES ||
            mess.InstrType === InstrumentTypes.CFD_FUTURES))
        return;

    this.cacheInstrument(mess);

    //создаем список всех роутов
    var infroutesList = mess.RoutesSupported;// Instrument.getSupportedRoutes(mess.RoutesSupported);
    //получаем список торговых роутов
    var tradableRoutes = mess.TradableRoutes;//Instrument.getSupportedRoutes(mess.TradableRoutes);

    // сначала торговые
    for (let i = 0; i < tradableRoutes.length; i++)
    {
        let route = tradableRoutes[i]
        let instruments = this.GenerateInstruments(mess, route)
        // TODO
        if (instruments)
            for (let j = 0; j < instruments.length; j++)
                this.AddOrUpdateInstrumentFromMessage(instruments[j], mess)
    }

    //потом только информационные
    for (let i = 0; i < infroutesList.length; i++)
    {
        let route = infroutesList[i];
        if (tradableRoutes.indexOf(route) !== -1)
            continue;


        let instruments = this.GenerateInstruments(mess, route);
        //TODO
        if (instruments)
            for (let j = 0; j < instruments.length; j++)
            {
                this.AddOrUpdateInstrumentFromMessage(instruments[j], mess);
            }
    }
};

// TODO.
_DataCache.prototype.HandleClosePositionMessageDirect = function (message)
{
    let id = message.Id;
    this.RemovePossition(id);
};

_DataCache.prototype.RemovePossition = function (id)
{
    let closedPosition = this.PositionDict[id] || this.PositionDictCorporateAction[id];
    if (!closedPosition) return;

    // TODO Performance think !!!
    let cpabc = closedPosition.Account.BaseCurrency;

    //TODO. Necessary?
    //TODO REALY NECESSARY!!!
    let slOrder = closedPosition.SLOrder;
    if (slOrder) this.TryRemoveOrder(slOrder.OrderNumber);

    let tpOrder = closedPosition.TPOrder;
    if (tpOrder) this.TryRemoveOrder(tpOrder.OrderNumber);

    closedPosition.Dispose();

    if (closedPosition.ProductType === ProductType.CorporateAction)
    {
        delete this.PositionDictCorporateAction[id];
        this.OnRemoveCorporateAction.Raise(closedPosition);
    }
    else
    {
        delete this.PositionDict[id];
        this.OnRemovePosition.Raise(closedPosition);
    }

    if (cpabc !== this.userBaseCurrency)
        this.userBaseCurrency = this.getUserBaseCurrency();
};


// TODO.
_DataCache.prototype.HandlePositionMessageDirect = function (message)
{
    this.TryAddPositionFromMessage(message) ||
        this.TryUpdatePositionFromMessage(message);
};

// TODO.
// window.testCounter = 0;
_DataCache.prototype.TryAddPositionFromMessage = function (message)
{
    // if (window.testCounter < 3)
    // {
    //     message.CorporateActionType = 0;
    //     message.ProductType = 3;
    //     window.testCounter++;
    // }

    var positionDict = this.PositionDict;
    var positionDictCorporateAction = this.PositionDictCorporateAction;

    var position = positionDict[message.PositionId] || positionDictCorporateAction[message.PositionId];

    if (message.Amount === 0 && position && this.Loaded && message.ProductTypeModifiedAmount !== null)
        FOrderExecutor.createEventMessageProductTypeModified(position)//, message.ProductTypeModifiedAmount)

    if (position) return false;
    //https://tp.traderevolution.com/entity/80132
    if (message.Amount === 0)
        return false;

    var instrument = this.getInstrumentByTradable_ID(message.InstrumentTradableID, message.Route),
        account = this.GetAccountById(message.Account);
    if (!instrument)// || !account)
    {
        this.AddDeferredMessage(message);
        return false;
    }
    if (!account)
        return false;

    position = new Position(
        this,
        this.FQuoteCache,
        {
            PositionId: message.PositionId,
            SuperPositionId: message.SuperPositionId,   //(FIFO) #80010
            Account: account,
            Instrument: instrument,
            Amount: message.Amount,
            OpenPrice: message.OpenPrice,
            OperationType: message.OperationType,
            OpenTime: message.OpenTime,
            OpenOrderId: message.OpenOrderId,
            Route: message.Route,
            RealizedPL: message.RealizedPL,
            ProductType: message.ProductType,
            Leverage: message.Leverage,
            ServerCalculation: new ServerCalculation(
                message.ServerCalculationCurPriceClose,
                message.ServerCalculationExposure,
                message.ServerCalculationPositionValue,
                message.ServerCalculationNetPL,
                message.ServerCalculationGrossPL)
        });

    position.OpenCrossPrice = message.OpenCrossPrice;
    position.Commission = message.Commission;
    position.Swaps = message.Swaps;
    position.StartOfDayAmount = message.StartOfDayAmount;

    position.OptionExerciseStatus = message.OptionExerciseStatus;
    position.CorporateActionType = message.CorporateActionType;

    position.Comment = message.Comment;

    if (position.ProductType === ProductType.CorporateAction)
        positionDictCorporateAction[message.PositionId] = position;
    else
        positionDict[message.PositionId] = position;

    if (Object.keys(positionDict).length === 1)
        this.userBaseCurrency = this.getUserBaseCurrency();

    let pabc = position.Account.BaseCurrency;
    if (pabc !== this.userBaseCurrency)
        this.userBaseCurrency = this.baseCurrency;

    position.RecalcAmounts()

    if (position.ProductType === ProductType.CorporateAction)
        this.OnAddCorporateAction.Raise(position);
    else
        this.OnAddPosition.Raise(position);

    if (this.Loaded && message.ProductTypeModifiedAmount !== null)
        this.FOrderExecutor.createEventMessageProductTypeModified(position, message.ProductTypeModifiedAmount)

    return true;
};

_DataCache.prototype.TryUpdatePositionFromMessage = function (message)
{
    var positionDict = this.PositionDict;
    var positionDictCorporateAction = this.PositionDictCorporateAction;

    var position = positionDict[message.PositionId] || positionDictCorporateAction[message.PositionId];
    if (!position) return false;

    if (message.Amount === 0)
    {
        this.RemovePossition(message.PositionId);
        return true;
    }
    position.UpdateByMessage(message);

    position.RecalcAmounts()

    if (position.ProductType === ProductType.CorporateAction)
        this.OnUpdateCorporateAction.Raise(position);
    else
        this.OnUpdatePosition.Raise(position);

    if (this.Loaded && message.ProductTypeModifiedAmount !== null)
        this.FOrderExecutor.createEventMessageProductTypeModified(position, message.ProductTypeModifiedAmount)

    return true;
};

/*TODO*/
_DataCache.prototype.cacheInstrument = function (message)
{
    return;
};

_DataCache.prototype.AddOrUpdateInstrumentFromMessage = function (ins, mess)
{
    this.tryUpdateExistingInstrumentTypes(ins)

    //var fullName = ins.FullName.toUpperCase();
    var instruments = this.Instruments;

    let id = ins.InstrumentTradableID;
    if (id)
    {
        let Route_U = ins.Route;
        let i_contains = !!this.InstrumentsTableByIDs[Route_U]

        let isContiniousContract = ins.InstrumentSpecificType === InstrumentSpecificType.ContiniousContract
        if (isContiniousContract)
            id += "_C";
        if (!i_contains)
            this.InstrumentsTableByIDs[Route_U] = new Object();

        let exist = this.InstrumentsTableByIDs[Route_U][id];
        if (!exist)
            this.InstrumentsTableByIDs[Route_U][id] = ins;

        if (ins.isFuturesSymbol && isContiniousContract)
        {
            if (!this.InstrumentsContiniousContractIDS[ins.Id])
                this.InstrumentsContiniousContractIDS[ins.Id] = new Object();
            if (!this.InstrumentsContiniousContractIDS[ins.Id][Route_U])
                this.InstrumentsContiniousContractIDS[ins.Id][Route_U] = new Object();
            if (!this.InstrumentsContiniousContractIDS[ins.Id][Route_U].CurrentContiniousContract)
                this.InstrumentsContiniousContractIDS[ins.Id][Route_U].CurrentContiniousContract = ins
        }
    }

    let InsInteriorId = ins.GetInteriorID()
    let ContiniousContract = ins.InstrumentSpecificType === InstrumentSpecificType.ContiniousContract
    let itidccc = this.InstrumentTradableIDChacheContiniousContract;
    let itidc = this.InstrumentTradableIDChache;
    let hasNewCach = ContiniousContract ? itidccc.hasOwnProperty(InsInteriorId) : itidc.hasOwnProperty(InsInteriorId);

    if (instruments.hasOwnProperty(InsInteriorId) && hasNewCach)
    {
        let updateMessage = mess && mess.InstrDateSettingsList && mess.InstrDateSettingsList.length ? ins.CreateInstrumentMessage() : mess; // возникло в связи с комментом в #88334 youbanoe gouno над которым надо хорошенько подумать!!! 
        ins = instruments[InsInteriorId];
        if (updateMessage)
        {
            ins.UpdateByMessage(updateMessage);
            ins.UpdateHideRouteMode();

            if (this.Loaded)
                this.CorrectlyAddNonFixInstrument(ins);
        }
        this.OnUpdateInstrument.Raise(ins);
    }
    else
    {
        if (ins.InstrumentSpecificType === InstrumentSpecificType.ContiniousContract)
            this.InstrumentTradableIDChacheContiniousContract[InsInteriorId] = ins;
        else
            this.InstrumentTradableIDChache[InsInteriorId] = ins;

        instruments[InsInteriorId] = ins;

        let t_id = ins.TradingSessionsId;
        if (!this.InstrumentsByTradingSessionsId[t_id])
            this.InstrumentsByTradingSessionsId[t_id] = new Array();
        this.InstrumentsByTradingSessionsId[t_id].push(ins);

        if (this.lastRiskPlanItem)  // #89151
            this.lastRiskPlanItem.processOneInstrument(ins)
        this.AddAggregatedInstrument(ins)
        ins.UpdateHideRouteMode();

        this.InstrumentsByIdCache[ins.Id] = ins;

        if (!this.InstrumentsByTradableIdCache[id])
            this.InstrumentsByTradableIdCache[id] = []
        this.InstrumentsByTradableIdCache[id].push(ins)

        if (this.Loaded)
        {
            this.CorrectlyAddNonFixInstrument(ins);

            this.ProcessDeferredMessages(ins)
        }

        this.OnAddInstrument.Raise(ins);
    }

    var dsTrID = ins.GetDataSourceTradableId()
    if (dsTrID)
    {
        var dsIns = this.GetDataSourceInstrument(ins)
        if (!dsIns)
            return
        let dsInteriorID = dsIns.GetInteriorID();

        if (!this.InstrumentsBySourceRouteIdTradableId[dsInteriorID])
            this.InstrumentsBySourceRouteIdTradableId[dsInteriorID] = new Array()

        if (this.InstrumentsBySourceRouteIdTradableId[dsInteriorID].indexOf(ins) == -1)
            this.InstrumentsBySourceRouteIdTradableId[dsInteriorID].push(ins)
    }
};

_DataCache.prototype.CorrectlyAddNonFixInstrument = function (instrument)
{
    // TODO

    //            foreach (RiskPlan rp in GetRiskPlans())
    //                rp.ProcessOneInstrument(instrument);

    //foreach (CrossratesPlan cp in GetCrossratesPlans())
    //                cp.ApplyMarkupToInstrument(instrument);

    //foreach (SpreadPlan sp in GetSpreadPlans())
    if (this.SpreadPlan)
        this.SpreadPlan.ApplyForInstrument(instrument);

    //foreach (CommissionPlan cp in GetCommissionPlans())
    //                cp.ApplyForInstrument(instrument);
}

_DataCache.prototype.tryUpdateExistingInstrumentTypes = function (instrument)
{
    if (!instrument)
        return

    let insType = instrument.InstrType
    if (insType === InstrumentTypes.INDICIES)
        return

    this.ExistingInstrumentTypesSet[instrument.CFD ? InstrumentTypes.EQUITIES_CFD : insType] = true
}

_DataCache.prototype.FindParentForOrder = function (order)
{
    if (!order) return null;

    let pos = this.PositionDict[order.PositionId];
    return pos || null;
};

_DataCache.prototype.TryAssignClosingOrderToPosition = function (order)
{
    if (!order) return false;

    let pos = this.FindParentForOrder(order);
    if (!pos) return false;

    let orderType = order.OrderType;
    let OrderTypes = OrderType;

    let isSL = orderType === OrderTypes.Stop || orderType === OrderTypes.StopLimit || orderType === OrderTypes.TrailingStop;
    if (isSL)
    {
        pos.SLOrder = order;
        this.OnAddSLOrderToPosition.Raise(pos);
        return true;
    }

    let isTP = orderType === OrderTypes.Limit;
    if (isTP)
    {
        pos.TPOrder = order;
        this.OnAddTPOrderToPosition.Raise(pos);
        return true;
    }

    let isMarket = orderType === OrderTypes.Market;     // #94085
    if (isMarket)
    {
        this.OnAddOrder.Raise(order);                   // #94085

        return true;
    }

    return false;
};

_DataCache.prototype.TryRemoveClosingOrderFromPosition = function (order)
{
    if (!order) return false;

    let pos = this.FindParentForOrder(order);
    if (!pos) return false;

    let orderNumber = order.OrderNumber;

    let slOrder = pos.SLOrder;
    if (slOrder && slOrder.OrderNumber === orderNumber)
    {
        this.OnRemoveSLOrderFromPosition.Raise(pos);
        pos.SLOrder = null;
        return true;
    }

    let tpOrder = pos.TPOrder;
    if (tpOrder && tpOrder.OrderNumber === orderNumber)
    {
        this.OnRemoveTPOrderFromPosition.Raise(pos);
        pos.TPOrder = null;
        return true;
    }

    return false;
};

_DataCache.prototype.AddRoute = function (message)
{
    if (!message.RouteId)
        return;

    var isUpdateRouteMessage = false;
    isUpdateRouteMessage = this.routes.hasOwnProperty(message.RouteId);

    if (!isUpdateRouteMessage && message.IsInfo && !message.IsTradable)
    {

        isUpdateRouteMessage = this.infoRoutes.hasOwnProperty(message.RouteId);

        if (isUpdateRouteMessage)
            this.infoRoutes[message.RouteId].UpdateByMessage(message);
        else
        {
            var route = new Route(message);
            this.infoRoutes[message.RouteId] = route;
            this.UpdateInstrumentForInfoRoute(route);
        }
        return;
    }

    if (isUpdateRouteMessage)
        this.routes[message.RouteId].UpdateByMessage(message);
    else
    {
        // Если пришел хотябы один роут - удаляем дефолтовый
        //TODO хз, что за и нужно ли нам?
        //if (this.routes.hasOwnProperty("DEFAULT"))
        //    delete this.routes["DEFAULT"];
        var route = new Route(message);
        this.routes[message.RouteId] = route;

        ////////updateNames
        if (this.InstrumentsTableByIDs[message.RouteId])
        {
            let ins = this.InstrumentsTableByIDs[message.RouteId];
            let instrs = Object.keys(ins);
            for (let i = 0; i < instrs.length; i++)
            {
                let c_i = ins[instrs[i]];
                c_i.RouteName = message.Name;
            }
        }
    }
}
_DataCache.prototype.UpdateInstrumentForInfoRoute = function (route)
{
    let routeName = route.RouteId;

    let ins = this.InstrumentsTableByIDs[routeName];
    if (!ins)
        return;
    let instrs = Object.keys(ins);

    for (let i = 0; i < instrs.length; i++)
    {
        let instr = ins[instrs[i]];
        let newInstr = this.getInstrument(instr.ShortName, routeName);
        if (newInstr === null)
            continue;

        //if (newInstr.InvisibleByUpdateHideRouteMode) //роуты сливаются - мы должны засетить основной инструмент
        instr.InstrumentDayBarMessageUpdateMode = route.InstrumentDayBarMessageUpdateMode;
        //else //инструмент видим отдельно 
        newInstr.InstrumentDayBarMessageUpdateMode = route.InstrumentDayBarMessageUpdateMode;
    }
}

_DataCache.prototype.isAllowedForAccount = function (name, account)
{
    return this.RulesCache.IsAllowedForAccount(name, account);
}

_DataCache.prototype.isAllowedForMainAccount = function (name)
{
    return this.RulesCache.IsAllowedForAccount(name, null); //main account
}

_DataCache.prototype.isAllowedForMyUser = function (ruleKey)
{
    return this.RulesCache.IsAllowedForMyUser(ruleKey);
}

_DataCache.prototype.isAllowedForUser = function (name, u)
{
    return this.RulesCache.IsAllowedForUser(name, u)
}

_DataCache.prototype.isAllowedFeachureFlag = function (ruleName)
{
    return this.RulesCache.IsAllowedFeatureFlag(ruleName);
}

_DataCache.prototype.getRuleStringValueForAccount = function (ruleName, account, defaultValue)
{
    let ruleValue = this.RulesCache.GetRuleValue(ruleName, account);
    if (!ruleValue)
        return defaultValue;
    return ruleValue;
}

_DataCache.prototype.getRuleNumberValueForAccount = function (ruleName, account, defaultValue)
{
    return +this.getRuleStringValueForAccount(ruleName, account, defaultValue);
}

_DataCache.prototype.getRuleStringValueForMyUser = function (ruleName, defaultValue)
{
    let ruleValue = this.RulesCache.GetRuleValueForMyUser(ruleName);
    if (!ruleValue)
        return defaultValue;
    return ruleValue;
}

_DataCache.prototype.getRuleNumberValueForMyUser = function (ruleName, defaultValue)
{
    return +this.getRuleStringValueForMyUser(ruleName, defaultValue);
}

/**
* Retrieves the boolean value for the rule named VALIDATE_MAX_LOT_CLOSE_ORDER from the DataCache.
* This rule is stored in RulesCache.#GlobalRules as either 'true' or 'false'.
* @returns {boolean} - The boolean value for the specified rule.
*/
_DataCache.prototype.getValidateMaxLotCloseOrderBoolRule = function ()
{
    return this.getRuleStringValueForAccount(RulesSet.VALIDATE_MAX_LOT_CLOSE_ORDER) === 'true';
}

_DataCache.prototype.getAllOrders = function ()
{
    return this.OrderDict
}

_DataCache.prototype.getOrderById = function (id)
{
    return this.OrderDict[id]
}

// TODO. Refactor. Come up with 1 filter method.
_DataCache.prototype.getOrdersById = function (idArray)
{
    let orderDict = {}
    for (let i = 0, len = idArray.length; i < len; i++)
    {
        let id = idArray[i]
        let ord = this.getOrderById(id)
        if (ord) orderDict[id] = ord
    }
    return orderDict
}

// TODO. Refactor. Come up with 1 filter method.
_DataCache.prototype.getOrdersByAccount = function (account)
{
    if (!account) return null

    let orderDict = this.OrderDict
    let resDict = {}

    let accId = account.BstrAccount

    for (let key in orderDict)
    {
        let order = orderDict[key]
        // TODO.
        if (order.Account.BstrAccount === accId)
            resDict[key] = order
    }

    return resDict
}

// TODO. Refactor. Come up with 1 filter method.
_DataCache.prototype.getOrdersByInstrument = function (instrument)
{
    if (!instrument) return null

    let orderDict = this.OrderDict
    let resDict = {}

    let insId = instrument.GetInteriorID()

    for (let key in orderDict)
    {
        let order = orderDict[key]
        if (order.Instrument.GetInteriorID() === insId)
            resDict[key] = order
    }

    return resDict
}

// TODO. Refactor. Come up with 1 filter method.
_DataCache.prototype.getOrdersBySide = function (side)
{
    let orderDict = this.OrderDict
    let resDict = {}

    for (let key in orderDict)
    {
        let order = orderDict[key]
        if (order.BuySell === side)
            resDict[key] = order
    }

    return resDict
}

// TODO. Refactor. Come up with 1 filter method.
_DataCache.prototype.getPositionsBySide = function (side)
{
    let posDict = this.PositionDict
    let resDict = {}

    for (let key in posDict)
    {
        let pos = posDict[key]
        if (pos.BuySell === side)
            resDict[key] = pos
    }

    return resDict
}


_DataCache.prototype.getPositionsByNetPL = function (isPositive)
{
    let posDict = this.PositionDict
    let posResDict = {};
    let negResDict = {};
    for (let key in posDict)
    {
        let pos = posDict[key]
        pos.PnLCalculator.NetPL > 0 ? posResDict[key] = pos : negResDict[key] = pos;
    }
    return isPositive ? posResDict : negResDict
}


// TODO. Refactor. Come up with 1 filter method.
_DataCache.prototype.getOrdersByTIF = function (tif)
{
    let orderDict = this.OrderDict
    let resDict = {}

    for (let key in orderDict)
    {
        let order = orderDict[key]
        if (order.TimeInForce === tif)
            resDict[key] = order
    }

    return resDict
}

// TODO. Refactor. Come up with 1 filter method.
_DataCache.prototype.getOrdersByType = function (orderType)
{
    let orderDict = this.OrderDict
    let resDict = {}

    for (let key in orderDict)
    {
        let order = orderDict[key]
        if (order.OrderType === orderType)
            resDict[key] = order
    }

    return resDict
}

// TODO. Optimize.
_DataCache.prototype.getOrdersByInstrumentAndAccount = function (instrument, account)
{
    if (!instrument || !account)
        return null

    let resOrderDict = {}
    let orderDict = this.OrderDict
    for (let key in orderDict)
    {
        let order = orderDict[key]
        if (Instrument.IsEqualInstrument(order.Instrument, instrument) && order.Account === account)
            resOrderDict[key] = order
    }
    return resOrderDict
}

_DataCache.prototype.getPositionById = function (id)
{
    return this.PositionDict[id]
}

_DataCache.prototype.getPositionsCorporateActionById = function (id)
{
    return this.PositionDictCorporateAction[id]
}

_DataCache.prototype.getAllPositions = function ()
{
    return this.PositionDict
}

_DataCache.prototype.getAllPositionsCorporateAction = function ()
{
    return this.PositionDictCorporateAction
}

_DataCache.prototype.getPositionsById = function (idArray)
{
    let posDict = {}
    for (let i = 0, len = idArray.length; i < len; i++)
    {
        let id = idArray[i]
        let pos = this.getPositionById(id)
        if (pos) posDict[id] = pos
    }
    return posDict
}

_DataCache.prototype.getPositionsByAccount = function (account, isCorporateAction)
{
    if (!account) return null

    let posDict = !isCorporateAction ? this.PositionDict : this.PositionDictCorporateAction;
    let resDict = {}

    let accId = account.BstrAccount

    for (let key in posDict)
    {
        let pos = posDict[key]
        // TODO.
        if (pos.Account.BstrAccount === accId)
            resDict[key] = pos
    }

    return resDict
}

_DataCache.prototype.getPositionsByInstrument = function (instrument, isCorporateAction)
{
    if (!instrument) return null

    let posDict = !isCorporateAction ? this.PositionDict : this.PositionDictCorporateAction;
    let resDict = {}

    let insId = instrument.GetInteriorID()

    for (let key in posDict)
    {
        let pos = posDict[key]
        if (pos.Instrument.GetInteriorID() === insId)
            resDict[key] = pos
    }

    return resDict
}

// TODO. Optimize.
_DataCache.prototype.getPositionsByInstrumentAndAccount = function (instrument, account)
{
    if (!instrument || !account)
        return null

    let resPosDict = {}
    let positionDict = this.PositionDict
    for (let key in positionDict)
    {
        let pos = positionDict[key]
        if (pos.Instrument === instrument && pos.Account === account)
            resPosDict[key] = pos
    }
    return resPosDict
}

_DataCache.prototype.ActiveOrdersList = function (addSLTP)
{
    return new Array();
};


_DataCache.prototype.AddNewTrade = function (tradeMSg)
{
    let trade = this.newTradesDict[tradeMSg.TradeId];

    if (tradeMSg.Route == '' || !tradeMSg.InstrumentTradableID)
        return;

    let ins = this.getInstrumentByTradable_ID(tradeMSg.InstrumentTradableID, tradeMSg.Route)
    if (!ins)
    {
        this.AddDeferredMessage(tradeMSg);
        return
    }

    //Обновляем информацию по объемам по паре аккаунт/инструмент для ассетов
    this.ProcessVolumeQuote(tradeMSg);

    if (!trade)
    {
        trade = new NewTrade(this, tradeMSg);
        this.newTradesDict[trade.TradeId] = trade;
        this.realTimeFilledOrdersArray.push(trade);
        this.filledOrdersArray.push(trade);
        //TODO костыль ебанный
        if (Object.keys(this.newTradesDict).length === 1)
        {
            // this.filledOrdersArray.push(trade)
            this.filledOrdersCurrency = this.getFilledOrdersCurrency();
        }

        let tabc = trade.Account.BaseCurrency;
        if (tabc !== this.filledOrdersCurrency)
            this.filledOrdersCurrency = this.baseCurrency;
    }
    else
    {
        trade.UpdateByMessage(tradeMSg);
    }

    if (this.Loaded)
        this.OnAddNewTradeEvent.Raise(this, trade);
};

_DataCache.prototype.ProcessVolumeQuote = function (message)
{
    // При расчете Today traded qty учитываем значение настройки Asset trading mode. Если Delivery, то в данном показателе учитываем 
    // только трейды c Trading mode = Delivery, если General, то учитываем только трейды с Trading mode = General.  
    if (!this.Loaded || message == null/* || message.ProductType != ProductType.Deliver*/)
        return;

    var symbol = this.getInstrumentByTradable_ID(message.InstrumentTradableID, message.Route);

    if (symbol == null)
        return;

    var assetName = symbol.AssetName;

    if (!assetName)
        return;

    if (!this.IsSharedAsset(assetName))
        return;

    var account = this.GetAccountByIdOrName(message.AccountId);

    if (account == null || account.AssetSharesNames == null)
        return;

    if (account.AssetSharesNames.indexOf(assetName) === -1)
        return;

    var assetDataInfo = account.GetAssetDataInfoByName(assetName);

    var assetType = assetDataInfo.AssetTradingMode;
    var tradeType = message.ProductType;

    if ((assetType === AssetTradingMode.General && tradeType === ProductType.General) ||
        (assetType === AssetTradingMode.Delivery && tradeType === ProductType.Delivery) ||
        (assetType === AssetTradingMode.All))
    {
        var amount = message.Amount * symbol.LotSize;
        assetDataInfo.TodayTradedQty += message.BuySell === OperationType.Buy ? amount : -amount;
    }
}

_DataCache.prototype.IsSharedAsset = function (name)
{
    var asset = this.GetAssetByName(name);
    return asset != null && asset.Type == AssetType.SHARES;
}

_DataCache.prototype.AddExchangeToCache = function (exchange)
{
    if (exchange && this.TradingExchanges.indexOf(exchange) === -1)
        this.TradingExchanges.push(exchange);
}

//TODO Optimization
_DataCache.prototype.FindInstrumentByTradingExchange = function (exchange)
{
    let keys = Object.keys(this.Instruments)
    let res = {};
    for (let i = 0; i < keys.length; i++)
    {
        let ins = this.Instruments[keys[i]];
        if (ins.TradingExchange === exchange)
            res[ins.DisplayName()] = ins.GetInteriorID();

    }
    let resKeys = Object.keys(res);
    resKeys.sort();
    let resId = res[resKeys[0]];
    return this.Instruments[resId] || null;
}

//#endregion Direct

_DataCache.prototype.GetSession = function (id)
{
    return this.SessionDict[id];
};

_DataCache.prototype.AddSession = function (msg)
{
    let sessContainer = new SessionsContainer(msg.Id, msg.Name);
    this.SessionDict[sessContainer.Id] = sessContainer;

    return sessContainer;
};

_DataCache.prototype.getRouteByName = function (name)
{
    if (name === null || !this.routes.hasOwnProperty(name))
        return null;
    else
        return this.routes[name];
};

_DataCache.prototype.getRouteByTextName = function (name)
{
    for (let r in this.routes)
    {
        if (this.routes[r].Name === name)
            return this.routes[r];
    }

    return null
};

_DataCache.prototype.getInfoRouteByName = function (name)
{
    if (name === null || !this.infoRoutes.hasOwnProperty(name))
        return null;
    else
        return this.infoRoutes[name];
};

_DataCache.prototype.getRouteById = function (id)
{
    // for (let r in this.routes)
    // {
    //     if (this.routes[r].RouteId === id)
    let route = this.routes[id];
    if (route)
        return route
    // }
    // only info - используется в 1м месте ниже (09/07/2015)
    // for (let r in this.infoRoutes)
    // {
    // if (this.infoRoutes[r].RouteId === id)
    route = this.infoRoutes[id];
    if (route)
        return route
    // }

    return null;
}

_DataCache.prototype.getNewsRoutesIDs = function ()
{
    let newsRoutesIDs = [],
        routes = this.routes

    for (let id in routes)
        if (routes[id].IsNewsRoute)
            newsRoutesIDs.push(routes[id].RouteId);

    routes = this.infoRoutes

    for (let id in routes)
        if (routes[id].IsNewsRoute)
            newsRoutesIDs.push(routes[id].RouteId);

    return newsRoutesIDs;
}

_DataCache.prototype.setNewsAsViewed = function (newsID)
{
    if (!newsID)
        return

    if (ApplicationInfo.isExploreMode) {
        return;
    }

    let viewedNews = LocalStorage.getViewedNews();
    let idEncrypt = this.EncryptUserAndConnectionNameByMD5();

    viewedNews = viewedNews ? JSON.parse(viewedNews) : {}

    if (!viewedNews[idEncrypt])
        viewedNews[idEncrypt] = []

    if (viewedNews[idEncrypt].indexOf(newsID) == -1)
        viewedNews[idEncrypt].push(newsID)

    LocalStorage.setViewedNews(JSON.stringify(viewedNews))
}

_DataCache.prototype.EncryptUserAndConnectionNameByMD5 = function ()
{
    if (!this.userAndConnectionNameEncryptedByMD5)
    {
        let connectionNameEncrypt = MD5(LocalStorage.getLastConnectionName());
        let userNameEncrypt = MD5(Connection.vendor.GetLogin());

        this.userAndConnectionNameEncryptedByMD5 = MD5(userNameEncrypt + connectionNameEncrypt)
    }

    return this.userAndConnectionNameEncryptedByMD5
}

_DataCache.prototype.IsNewsViewed = function (newsID)
{
    if (!newsID)
        return

    let viewedNews = LocalStorage.getViewedNews();
    let idEncrypt = this.EncryptUserAndConnectionNameByMD5();

    if (!viewedNews)
        return false

    viewedNews = JSON.parse(viewedNews)

    if (!viewedNews[idEncrypt] || viewedNews[idEncrypt].indexOf(newsID) == -1)
        return false

    return true
}

_DataCache.prototype.PositionListForAccountInstrument = function (instrument, account)
{
    let positions = new Array();
    if (instrument !== null && account !== null)
    {
        //
        //
        // если передали базовый опцион то по нему надо все дочерние добавлять
        //TODO ???
        //bool isBaseOptionSymbol = instrument.isOptionSymbol && instrument.ForwardBaseInstruments != null;
        let list = this.PositionDict;
        if (Object.keys(this.PositionDict).length > 0)
        {
            for (let pos in list)
                if (list[pos].Account === account &&
                    //list[pos].Instrument === instrument &&
                    Instrument.IsEqualInstrument(list[pos].Instrument, instrument) &&
                    list[pos].Instrument.Route === instrument.Route)
                    //(BaseDataCache.IsEqualInstrument(pos.instrument, instrument) ||
                    //(isBaseOptionSymbol && ((pos.instrument.SourceName == instrument.ShortName ||
                    //(util.Utils.OptionsWorkingMode == OptionsWorkingMode.Direct && pos.instrument.ForwardBaseInstrument != null && instrument.ForwardBaseInstrument != null && pos.instrument.ForwardBaseInstrument.ShortName == instrument.ForwardBaseInstrument.ShortName))
                    //&& pos.instrument.getRoute() == instrument.getRoute()))))//роут тоже учитываем
                    positions.push(list[pos]);
        }
    }
    return positions;
}

_DataCache.prototype.OrdersListForAccountInstrument = function (instrument, account, buySell = null, addSLTP = true)
{
    let orders = new Array();
    if (instrument != null && account != null)
    {
        //
        // если передали базовый опцион то по нему надо все дочерние добавлять
        //bool isBaseOptionSymbol = instrument.isOptionSymbol && instrument.ForwardBaseInstruments != null;

        var list = this.ActiveOrdersList(addSLTP);
        var list = this.OrderDict;
        if (Object.keys(this.OrderDict).length > 0)
        {
            for (let ord in list)
                if (list[ord].Account === account &&
                    //list[ord].Instrument === instrument &&
                    Instrument.IsEqualInstrument(list[ord].Instrument, instrument) &&
                    list[ord].Instrument.Route === instrument.Route &&
                    list[ord].Active)
                    //(BaseDataCache.IsEqualInstrument(ord.instrument, instrument) || (isBaseOptionSymbol && (ord.instrument.SourceName == instrument.ShortName))) && (!buySell!==null|| buySell  == ord.BuySell) ||
                    //(util.Utils.OptionsWorkingMode == OptionsWorkingMode.Direct && ord.instrument.ForwardBaseInstrument != null && instrument.ForwardBaseInstrument != null && ord.instrument.ForwardBaseInstrument.ShortName == instrument.ForwardBaseInstrument.ShortName))
                    orders.push(list[ord]);
        }
    }
    return orders;
}

_DataCache.prototype.GetFilteredTrades = function (instrument, account)
{
    let orders = new Array();
    if (instrument != null && account != null)
    {
        var list = this.realTimeFilledOrdersArray;
        //  var list = this.filledOrdersArray

        for (let i = 0; i < list.length; i++)
            if (list[i].Account === account &&
                Instrument.IsEqualInstrument(list[i].Instrument, instrument) &&
                list[i].Instrument.Route === instrument.Route)
                orders.push(list[i]);
    }
    return orders;
}

_DataCache.prototype.GetTradeSessionStatus = function (id)
{
    let sessionStatus = this.tradeSessionStatusCache[id];
    if (sessionStatus)
        return sessionStatus;

    return null;
}

_DataCache.prototype.getUserByLogin = function (login)
{
    //TODO
    let user = {};
    user.Accounts = new Array();
    let acc = null;
    let keys = Object.keys(this.Accounts);
    for (let i = 0; i < keys.length; i++)
        if (this.Accounts[keys[i]].userLogin === login)
            acc = this.Accounts[keys[i]];
    if (!acc)
        acc = this.Accounts[keys[0]];
    if (acc)
        user.Accounts.push(acc);
    return user;
}

_DataCache.prototype.getAnyAccount = function ()
{
    let accs = this.Accounts
    for (let accID in accs)
        return accs[accID]

    return null
}

_DataCache.prototype.getUserID = function ()
{
    let acc = this.getAnyAccount()
    return acc ? acc.userID : null
}

_DataCache.prototype.getUserName = function ()
{
    return this.UserLogin
}

_DataCache.prototype.getAllowedReportDict = function ()
{
    return this.reportDict
}

_DataCache.prototype.getCachedInstruments = function ()
{

    // TODO бля потом переделывать 100%

    return Object.keys(this.Instruments).map(function (value) { return this.Instruments[value]; }.bind(this));

    //// Тут я Никиту не понял ???
    //if (this.NonFixedList)
    //    return getSortedInstruments();

    //if (this.cachedInstrumentsWR == null || this.cachedInstrumentsWR.length == 0)
    //{
    //    //на всякий
    //    //TODO IMPLEMENT
    //    //this.GenerateOptions();
    //    this.cachedInstrumentsWR = this.getSortedInstruments();
    //}

    //return this.cachedInstrumentsWR;
}

_DataCache.prototype.RoutesArray = function ()
{
    return Object.keys(dc.routes);
}

_DataCache.prototype.ProcessTradeSessionSwitchMessage = function (msg)
{
    var instruments = this.GetInstrumentsBySessionID(msg.SessionId);

    if (!instruments.length)
    {
        console.log("TradeSessionSwitchMessage", "DataCache::ProcessTradeSessionSwitchMessage::Can't find instruments by session id:" + msg.SessionId, EventType.Exception);
        return;
    }

    for (let i = 0; i < instruments.length; i++)
    {
        instruments[i].TradeSessionStatusId = msg.TradeSessionStatusId;
        if (msg.ExchangeSessionName)
            instruments[i].ExchangeSessionName = msg.ExchangeSessionName;
    }
}

_DataCache.prototype.GetInstrumentsBySessionID = function (sessionId)
{
    return this.InstrumentsByTradingSessionsId[sessionId] || [];
}

_DataCache.prototype.ProcessInstrumentUpdateMessage = function (message, continiouseContractSearch = false)
{
    let ai = this.GetAggregatedInstrument(message.Name);
    if (ai === null)
    {
        if (!continiouseContractSearch)
            console.log("InstrumentUpdateMessage", "DataCache::ProcessInstrumentUpdateMessage::Can't find AggregatedInstrument for symbol: " + message.Name + " instrument id: " + message.InstrumentID + (message.ContractID !== null ? " contract id: " + message.ContractID : ""), EventType.Exception);
        return;
    }

    let isFutures = false;
    for (let i = 0; i < ai.length; i++)
    {
        let inst = ai[i];
        if (message.TradeSessionStatusId !== null && message.TradeSessionStatusId !== undefined)
            inst.TradeSessionStatusId = message.TradeSessionStatusId;

        if (message.ExchangeSessionName)
            inst.ExchangeSessionName = message.ExchangeSessionName;

        if (inst.InstrType == InstrumentTypes.FUTURES)
            isFutures = true;
    }

    if (isFutures && !continiouseContractSearch)
    {
        message.Name = "[" + message.Name + "]";
        this.ProcessInstrumentUpdateMessage(message, true);
    }
}

_DataCache.prototype.AddAggregatedInstrument = function (ins)
{
    if (!this.AggregatedInstruments[ins.ShortName])
        this.AggregatedInstruments[ins.ShortName] = new Array();

    if (ins.SourceName)
    {
        if (!this.AggregatedInstruments[ins.SourceName])
            this.AggregatedInstruments[ins.SourceName] = new Array();
        this.AggregatedInstruments[ins.SourceName].push(ins);
    }

    this.AggregatedInstruments[ins.ShortName].push(ins);
}

_DataCache.prototype.GetAggregatedInstrument = function (name)
{
    return this.AggregatedInstruments[name] || null;
}

//#region хуй пойми нужно ли сейчас

/// <summary>
/// Главный метод - дает линейное отображение 
/// иерархии иснтрумнентов с типами
/// </summary>
/// <returns></returns>
//DataCache.prototype.GetInstrumentListByType = function ()
//{
//    let tradableRoutes = this.RoutesArray();

//    let isHideRoute = false;

//    // Получаем список инструмнтов и разбиваем его на группы
//    let instList = new Object()
//    //let instrumentsList = this.Instruments;
//    let instrumentsList = Object.keys(this.Instruments).map(function (value) { return this.Instruments[value]; }.bind(this));
//    let routesArrayCount = tradableRoutes.length;
//    for (let i = 0 ; i < k.length; i++)
//    {
//        let ins = instrumentsList[i];
//        if (ins.IsHideRouteMode && routesArrayCount > 0 && (ins.AggregatedInstrument))
//            continue;

//        if (ins.InvisibleByUpdateHideRouteMode)
//            continue;

//        let l = instList[ins.TypeId];
//        if (l == null)
//            instList[ins.TypeId] = l = new Array();
//        l.Add(ins);
//    }
//    // Сортируем каждую группу
//    //TODO IMPLEMENT
//    //foreach (ArrayList l in instList.Values)
//    //l.Sort(new InstrumentComparer());
//    // Получаем список инструментов отсортированных по иерархии
//    let list = new Array();
//    list.AddRange(InstrumentTypeList);
//    // Проходим по списку и включаем в него названия инструментов
//    letlst = new Array();
//    for (let i = 0; i < list.length; i++)
//    {
//        // Добавляем название группы
//        let insTp = list[i];
//        lst.Add(insTp);
//        // Получаем отсортированную группу инструментов
//        let subList = instList[insTp.TypeId];
//        // Если инструменты этой группы есть - добавляем их
//        if (subList != null)
//            lst.AddRange(subList);
//    }
//    return lst;
//}

//DataCache.prototype.getSortedInstruments = function ()
//{
//    // Получаем список инструментов отсортированных по типу
//    let list = this.GetInstrumentListByType();
//    let strList = new Array();//new List<IInstrument>();
//    for (let i = 0; i < list.length; i++)
//    {
//        let ins = list[i];
//        if (ins == null)
//            continue;
//        if (ins.InvisibleByUpdateHideRouteMode)
//            continue;

//        strList.push(ins);
//    }
//    return strList;
//}

//#endregion хуй пойми нужно ли сейчас

_DataCache.prototype.AddSpreadPlan = function (message)
{
    //
    // Spread plan
    //
    try
    {
        if (!this.Loaded)
        {
            //стадия загрузки. сохраняем только непустые планы
            //теперь сохраняем все планы

            let plan = new SpreadPlan();
            plan.InitBy(message, this);
            this.SpreadPlans[message.Id] = plan;
            this.SpreadPlan = plan;
        }
        else
        {
            //мы подконнекчены, пришел спредплан - значит, его поменяли, и нам нужно подхватить апдейт

            let plan = this.SpreadPlans[message.Id];
            if (!plan)
            {
                //не было такого плана еще
                this.SpreadPlans[message.Id] = plan = new SpreadPlan();
                plan.InitBy(message, this);

                let acc = Object.keys(this.Accounts);


                //    let accs = new Account[accounts.Count];
                //    accounts.Values.CopyTo(accs, 0);

                //    foreach (Account acc in accs)
                //    {
                //        if (acc.SpreadPlanId == message.Id)
                //    {
                //        acc.SpreadPlan = plan;
                //        this.OnUpdateAccount(acc);
                //    }
                //}
            }
            else
                plan.InitBy(message, this);
        }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        console.log(ex);
    }
}

_DataCache.prototype.GetSpreadPlan = function (account)
{
    let id = null;
    if (account)
        id = account.SpreadPlanId
    else if (this.MainAccountNew)
        id = this.MainAccountNew.SpreadPlanId

    return this.SpreadPlans[id]
}

// получение массива HistoryType по периоду(для тиков берутся Trades и Ask или Bid)
_DataCache.prototype.GetAllowedHistoryTypesByPeriod = function (period)
{
    var listHistoryType = new Array();

    var allowedHistoryTypes = this.AllowedHistoryTypes;
    var isTickQuote = false;

    var len = allowedHistoryTypes.length;
    for (var i = 0; i < len; i++)
    {
        var allowedHistoryType = allowedHistoryTypes[i];
        //+++denis http://tp.pfsoft.net/Project/QA/Bug/View.aspx?BugID=16422&acid=74FA48A38CBEE17F6445F76C9BEC721B&list=STATE_0_ctl00_mainArea_projectCatalogZone1_ctl00_tdL_gA&pageIndex=1
        if (period === Periods.TIC)
        {
            if (!isTickQuote && (allowedHistoryType === HistoryType.BID || allowedHistoryType === HistoryType.ASK))
            {
                listHistoryType.push(allowedHistoryType);
                isTickQuote = true;
            }
            else if (allowedHistoryType === HistoryType.LAST)
            {
                listHistoryType.push(allowedHistoryType);
            }
        }
        else listHistoryType.push(allowedHistoryType);
    }
    return listHistoryType;
};

Object.defineProperty(_DataCache.prototype, 'AllowedHistoryTypes', {
    get: function ()
    {
        return this.FAllowedHistoryTypes ? this.FAllowedHistoryTypes.slice() : new Array();
    }
});

// From MultiDataCache.cs
_DataCache.prototype.formatPrice = function (symbol, price)
{
    /*
    DecimalFormat ft = new DecimalFormat();
    ft.GroupingUsed = true;
    ft.setMaximumFractionDigits(getPrecision(symbol));
    return ft.FormatDouble(price);
    */

    // TODO. Replace with code from above.
    var ins = this.getInstrumentByName(symbol);
    if (ins) return ins.formatPrice(price);
};

_DataCache.prototype.quoteCache_OnReload = async function (quoteController, historyParams, signal)
{
    const conn = Connection;
    if (!conn)
    {
        return [];
    }

    const intervals = await conn.GetHistoryPromise(historyParams, signal);
    return intervals;
}

//IInstrument instr, double volume, bool showLots, ProductType productType = ProductType.General
_DataCache.prototype.formatVolume = function (instr, volume, showLots, productType)
{
    var roundedValue = volume;
    var precision = showLots ? 3 : 0;

    precision = instr?.getAmountPrecision(showLots);

    roundedValue = PriceFormatter.getNumberFormatter(precision).format(roundedValue)

    return roundedValue;
};

// TODO. Refactor.
_DataCache.prototype.getBindToOrderIdArray = function (srcOrder)
{
    let arr = []

    let orderDict = this.OrderDict
    for (let key in orderDict)
    {
        let bindToOrder = orderDict[key]
        if (_DataCache.canBindTo(srcOrder, bindToOrder))
            arr.push(bindToOrder.OrderNumber)
    }
    return arr
}

_DataCache.canBindTo = function (srcOrder, bindToOrder)
{
    if (!srcOrder ||
        !bindToOrder ||
        srcOrder.Instrument !== bindToOrder.Instrument ||
        srcOrder.Account !== bindToOrder.Account ||
        srcOrder.SharesFilled !== 0 ||
        bindToOrder.SharesFilled !== 0 ||
        !srcOrder.Active ||
        !bindToOrder.Active)
        return false

    let orderTypes = OrderType

    let srcOrdType = srcOrder.OrderType
    let bindToOrdType = bindToOrder.OrderType

    if (srcOrdType === orderTypes.Market ||
        bindToOrdType === orderTypes.Market)
        return false

    let srcBuy = srcOrder.BuySell
    let bindToBuy = bindToOrder.BuySell

    let isSrcLimit = srcOrdType === orderTypes.Limit
    let isSrcStop =
        srcOrdType === orderTypes.Stop ||
        srcOrdType === orderTypes.StopLimit ||
        srcOrdType === orderTypes.TrailingStop

    let isBindToLimit = bindToOrdType === orderTypes.Limit
    let isBindToStop =
        bindToOrdType === orderTypes.Stop ||
        bindToOrdType === orderTypes.StopLimit ||
        bindToOrdType === orderTypes.TrailingStop

    // Ордера с одинаковым типом, но с разным направлением можно линковать.
    if (srcOrdType === bindToOrdType)
        return srcBuy !== bindToBuy

    // Ордера с разным типом и с одинаковым направлением можно линковать,
    // тип имеет значение.
    return srcBuy === bindToBuy &&
        // один должен быть limit, а второй или Stop, или StopLimit, или TrailingStop
        (isSrcLimit && isBindToStop || isSrcStop && isBindToLimit)
}

_DataCache.prototype.GetInstrumentList = function (patern, exchangeIDs, instrumentTypes, generateStrikes, searchInDescription, aliasLanguage, oldLookupType)
{
    if (!this.NonFixedList)
        return Promise.resolve(this.InstrumentsArray);

    return Connection.vendor.GetInstrumentList(patern, exchangeIDs, instrumentTypes, generateStrikes, searchInDescription, aliasLanguage, oldLookupType)
        .then(this.ResponseGenerationFakeInstruments.bind(this, generateStrikes));
}

_DataCache.prototype.ResponseGenerationFakeInstruments = function (generateStrikes, msg)
{
    let result = new Array();
    let ansverList = msg[0].NonFixedInstrumentInfoList;
    for (let i = 0; i < ansverList.length; i++)
    {
        // тут надо по всем правилам сгенерить фэйк-инструменты по тому что прислал сервер
        let entry = ansverList[i];
        let im = new DirectInstrumentMessage();
        im.Name = entry.Name;
        im.ExchangeId = entry.ExchangeId;
        im.TradingExchange = entry.TradingExchange;
        im.MarketDataExchange = entry.MarketDataExchange;
        im.InstrumentTradableID = entry.InstrumentTradableID
        im.InstrType = entry.InstrType;
        im.TypeId = entry.GroupId;
        im.Id = entry.ID;
        im.ForwardBaseInstrument = entry.Underlier;
        im.ForwardBaseInstrumentUnderlier = entry.UnderlierUnderlier;
        im.ForwardBaseInstrumentDescr = entry.UnderlierDescr;
        im.ForwardBaseInstrumentCountryId = entry.UnderlierCountryId;
        im.ForwardBaseInstrumentTradingExchange = entry.UnderlierTradingExchange;
        // НУЖЕН норм. признак для формы лукапа
        im.Exp2 = Instrument.FAKE_NONFIXED_RESPONSE;
        im.InstrDateSettingsList = entry.InstrDateSettingsList;
        im.UseOptionDescription = entry.UseOptionDescription;
        im.AllExpDateLoaded = entry.AllExpDateLoaded;
        im.AllContractsLoaded = entry.AllContractsLoaded;
        im.SeriesGroupName = entry.SerieName;
        im.Descr = entry.Description;
        im.UnderlierDescription = entry.Underlier;

        if (entry.LanguageAliases != null)
            im.LanguageAliases = Instrument.CloneLangAliases(entry.LanguageAliases);

        let pointSize = entry.PointSize;
        let routesSupported = new Array();
        let routesTradable = new Array();

        this.AddExchangeToCache(entry.TradingExchange);

        im.CountryId = entry.CountryId;

        for (let j = 0; j < entry.Routes.length; j++)
        {
            let routeId = entry.Routes[j];
            let rroute = this.getRouteById(routeId);
            if (rroute != null && rroute.IsTradable)
            {
                im.TradableRoutes.push(routeId);// += rroute.Name + ";";
                routesTradable.push(routeId);
            }
            if (rroute !== null && rroute.IsInfo)
            {
                im.RoutesSupported.push(routeId);// += rroute.Name + ";";
                routesSupported.push(routeId)
            }
        }

        /* логика схлопывания роутов когда инфо+трейд. */
        let showInfoRoutes = true;
        if (routesTradable.length === 1 && routesSupported.length === 1)
            showInfoRoutes = false;
        else
            showInfoRoutes = true;


        for (let j = 0; j < entry.Routes.length; j++)
        {
            let routeID = entry.Routes[j];
            let rr = this.getRouteById(routeID);
            let route = rr != null ? rr.Name : /*тут не должно заходить*/"ROUTE_ID_" + routeID;

            //if (!rr)
            //    continue; //TODO

            if (!showInfoRoutes && rr.IsInfo && !rr.IsTradable) // если надо прятать инфо
                continue; // вообще не добавляем инфошные если их надо прятать(схлопывать!)

            if (im.InstrType === InstrumentTypes.OPTIONS)
            {
                for (let z = 0; z < im.InstrDateSettingsList.length; z++)
                {
                    let ds = im.InstrDateSettingsList[z];
                    let tmp = {};
                    for (let p = 0; p < ds.StrikePricesList.length; p++)
                        tmp[ds.StrikePricesList[p].StrikePrice] = ds.StrikePricesList[p];
                    ds.StrikePricesListDict = tmp;
                    this.GenerateFakeOptionPair(result, im, ds, routeID, pointSize, generateStrikes/*, cancellationToken*/);
                }
            }
            else
            {
                // все неопцоны - обрабатываются тут
                var fakeInstruments = this.GenerateInstruments(im, routeID/*, cancellationToken, GenerateInstrumentContext.IntellisenseList*/);
                result = result.concat(fakeInstruments);
            }
        }
    }
    return result;
}

_DataCache.prototype.getInstrumentById = function (instrumentID)
{
    if (!this.NonFixedList)
        return Promise.resolve(false);

    let me = this;
    return Connection.vendor.getInstrumentById(instrumentID)
        .then(me._gIBId);
};

//the same
_DataCache.prototype.updateInstrumentById = _DataCache.prototype.getInstrumentById;
// DataCache.prototype.updateInstrumentById = function (instrumentID)
// {
//     if (!this.NonFixedList)
//         return Promise.resolve(false);

//     let me = this;
//     return App.Connection.vendor.getInstrumentById(instrumentID)
//         .then(me._gIBId);
// };

_DataCache.prototype._gIBId = function (msg)
{
    if (!msg.length)
        return false;

    let _msg = msg[0];

    if (_msg.Code !== DirectMessageCode.CODE_INSTRUMENT)
        return false;

    this.NewMessage(_msg);
    return true;
}

_DataCache.prototype.getInstrumentByNameNFL = function (instrumentName)
{
    if (!this.NonFixedList)
        return Promise.resolve(this.getInstrumentByName(instrumentName));

    return Connection.vendor.getInstrumentByNameNFL(InstrumentUtils.RemoveRouteName(instrumentName))
        .then((msg) =>
        {
            if (!msg)
                return null;
            if (!msg.length)
                return null;
            if (msg[0].Code !== DirectMessageCode.CODE_INSTRUMENT)
                return null;
            for (let i = 0; i < msg.length; i++)
                if (msg[i].Code === DirectMessageCode.CODE_INSTRUMENT)
                    this.NewMessage(msg[i]);
            let ins = this.getInstrumentByName(instrumentName);
            if (!ins)
                return null;
            let rpKeys = Object.keys(this.riskPlanDict);
            for (let i = 0, len = rpKeys.length; i < len; i++)
                this.riskPlanDict[rpKeys[i]].processOneInstrument(ins, this.riskPlanDict[rpKeys[i]].items);

            return ins;
        });
};

_DataCache.prototype.getInstrumentByInstrumentTradableID_NFL = function (InstrumentTradableID, Route, InteriorID = null, RouteValidationIgnore = false)
{
    if (isNaN(InstrumentTradableID))
        return Promise.resolve(null);

    if (!this.getRouteById(Route) && !RouteValidationIgnore)
        return Promise.resolve(null);

    let mb_we_have_instrument = this.getInstrumentByTradable_ID(InstrumentTradableID, Route);
    if (mb_we_have_instrument)
        return Promise.resolve(mb_we_have_instrument);

    //#98849 возможен фикс
    if (!this.NonFixedList)
        if (RouteValidationIgnore)
        {
            let mb_we_have_instrument_by_t_id = this.getInstrumentByTradable_ID_first(InstrumentTradableID);
            return Promise.resolve(mb_we_have_instrument_by_t_id);
        }
        else
            return Promise.resolve(null);

    return Connection.vendor.getInstrumentByInstrumentTradableID_NFL(InstrumentTradableID)
        .then((msg) =>
        {
            if (!msg)
                return null;
            if (!msg.length)
                return null;
            if (msg[0].Code !== DirectMessageCode.CODE_INSTRUMENT)
                return null;
            for (let i = 0; i < msg.length; i++)
                if (msg[i].Code === DirectMessageCode.CODE_INSTRUMENT)
                    this.NewMessage(msg[i]);
            let ins = null;

            if (!InteriorID)
                ins = this.getInstrumentByTradable_ID(InstrumentTradableID, Route);
            else
                ins = this.getInstrumentByName(InteriorID);

            if (!ins)
            {
                if (RouteValidationIgnore)
                    ins = this.getInstrumentByTradable_ID_first(InstrumentTradableID);
                if (!ins)
                    return null;
            }
            let rpKeys = Object.keys(this.riskPlanDict);
            for (let i = 0, len = rpKeys.length; i < len; i++)
                this.riskPlanDict[rpKeys[i]].processOneInstrument(ins, this.riskPlanDict[rpKeys[i]].items);

            return ins;
        });
};

_DataCache.prototype.GetNonFixedInstrumentListByAssetName = function (AsssetName)
{
    let me = this;
    return Connection.vendor.GetNonFixedInstrumentListByAssetName(AsssetName)
        .then(this.ResponseGenerationFakeInstruments.bind(this, false))
};

_DataCache.prototype.GetNonFixedInstrumentStrikes = function (instrument)
{
    return Connection.vendor.GetNonFixedInstrumentStrikes(instrument.ContractID)
        .then((msg) =>
        {
            var im = instrument.CreateInstrumentMessage();
            let result = new Array();
            let findedStrikes = msg[0];
            // TODO КОСТЫЛЬ переделать запрос, изменить instrument передавать контракт
            im.Descr = "";
            for (let i = 0, len = im.InstrDateSettingsList.length; i < len; i++)
            {
                let ds = im.InstrDateSettingsList[i];
                if (findedStrikes != null)
                {
                    if (+ds.ContractMonthDate === +instrument.ExpDate)
                    {
                        ds.StrikePricesList = findedStrikes.strikePricesList;
                        //Todo
                        let tmp = new Object();
                        for (let j = 0, len_j = ds.StrikePricesList.length; j < len_j; j++)
                            tmp[ds.StrikePricesList[j].StrikePrice] = ds.StrikePricesList[j];
                        ds.StrikePricesListDict = tmp;
                        OptionContract.MergeOptionContractGroup(ds, findedStrikes.optionContractGroup);
                        this.GenerateFakeOptionPair(result, im, ds, instrument.Route, findedStrikes.PointSize, true);
                    }
                }
            }
            return result;
        });
}

_DataCache.prototype.GenerateInstruments = function (mess, route/*, CancellationToken cancellationToken, GenerateInstrumentContext context = GenerateInstrumentContext.Normal*/)
{
    let instruments = new Array();
    let isFuture = mess.InstrType === InstrumentTypes.FUTURES || mess.InstrType === InstrumentTypes.CFD_FUTURES;
    let isOption = mess.InstrType === InstrumentTypes.OPTIONS;

    let cancellationToken;
    let context;

    if (isFuture)
    {
        //фьючерс детектед
        //New schema
        let list = mess.InstrDateSettingsList;
        for (let i = 0; i < list.length; i++)
        {
            let ds = list[i];
            if (ds.ContractMonthDate.getFullYear() <= 1970)
                continue;

            let futureInstrument = this.GenerateFutureInstrument(mess, ds, route, false);
            instruments.push(futureInstrument);

            if (ds.IsContinious)
            {
                futureInstrument.IsContiniousContract = true; // по этому инструменту есть cont.contract
                futureInstrument.HaveContiniousContract = true;
                if (!this.ContractsCache[route])
                    this.ContractsCache[route] = new Object();
                if (mess.Id)
                    this.ContractsCache[route][mess.Id] = futureInstrument;

                instruments.push(this.GenerateFutureInstrument(mess, ds, route, true));
            }
        }
    }
    else if (isOption)
    {
        let len = mess.InstrDateSettingsList.length;
        for (let i = 0; i < len; i++)
        {
            //
            // alexb opt: cache baseInstr
            //
            let ds = mess.InstrDateSettingsList[i];

            let underlierName = mess.ForwardBaseInstrument;

            //let tmp = ds.FutureAliasName.split(" ")[0];
            //if (tmp && tmp.indexOf(underlierName) !== -1)
            //    underlierName = tmp;


            let baseInstr = this.FindUnderlier(mess, underlierName, route, ds.UnderlierTradableId || ds.InstrumentTradableID);
            if (baseInstr == null)
                return null;

            for (let j = 0, len_j = ds.StrikePricesList.length; j < len_j; j++)
            {
                let sett = ds.StrikePricesList[j];
                if (ds.ContractMonthDate.getFullYear() <= 1970)
                    continue;

                if (sett.CallEnabled)
                {
                    let optionInstr = this.GenerateOptionInstrument(mess, ds, route, OptionPutCall.OPTION_CALL_VANILLA, baseInstr, sett);
                    if (optionInstr != null)
                        instruments.push(optionInstr);
                }
                if (sett.PutEnabled)
                {
                    let optionInstr = this.GenerateOptionInstrument(mess, ds, route, OptionPutCall.OPTION_PUT_VANILLA, baseInstr, sett);
                    if (optionInstr != null)
                        instruments.push(optionInstr);
                }

            }

            //добавляем главный опцион в отдельный список
            //let name = DataCache.GetFullName(mess.Name, route);
            //let instr;
            //if (FOptionInstruments.TryGetValue(name, /*out*/ instr))
            //{
            //    instr.UpdateByMessage(mess);
            //    SetInvisibleByUpdateHideRouteMode(instr, mess);
            //}
            //else
            //{
            //    instr = new Instrument( this, mess, route);
            //    if (instr != null && (instr.ForwardBaseInstrument != null || instr.ForwardBaseInstruments.Length > 0))
            //    {
            //        FOptionInstruments[name] = instr;
            //        SetInvisibleByUpdateHideRouteMode(instr, mess);
            //    }
            //}
            //instr.SourceName = mess.Name;
        }
    }
    else
    {
        let instrNames = this.Instruments;
        //простой инструмент
        let name = InstrumentUtils.GetFullName(mess.Name, route).toUpperCase();
        let instr = new Instrument(this, mess, route);
        // TODO UNIC names

        //let instr = instrNames.hasOwnProperty(name) ? instrNames[name] : new Instrument(/*FQuoteController,*/ this, mess, route);
        instruments.push(instr);
    }

    return instruments;
}

_DataCache.prototype.GenerateFutureInstrument = function (mess, ds, route, isContinious/*, GenerateInstrumentContext context = GenerateInstrumentContext.Normal*/)
{
    let name = mess.Name;

    let namingType = mess.FuturesNamingType | this.FuturesNamingType;

    if (ds.FutureAliasName)
        name = ds.FutureAliasName;

    let instrument = this.GenerateInstrument((isContinious ? ("[" + name + "]") : name), mess, ds, route/*, context: context*/);
    if (isContinious)
    {
        instrument.InstrumentSpecificType = InstrumentSpecificType.ContiniousContract;
        instrument.ContiniousContractName = name;
    }

    return instrument;
}

_DataCache.prototype.GenerateInstrument = function (name, mess, ds, route, forseCreateFake /*, GenerateInstrumentContext context = GenerateInstrumentContext.Normal*/)
{
    if (!forseCreateFake)
        forseCreateFake = false;

    let copy = mess.Clone();

    copy.Name = name;
    let fullName = InstrumentUtils.GetFullName(name, route).toUpperCase();

    let instrNames = this.Instruments;

    //TODO UNIC names

    //let useInstrumentFromCache = !!instrNames[fullName] && !forseCreateFake;
    let useInstrumentFromCache = false;
    //let der_instr = useInstrumentFromCache ? instrNames[fullName] : new Instrument(this, copy, route);
    let der_instr = new Instrument(this, copy, route);

    // +++ nfrolov: Если инструмента в кэше еще нет, то можем спокойно (и должны) установить все поля из мессэджа
    if (/*context == GenerateInstrumentContext.Normal ||*/ !useInstrumentFromCache)
    {
        der_instr.SourceName = mess.Name;

        der_instr.SeriesGroupName = mess.SeriesGroupName;

        der_instr.SourceDescription = mess.Descr;

        this.UpdateDerInstr(der_instr, ds, mess);

        if (mess.MarginCoeficientList.length > 0)
        {
            let futCoef = mess.MarginCoeficientList[0];
            for (let fc in mess.MarginCoeficientList)
                if (fc.FuturesExpYear == ds.ContractMonthDate.Year && fc.FuturesExpMonth == ds.ContractMonthDate.Month && fc.FuturesExpDay == ds.ContractMonthDate.Day)
                {
                    futCoef = fc;
                    break;
                }

            der_instr.RiskSettings.MarginCoefficients = futCoef;
        }
    }

    return der_instr;
}

_DataCache.prototype.GetBrandingRules = function (brandingKey)
{
    let me = this;

    return Connection.vendor.GetBrandingRules(brandingKey)
        .then(function (msg)
        {
            if (!msg.length || msg[0].Code !== DirectMessageCode.CODE_BRANDING_MESSAGE)
                return -1;


            if (msg[0].CompanyName)
                me.CompanyName = msg[0].CompanyName             // #85820

            if (msg[0].BrokerInformation)
                me.BrokerInformation = msg[0].BrokerInformation // #87238

            if (msg[0].PoweredByName)
                me.PoweredByName = msg[0].PoweredByName         // #88323
            if (msg[0].PoweredByURL)
                me.PoweredByURL = msg[0].PoweredByURL           // #88323

            if (msg[0].BrandingClientSessionTimeoutInMinutes)
                me.BrandingClientSessionTimeoutInMinutes = parseInt(msg[0].BrandingClientSessionTimeoutInMinutes)

            if (msg[0].BrandingVisibleInstrumentLogo)
                me.BrandingVisibleInstrumentLogo = msg[0].BrandingVisibleInstrumentLogo === "1"

            return me.BrandingClientSessionTimeoutInMinutes;
        });
};

_DataCache.prototype.GetYieldRate = function (instrument)
{
    let instrumentID = instrument.Id,
        routeID = this.getRouteByName(instrument.getRoute()).RouteId;

    return Connection.vendor.GetYieldRate(instrumentID, routeID)
        .then(function (msg)
        {
            if (!msg.length || msg[0].Code !== DirectMessageCode.CODE_YTM)
                return false;

            if (msg[0].YieldRate)
                instrument.YieldRate = MathUtils.RoundDouble(msg[0].YieldRate, 4).toString();

            return true;
        });
};

_DataCache.prototype.AddDeferredMessage = function (msg)
{
    // if (this.SkipNewDefer)
    //     return

    let route = msg.Route,
        insTradableID = msg.InstrumentTradableID

    if (!route || !insTradableID) return

    let key = insTradableID + "::" + route;
    if (!this.deferredInsReq[key])
    {
        this.deferredInsReq[key] = true
        this.getInstrumentByInstrumentTradableID_NFL(insTradableID, route)
            .then(function (instrument)
            {

                this.deferredInsReq[key] = false
                if (!instrument)
                    return


                this.ProcessDeferredMessages(instrument);
            }.bind(this))
    }

    if (!this.deferredMessages[route])
        this.deferredMessages[route] = {}

    let deferredByRoute = this.deferredMessages[route]

    if (!deferredByRoute[insTradableID])
        deferredByRoute[insTradableID] = []

    deferredByRoute[insTradableID].push(msg)
};

_DataCache.prototype.ProcessDeferredMessages = function (instrument)
{
    let deferredMsgArr = []

    // this.SkipNewDefer = true;
    if (instrument === null)
    {
        let routes = Object.keys(this.deferredMessages)
        for (let i = 0; i < routes.length; i++)
        {
            let byRoute = this.deferredMessages[routes[i]],
                byIns = Object.keys(byRoute)

            for (let j = 0; j < byIns.length; j++)
                deferredMsgArr = deferredMsgArr.concat(byRoute[byIns[j]])
        }

        this.deferredMessages = {}
    }
    else
    {
        let route = instrument.Route,
            insTradableID = instrument.InstrumentTradableID

        if (!route || !insTradableID) return

        let deferredByRoute = this.deferredMessages[route]

        if (!deferredByRoute) return

        let deferredByInstrument = deferredByRoute[insTradableID]

        if (!deferredByInstrument || !deferredByInstrument.length) return

        deferredMsgArr = deferredByRoute[insTradableID].slice(0)

        delete deferredByRoute[insTradableID]

        if (!Object.keys(deferredByRoute).length)
            delete this.deferredMessages[route]
    }

    for (let i = 0; i < deferredMsgArr.length; i++)
    {
        //TODO заебашить норм в 3.99, то херь дикая как по мне 18.05.2021
        //переделать на менеджер этого всего
        let resMsgs = [];
        let msg = deferredMsgArr[i];
        if (msg.CPHFD)
            resMsgs = msg.CPHFD(msg);
        else
            resMsgs = [msg];

        for (let j = 0; j < resMsgs.length; j++)
            this.NewMessage(resMsgs[j])
    }
    // this.SkipNewDefer = false;
};

_DataCache.prototype.GenerateFakeOptionPair = function (result, im, ds, route, pointSize, generateStrikes/*, CancellationToken cancellationToken*/)
{
    // TODO: как только сервер дошлёт заменить на реальный пресижн
    let baseInstrPrecision = MathUtils.GetDecimalPlaces(pointSize, 20);

    var strikes = generateStrikes ? ds.StrikePricesList : new Array();

    // TODO его у нас нет
    if (strikes.length == 0)
        //для запроса опшионмастера
        result.push(this.GenerateFakeOptionInstrument(im, ds, route, -1, -1, baseInstrPrecision, true));

    for (let i = 0; i < strikes.length; i++)
    {
        let strike = strikes[i];
        let strikePrice = strike.StrikePrice;

        if (strike.CallEnabled)
        {
            let optionInstr = this.GenerateFakeOptionInstrument(im, ds, route, strikePrice, OptionPutCall.OPTION_CALL_VANILLA, baseInstrPrecision);
            if (optionInstr != null)
                result.push(optionInstr);
        }
        if (strike.PutEnabled)
        {
            let optionInstr = this.GenerateFakeOptionInstrument(im, ds, route, strikePrice, OptionPutCall.OPTION_PUT_VANILLA, baseInstrPrecision);
            if (optionInstr != null)
                result.push(optionInstr);
        }
    }
}


_DataCache.prototype.GenerateFakeOptionInstrument = function (mess, ds, route, strikeprice, putcall, baseinstrumentPrecision, OptionMasterLookupMode)
{
    if (!strikeprice)
        strikeprice = -1;

    if (putcall === undefined || putcall === null)
        putcall = -1;

    if (!baseinstrumentPrecision)
        baseinstrumentPrecision = 0;

    if (!OptionMasterLookupMode)
        OptionMasterLookupMode = false;
    /// baseinstrumentPrecision ! Женя: в этом методе и выше (GenerateOptionInstrument) надо брать вариабл тик сайз если он установлен. когда протягивать то для интелисенса надо тоже! 

    let underlierRoute = route;

    // 
    // Fake
    let underlierName = ds.Underlier || mess.ForwardBaseInstrument;
    let underlierDescription = mess.ForwardBaseInstrumentDescr;
    let underlierCountryId = mess.ForwardBaseInstrumentCountryId;
    let underlierTradingExchange = mess.ForwardBaseInstrumentTradingExchange;

    //TODO NOrmal logic 
    // if (ds.UnderlierDate)
    //     underlierName = ds.FutureAliasName;

    let baseInstr = this.getInstrumentByTradable_ID(ds.UnderlierTradableId, route);
    if (baseInstr == null)
    {
        var im_forBase = new DirectInstrumentMessage();
        im_forBase.Name = mess.ForwardBaseInstrument;
        im_forBase.RoutesSupported = mess.RoutesSupported; //route <- закомментил, а то IsHideRouteMode неправильно применялся
        im_forBase.TradableRoutes = mess.TradableRoutes; //#44590
        im_forBase.Exp2 = Instrument.FAKE_NONFIXED_RESPONSE;
        im_forBase.InstrType = ds.UnderlierDate ? InstrumentTypes.FUTURES : InstrumentTypes.INDICIES;
        im_forBase.SeriesGroupName = mess.SeriesGroupName;
        if (underlierDescription)
            im_forBase.Descr = underlierDescription;
        if (underlierCountryId)
            im_forBase.CountryId = underlierCountryId;
        if (underlierTradingExchange)
            im_forBase.TradingExchange = underlierTradingExchange;

        //#57604 - hsa: ранее мы использовали роут опциона при генерации андерлаера, но по факту это делать нельзя, т.к. у андерлаера может быть другой роут
        //нельзя генерировать фейковый андерлаер по неправильному роуту, если реальный андерлаер уже находится в агрегированных инструментах, т.к. в методе UpdateHideRouteMode апдейтится видимость инструментов на агрегированных инструментах
        let agrIns = this.getInstrumentByName(underlierName);
        //let agrIns = BaseApplication.App.MultiDataCache.GetAggregatedInstrument(underlierName);
        if (agrIns != null)
        {
            if (!agrIns.TradableRoutes.indexOf(underlierRoute) > 0 && !agrIns.RoutesSupported.indexOf(underlierRoute) > 0)
            {
                //не нашли нужный нам роут, берем первый (!) торговый (или первый информационный), это должно покрыть кейс, когда андерлаер и опцион находятся на разных роутах
                if (agrIns.TradableRoutes.length > 0)
                    underlierRoute = agrIns.TradableRoutes[0];
                else if (agrIns.RoutesSupported.length > 0)
                    underlierRoute = agrIns.RoutesSupported[0];
            }

            //нужно зааффектить InstrumentMessage, иначе неверно отработает метод UpdateHideRouteMode на инструменте
            im_forBase.RoutesSupported = String.StringJoin(";", agrIns.RoutesSupported);
            im_forBase.TradableRoutes = String.StringJoin(";", agrIns.TradableRoutes);
        }

        let baseInstrDs = new InstrDateSettings();
        if (ds.UnderlierTradableId)
            baseInstrDs.InstrumentTradableID = ds.UnderlierTradableId;

        if (im_forBase.InstrType == InstrumentTypes.FUTURES)
        {
            baseInstrDs.ContractMonthDate = ds.UnderlierDate;
            baseInstr = this.GenerateInstrument(underlierName, im_forBase, baseInstrDs, underlierRoute, true);
        }
        else
            baseInstr = this.GenerateInstrument(underlierName, im_forBase, baseInstrDs, underlierRoute, true);

        if (baseInstr == null)
            return null;

        // +++
        baseInstr.SourceName = mess.ForwardBaseInstrument;
    }
    // 3  кейса: 
    // 1. опцион без страйков для начального заполнения лукапа
    // 2. заполнение опциона со страйками для StrikesResponse
    // 3. OptionMasterLookupMode - имя как у андерлаера
    let name;

    //if (OptionMasterLookupMode)
    //    name = mess.Name;
    //else
    //{
    let aliasName = null;
    let optGenName = mess.Name;
    if (ds.useAliasName)
    {
        let s = ds.StrikePricesListDict[strikeprice];
        //let strikesList = ds.StrikePricesList != null ? ds.StrikePricesList.Where(x => (double)x.StrikePrice == strikeprice).ToList<StrikePriceSettings>() : new List<StrikePriceSettings>();
        //for (var s in strikesList)
        {
            if (putcall == OptionPutCall.OPTION_CALL_VANILLA && s.CallEnabled)
                aliasName = s.CallTicker;
            else if (putcall == OptionPutCall.OPTION_PUT_VANILLA && s.PutEnabled)
                aliasName = s.PutTicker;
        }
    }

    if (aliasName)
    {
        name = aliasName;
    }
    else
        name = optGenName;
    //}

    let cur_route = OptionMasterLookupMode ? underlierRoute : route;

    let instrument = this.GenerateInstrument(name, mess, ds, cur_route, true);
    instrument.StrikePrice = strikeprice;
    instrument.PutCall = putcall;

    let s = ds.StrikePricesListDict[strikeprice];

    let description = "";
    if (s && putcall == OptionPutCall.OPTION_CALL_VANILLA && s.CallEnabled)
    {
        instrument.InstrumentTradableID = s.InstrumentTradableIDCall;
        description = s.DescriptionCall || "";
    }
    else if (s && putcall == OptionPutCall.OPTION_PUT_VANILLA && s.PutEnabled)
    {
        instrument.InstrumentTradableID = s.InstrumentTradableIDPut;
        description = s.DescriptionPut || "";
    }

    if (mess.Descr)
        instrument.Description = instrument.SeriesDescription = mess.Descr;

    if (ds.Description)
        instrument.Description = instrument.SeriesDescription = ds.Description;

    if (description)
        instrument.Description = description;

    instrument.ForwardBaseInstrument = baseInstr;
    instrument.ForwardBaseInstruments = new Array(0);

    return instrument;
}

/// <summary>
/// внутри обновляются инструмент поля
/// </summary>
_DataCache.prototype.UpdateDerInstr = function (der_instr, ds, mess)
{
    if (der_instr == null || ds == null)
        return;

    der_instr.ContractID = ds.ContractID;

    der_instr.ExpDate = ds.ContractMonthDate;
    der_instr.ExpDateReal = ds.ExpirationDate;

    der_instr.DeliveryStatus = ds.DeliveryStatus;
    der_instr.ContractMonthDate = ds.ContractMonthDate;
    der_instr.LastTradeDate = ds.LastTradeDate;
    der_instr.SettlementDate = ds.SettlementDate;
    der_instr.NoticeDate = ds.NoticeDate;
    der_instr.FirstTradeDate = ds.FirstTradeDate;
    der_instr.AutoCloseDate = ds.AutoCloseDate;

    der_instr.Limits.HightLimit = ds.HightLimit;
    der_instr.Limits.LowLimit = ds.LowLimit;
    der_instr.Limits.PriceLimitMeasure = ds.PriceLimitMeasure;

    der_instr.Limits.IsHighLimitFrontEndValidationEnabled = ds.IsHighLimitFrontEndValidationEnabled;
    der_instr.Limits.IsLowLimitFrontEndValidationEnabled = ds.IsLowLimitFrontEndValidationEnabled;

    if (mess.Descr)
        der_instr.Description = der_instr.SeriesDescription = mess.Descr;

    if (ds.Description)
        der_instr.Description = der_instr.SeriesDescription = ds.Description;

    //
    // Trading halt
    //
    der_instr.TradingMode = ds.TradingMode;


    // #53213 Tick size/Tick cost/Lot step on contact level
    if (ds.LotSize !== undefined)
        der_instr.LotSize = ds.LotSize;

    if (ds.PointSize !== undefined)
        der_instr.PointSize = ds.PointSize;

    if (ds.LotStep !== undefined)
        der_instr.LotStep = ds.LotStep;

    if (ds.FuturesTickCoast !== undefined)
        der_instr.FuturesTickCoast = ds.FuturesTickCoast;

    if (ds.VariableTicks != null)
        der_instr.VariableTickList = ds.VariableTicks;
    else if (ds.PointSize && ds.FuturesTickCoast) // не пришел переменный тиксайз, зато пришел тиксайз и тиккост, настроенный на серии, мы не имеем права брать переменный тиксайз из IM
        der_instr.VariableTickList = [new VariableTick({ lowLimit: Number.NEGATIVE_INFINITY, highLimit: Number.POSITIVE_INFINITY, allowLimit: true, pointSize: ds.PointSize, tickCost: ds.FuturesTickCoast })];

    der_instr.AdditionalFields = ds.AdditionalFields;

    //hsa. мы должны дополнительно еще задать точность
    if (ds.PointSize !== undefined || ds.VariableTicks !== null)
    {
        let prec = Instrument.CalculatePrecision(ds.PointSize !== undefined ? ds.PointSize : 0, ds.VariableTicks !== null ? ds.VariableTicks : der_instr.VariableTickList);
        der_instr.Precision = prec;
    }

    der_instr.TradeSessionStatusId = ds.TradeSessionStatusId || -1;
    der_instr.ExchangeSessionName = ds.ExchangeSessionName;

    der_instr.InstrumentTradableID = ds.InstrumentTradableID;

    der_instr.MinLot = ds.minLot
    der_instr.MaxLot = ds.maxLot

    der_instr.ISIN = ds.ISIN;
}

_DataCache.prototype.GenerateOptionInstrument = function (mess, ds, route, putcall, baseInstr, strikeSettings)
{
    let strikePrice = strikeSettings.StrikePrice;
    let optTicker = "";
    let lowLimit = 0;
    let highLimit = 0;
    let highLimitFrontEndValidationEnabled = null, lowLimitFrontEndValidationEnabled = null;
    let minLot = -1;
    let maxLot = -1;
    let priceLimitMeasure = PriceLimitMeasure.DISABLE;
    let description = "";
    let extFields = null;
    let tradeSessionStatusId;
    let exchangeSessionName;
    let InstrumentTradableID;
    let ISIN = "";


    if (putcall === OptionPutCall.OPTION_PUT_VANILLA)
    {
        optTicker = strikeSettings.PutTicker;
        lowLimit = strikeSettings.LowLimitPut;
        highLimit = strikeSettings.HighLimitPut;
        lowLimitFrontEndValidationEnabled = strikeSettings.IsLowLimitFrontEndValidationEnabledPut
        highLimitFrontEndValidationEnabled = strikeSettings.IsHighLimitFrontEndValidationEnabledPut
        minLot = strikeSettings.minLotPut
        maxLot = strikeSettings.maxLotPut
        priceLimitMeasure = strikeSettings.PriceLimitMeasurePut;
        description = strikeSettings.DescriptionPut;
        extFields = strikeSettings.ExtFieldsPut;
        tradeSessionStatusId = strikeSettings.TradeSessionStatusIdPut;
        exchangeSessionName = strikeSettings.ExchangeSessionNamePut;
        InstrumentTradableID = strikeSettings.InstrumentTradableIDPut;
        ISIN = strikeSettings.ISINPut;
    }
    else
    {
        optTicker = strikeSettings.CallTicker;
        lowLimit = strikeSettings.LowLimitCall;
        highLimit = strikeSettings.HighLimitCall;
        lowLimitFrontEndValidationEnabled = strikeSettings.IsLowLimitFrontEndValidationEnabledCall
        highLimitFrontEndValidationEnabled = strikeSettings.IsHighLimitFrontEndValidationEnabledCall
        minLot = strikeSettings.minLotCall
        maxLot = strikeSettings.maxLotCall
        priceLimitMeasure = strikeSettings.PriceLimitMeasureCall;
        description = strikeSettings.DescriptionCall;
        extFields = strikeSettings.ExtFieldsCall;
        tradeSessionStatusId = strikeSettings.TradeSessionStatusIdCall;
        exchangeSessionName = strikeSettings.ExchangeSessionNameCall;
        InstrumentTradableID = strikeSettings.InstrumentTradableIDCall;
        ISIN = strikeSettings.ISINCall;
    }

    let instrument = this.GenerateInstrument(optTicker, mess, ds, route);

    instrument.StrikePrice = strikePrice;
    instrument.PutCall = putcall;
    instrument.Limits.LowLimit = lowLimit;
    instrument.Limits.HightLimit = highLimit;
    instrument.Limits.IsHighLimitFrontEndValidationEnabled = highLimitFrontEndValidationEnabled;
    instrument.Limits.IsLowLimitFrontEndValidationEnabled = lowLimitFrontEndValidationEnabled;
    instrument.MinLot = minLot
    instrument.MaxLot = maxLot
    instrument.Limits.PriceLimitMeasure = priceLimitMeasure;
    if (description)
        instrument.Description = description;
    instrument.AdditionalFields = extFields;
    //указываю явно базовый инструмент, поскольку для случая, когда фьючерсов больше 1го,
    //инструмент сам определиться не может - какой именно ему нужен как базовый (не знает конкретную ds.ContractMonthDate)
    instrument.ForwardBaseInstrument = baseInstr;
    instrument.ForwardBaseInstruments = new Array(0); //если потребуется поменять, не забудь пофиксить ParseOptionName, иначе подписка накроется
    instrument.TradeSessionStatusId = tradeSessionStatusId | -1;
    instrument.ExchangeSessionName = exchangeSessionName;
    instrument.InstrumentTradableID = InstrumentTradableID;
    instrument.ISIN = ISIN;
    instrument.TradingMode = strikeSettings.TradingMode;     // #115179

    return instrument;
}

_DataCache.prototype.RecalculateBalances = function ()
{
    if (!this.Loaded)
        return;

    let orderDict = this.OrderDict
    let prevTable = this.InstrumentBalancerTable;
    this.InstrumentBalancerTable = new Object();
    let userBalanserTable = new Object();

    let posKeys = Object.keys(this.PositionDict);

    for (let i = 0, len = posKeys.length; i < len; i++)
    {
        let pos = this.PositionDict[posKeys[i]];

        let InsFullName = pos.Instrument.GetInteriorID();
        let pBal = this.InstrumentBalancerTable[InsFullName];
        if (!pBal)
        {
            pBal = new InstrumentBalancer(pos.Instrument);
            this.InstrumentBalancerTable[InsFullName] = pBal;
        }

        if (pos.Account != null)
        {
            let uBal = userBalanserTable[pos.Account.AcctNumber];
            if (!uBal)
            {
                uBal = new UserBalancer(pos.Account);
                userBalanserTable[pos.Account.AcctNumber] = uBal;
            }
            if (pos.lastPriceUpdated && !pos.isTradePosition) //#49087 - не считаем профит если не было котировок; #97626 - не учитываем трейдовую позицию для профита 
            {
                let profit = pos.getSettlementProfit(true);
                uBal.Profit += (pos.Instrument.OptionTradingStyle !== OptionTradingStyle.MarkToMarket) ? profit : 0;

                let netProfitInAccCur = pos.getNetSettlementProfit(true);
                if (netProfitInAccCur !== null && uBal.ProfitNet !== null)
                    uBal.ProfitNet += netProfitInAccCur;
                else
                    uBal.ProfitNet = null;

                //uBal.ProfitForMargin += MarginCounterLocal.GetProfitForMargin(pos, profit);
                let onlyProfit = MarginCounterLocal.GetProfitLossWithUnsettledProfitProperties(pos, profit > 0 ? profit : 0);
                uBal.ProfitForUnsettled += onlyProfit;
                let rs = pos.Instrument.RiskSettings;
                if (rs !== null && rs.AllowWithdrawUnrealizedProfit(pos.ProductType, pos.Account) === false)
                    uBal.OnlyProfitForWithdrawalAvailable += onlyProfit;

                uBal.LossForUnsettled += MarginCounterLocal.GetProfitLossWithUnsettledProfitProperties(pos, profit < 0 ? profit : 0);

                uBal.ProfitBase += pos.getProfitValue(false);

                let netProfitInBaseCur = pos.getNetProfit(false);
                if (netProfitInBaseCur !== null && uBal.ProfitNetBase !== null)
                    uBal.ProfitNetBase += netProfitInBaseCur;
                else
                    uBal.ProfitNetBase = null;

                uBal.OptionValue += pos.DataCache.CalculateOptionValue(pos.Instrument, pos.Amount, pos.BuySell, pos.CurPriceClose, pos.Account.assetBalanceDefault.Asset.Name);
                uBal.OptionPremium += pos.CalcultateOptionPremium();
            }
            if (!pos.isTradePosition)
                uBal.OpenPosNumber += 1;    // not counting trades number (FIFO) #80010
            uBal.OpenPosExposition += pos.getExpositionValue(true);
            uBal.OpenPosAmount += pos.Amount;

            if (pos.Instrument != null && (pos.Instrument.InstrType == InstrumentTypes.CORPORATE || pos.Instrument.InstrType == InstrumentTypes.EQUITIES || pos.Instrument.InstrType == InstrumentTypes.ETF) && !pos.Instrument.CFD) //#38604
                uBal.BlockedForStocksAccountCurrency += pos.OpenPrice * pos.Amount * pos.Instrument.LotSize * pos.OpenCrossPrice;

            let rp = pos.Account.RiskPlan;
            if (pos.Instrument !== null && rp !== null)
            {
                let item = rp.GetRisksForInstrument(pos.Instrument.GetInteriorID());

                let marginTypes = -1;
                if (item !== null && item.cache[pos.ProductType] !== null && item.cache[pos.ProductType] !== undefined)
                    marginTypes = item.cache[pos.ProductType].CalculateMarginType;

                //#82717
                if (marginTypes == MarginTypes.MARGIN_CUSTOM_COEF_WITH_FIXED_VALUE)
                {
                    let value = pos.OpenPrice * pos.Amount * pos.Instrument.LotSize * pos.OpenCrossPrice;
                    if (pos.BuySell == OperationType.Sell)
                        value = -value;
                    uBal.BlockedForStocks += value;
                }

                else if (marginTypes === MarginTypes.MARGIN_STOCK)
                {
                    // Бабаев: #62999
                    // Blocked for stocks должен расчитываться только для открытых Long позиций с типом маржи Stocks pre-paid. Раньше не проявлялось потому что не было возможности открыть шорт позицию.
                    if (pos.BuySell !== OperationType.Sell)
                        uBal.BlockedForStocks += pos.OpenPrice * pos.Amount * pos.Instrument.LotSize * pos.OpenCrossPrice;
                    // Бабаев: #62868 - для Stocks pre-paid считаем только buy-позиции
                    if (pos.BuySell !== OperationType.Sell)
                        uBal.StockValue += pos.CurPriceClose * pos.Amount * pos.Instrument.LotSize * (this.CrossRateCache.GetCrossPriceExp1Exp2(pos.Instrument.Exp2, pos.Account.BaseCurrency) - Math.abs(pos.GetMarkup()));
                }
            }

            //
            // +++ Asset support
            //
            if (pos.Account.AccountType == AccountType.MultiAsset && pos.Instrument != null)
            {
                let assetName = Instrument.GetAssetNameCorrect(pos.Instrument, pos.BuySell == OperationType.Buy);

                let userBalanceField = null;
                userBalanceField = uBal.AssetBalances[assetName];
                if (!userBalanceField)
                {
                    userBalanceField = new UserBalancesAssetBalanceFields();
                    uBal.AssetBalances[assetName] = userBalanceField;
                }

                // info
                userBalanceField.OpenPosAmount += pos.Amount;
                userBalanceField.OpenPosNumber += 1;
            }

            //PositionsValue
            if (pos.Instrument.LastQuote !== null && pos.Account !== null)
            {
                let sp = this.GetSpreadPlan(pos.Account)
                let lastPrice = (pos.BuySell == OperationType.Buy ? pos.Instrument.LastQuote.BidSpread_SP_Ins(sp, pos.Instrument) : pos.Instrument.LastQuote.AskSpread_SP_Ins(sp, pos.Instrument));
                let posVal = pos.Amount * pos.Instrument.LotSize * lastPrice * pos.getCrossPrice() * pos.Account.getCrossPrice();

                let instrVal = 0;
                if (uBal.DictPosInstrVal.hasOwnProperty(pos.Instrument))
                    instrVal = uBal.DictPosInstrVal[pos.Instrument];
                if (pos.BuySell == OperationType.Buy)
                    instrVal += posVal;
                else
                    instrVal -= posVal;

                uBal.DictPosInstrVal[pos.Instrument] = instrVal;
            }

            uBal.positions.push(pos);

            uBal.totalPositionAmount += pos.Amount;
            uBal.averagePositionPrice += pos.Price;
            uBal.NotIsOptionCount += 1;
        }



        pBal.Add(pos);
        pBal.AmountPosition += 1;

        if (pos.BuySell === OperationType.Buy)
            pBal.TotalPositionAmount += pos.Amount;
        else if (pos.BuySell === OperationType.Sell)
            pBal.TotalPositionAmount -= pos.Amount;

        if (pos.Account !== null && pos.Amount !== 0 && !pos.isTradePosition)     // not counting trades amount (FIFO) #80010 #85827
        {
            let accNumber = pos.Account.AcctNumber;
            //Total position amount
            let totalPositionAmount = 0;
            if (pBal.AccountTotalPositionAmount[accNumber])
                totalPositionAmount = pBal.AccountTotalPositionAmount[accNumber];

            if (pos.BuySell === OperationType.Buy)
                totalPositionAmount += pos.Amount;
            else if (pos.BuySell === OperationType.Sell)
                totalPositionAmount -= pos.Amount;
            pBal.AccountTotalPositionAmount[accNumber] = totalPositionAmount;



            //Total profit/loss
            let totalProfitLoss = 0;
            if (pBal.AccountTotalProfitLoss[accNumber])
                totalProfitLoss = pBal.AccountTotalProfitLoss[accNumber];

            //TODO PnL
            let val = pos.getSettlementProfit(true);
            if (!isNaN(val) && isFinite(val))
                totalProfitLoss += val;
            pBal.AccountTotalProfitLoss[accNumber] = totalProfitLoss;

            if (pos.BuySell === OperationType.Buy)
            {
                let accountPositionPriceMultiplyAmountBuy = 0;
                let accountPositionAmountBuy = 0;

                if (pBal.AccountPositionPriceMultiplyAmountBuy[accNumber] != null)
                    accountPositionPriceMultiplyAmountBuy = pBal.AccountPositionPriceMultiplyAmountBuy[accNumber];

                if (pBal.AccountPositionAmountBuy[accNumber] != null)
                    accountPositionAmountBuy = pBal.AccountPositionAmountBuy[accNumber];

                accountPositionPriceMultiplyAmountBuy += (pos.Price * pos.Amount);
                accountPositionAmountBuy += pos.Amount;

                pBal.AccountPositionPriceMultiplyAmountBuy[accNumber] = accountPositionPriceMultiplyAmountBuy;
                pBal.AccountPositionAmountBuy[accNumber] = accountPositionAmountBuy;
            }

            if (pos.BuySell === OperationType.Sell)
            {
                let accountPositionPriceMultiplyAmountSell = 0;
                let accountPositionAmountSell = 0;

                if (pBal.AccountPositionPriceMultiplyAmountSell[accNumber] != null)
                    accountPositionPriceMultiplyAmountSell = pBal.AccountPositionPriceMultiplyAmountSell[accNumber];

                if (pBal.AccountPositionAmountSell[accNumber] != null)
                    accountPositionAmountSell = pBal.AccountPositionAmountSell[accNumber];

                accountPositionPriceMultiplyAmountSell += (pos.Price * pos.Amount);
                accountPositionAmountSell += pos.Amount;

                pBal.AccountPositionPriceMultiplyAmountSell[accNumber] = accountPositionPriceMultiplyAmountSell;
                pBal.AccountPositionAmountSell[accNumber] = accountPositionAmountSell;
            }

            // qty для ордеров на sell
            let amountSellOrders = 0
            for (let key in orderDict)
            {
                let order = orderDict[key]
                if (
                    order.Active &&
                    order.BuySell === OperationType.Sell &&
                    order.Instrument === pos.Instrument &&
                    order.Account === pos.Account)
                    amountSellOrders += order.AmountRemaining
            }
            // TODO. Refactor.
            pos.AvailableForSell = amountSellOrders
        }

        pos.RecalcAmounts();
    }
    // https://tp.traderevolution.com/entity/105525
    for (let pk in this.PositionDictCorporateAction)
        this.PositionDictCorporateAction[pk].RecalcAmounts();

    //
    // Orders
    //
    let ordersKeys = Object.keys(this.OrderDict);
    for (let i = 0; i < ordersKeys.length; i++)
    {
        let ord = this.OrderDict[ordersKeys[i]];

        if (ord.Account != null && (ord.Active || (ord.BindTo && ord.BindTo != "-1") || (ord.PositionId && ord.PositionId != "-1")))
        {
            let uBal = userBalanserTable[ord.Account.AcctNumber];
            if (!uBal)
            {
                uBal = new UserBalancer(ord.Account);
                userBalanserTable[ord.Account.AcctNumber] = uBal;
            }

            uBal.OpenOrdAmount += ord.Amount;
            uBal.OpenOrdNumber += 1;

            //
            // +++ Asset support
            //
            if (ord.Account.AccountType == AccountType.MultiAsset && ord.Instrument != null)
            {
                let assetName = Instrument.GetAssetNameCorrect(ord.Instrument, ord.BuySell == OperationType.Buy);

                let userBalanceField = uBal.AssetBalances[assetName];
                if (!userBalanceField)
                {
                    userBalanceField = new UserBalancesAssetBalanceFields();
                    uBal.AssetBalances[assetName] = userBalanceField;
                }

                // info
                userBalanceField.OpenOrdAmount += ord.Amount;
                userBalanceField.OpenOrdNumber += 1;
            }

            if ((ord.Instrument.InstrType == InstrumentTypes.CORPORATE || ord.Instrument.InstrType == InstrumentTypes.EQUITIES || ord.Instrument.InstrType == InstrumentTypes.ETF) && !ord.Instrument.CFD) //#38604
                uBal.BlockedForStocksAccountCurrency += ord.Price * ord.Amount * ord.getCrossPrice() * ord.Account.getCrossPrice();
        }


        if (ord.Account != null)
        {
            if (ord.Active && ord.Status !== OrderStatus.PENDING_NEW)
            {
                let InsID = ord.Instrument.GetInteriorID();
                let pBal = this.InstrumentBalancerTable[InsID];
                if (!pBal)
                {
                    pBal = new InstrumentBalancer(ord.Instrument);
                    this.InstrumentBalancerTable[InsID] = pBal;
                }

                pBal.OrderAmount += 1;
            }

        }
    }



    try
    {
        let count = 0;

        //
        // AlexB Optimization: внутри getTotalMargin()
        // каждый раз запрашивается список позиций. Получим его один раз и передадим как параметр
        //
        for (let acc in this.Accounts)
        {
            acc = this.Accounts[acc];

            //this rules should be got for current user -u
            let marginWarn = acc.WarningLevel; //RuleSet.getRuleFloatValue(RulesSet.VALUE_MARGINCALL, null);
            let marginTrading = acc.TradingLevel; //RuleSet.getRuleFloatValue(RulesSet.VALUE_MARGINTRADING, null);
            let stopOut = acc.MarginLevel; //RuleSet.getRuleFloatValue(RulesSet.VALUE_STOPOUT, null);

            let uBal = userBalanserTable[acc.AcctNumber];

            // AlexB optimization: не будем создавать лишний объект, используем готовый с теми же нулевыми значениями
            if (uBal == null)
                uBal = this.EmptyUserBalancer;
            //    userBalanserTable[acc.AccountNumber] = (uBal = new UserBalancer(acc));                        

            acc.Profit = uBal.Profit;
            acc.LossForUnsettled = uBal.LossForUnsettled;
            acc.ProfitBase = uBal.ProfitBase;
            acc.ProfitNet = uBal.ProfitNet;
            acc.ProfitForMargin = uBal.ProfitForMargin;
            acc.ProfitForUnsettled = uBal.ProfitForUnsettled;
            acc.OnlyProfitForWithdrawalAvailable = uBal.OnlyProfitForWithdrawalAvailable;
            acc.ProfitNetBase = uBal.ProfitNetBase;
            acc.OpenOrdAmount = uBal.OpenOrdAmount;
            acc.OpenPosAmount = uBal.OpenPosAmount;
            acc.OpenPosNumber = uBal.OpenPosNumber;
            acc.OpenOrdNumber = uBal.OpenOrdNumber;
            acc.BlockedForStocksAccountCurrency = uBal.BlockedForStocksAccountCurrency;
            acc.BlockedForStocks = uBal.BlockedForStocks;
            acc.StockValue = uBal.StockValue;
            acc.OptionValue = uBal.OptionValue;
            acc.OptionPremium = uBal.OptionPremium;

            //
            // Asset support
            //
            if (acc.AccountType == AccountType.MultiAsset)
            {
                let userBalancesAssetFields = null;

                for (let assBal in acc.assetBalances)
                {
                    assBal = acc.assetBalances[assBal];
                    //
                    // !assBal.IsEmpty - для оптимизации
                    // Андрей сказал, что это признак того, что нет ордеров/позиций
                    //
                    let userBalancesAssetFields = uBal.AssetBalances[assBal.Asset.Name];
                    if (!assBal.IsEmpty && userBalancesAssetFields)
                    {
                        assBal.OpenOrdAmount = userBalancesAssetFields.OpenOrdAmount;
                        assBal.OpenPosAmount = userBalancesAssetFields.OpenPosAmount;
                        assBal.OpenPosNumber = userBalancesAssetFields.OpenPosNumber;
                        assBal.OpenOrdNumber = userBalancesAssetFields.OpenOrdNumber;
                    }
                    else
                    {
                        assBal.OpenOrdAmount = 0;
                        assBal.OpenPosAmount = 0;
                        assBal.OpenPosNumber = 0;
                        assBal.OpenOrdNumber = 0;
                    }
                }
            }


            acc.OpenPosExposition = uBal.OpenPosExposition;

            let posVal = 0;
            for (let instr in uBal.DictPosInstrVal.Keys)
                posVal += Math.Abs(uBal.DictPosInstrVal[instr]);
            acc.PositionsValue = posVal;

            //+++ antonV
            acc.averagePositionPrice = uBal.averagePositionPrice / uBal.NotIsOptionCount;
            acc.totalPositionAmount = uBal.totalPositionAmount;

            uBal.MarginWarning = ((acc.MinMargin + acc.BlockedForOrders) / marginWarn * 100);
            uBal.StopOut = (((acc.MinMargin + acc.BlockedForOrders) / stopOut) * 100);

            acc.MarginWarning = uBal.MarginWarning;

            let common = acc.Balance + uBal.ProfitForMargin;
            if (common != 0)
                acc.CurrMargin = (((acc.MinMargin + acc.BlockedForOrders) / (common)) * 100);
            else
                acc.CurrMargin = 0;

            //
            // +++
            //
            if (acc.User != null)
            {
                acc.User.marginUsed += acc.MinMargin;
                acc.User.curBalance += acc.Balance + acc.Profit;
            }

            let profitForWA = acc.Profit;
            if (profitForWA > 0)
                profitForWA = 0;

            // +++ Пересчитать today balance
            if (uBal.positions.length > 0)
            {
                acc.StockValueByBasis = 0;//GetStockValue(acc, StockValueMode.BeginBalance, uBal.positions);
                acc.StockValueWithoutPnl = 0;//GetStockValue(acc, StockValueMode.StockValueWithoutPnl, uBal.positions);
                acc.StockValueDiscount = 0;//GetStockValue(acc, StockValueMode.StockValueDiscount, uBal.positions);
            }
            else
            {
                acc.StockValueByBasis = 0;
                acc.StockValueWithoutPnl = 0;
                acc.StockValueDiscount = 0;
            }
            uBal.positions.length = 0;

            //users[count++] = acc;
        }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
    }


    //clear  prevTable
    if (prevTable)
    {

        let IBKeys = Object.keys(prevTable);

        for (let i = 0, len = IBKeys.length; i < len; i++)
        {
            let bal = prevTable[IBKeys[i]];
            let instrument = bal.instrument;

            instrument.amountPosition = 0;
            instrument.totalPositionAmount = 0;
            instrument.averagePositionPrice = 0;
            instrument.orderAmount = 0;
            instrument.TotalProfitLoss = 0;

            //instrument.AccountAmountPosition = bal.AccountAmountPosition;
            instrument.AccountAveragePositionPrice = new Object();
            //instrument.AccountOrderAmount = bal.AccountOrderAmount;
            instrument.AccountTotalPositionAmount = new Object();
            //instrument.AccountTotalPositionTicks = bal.AccountTotalPositionTicks;
            instrument.AccountTotalProfitLoss = new Object();
            instrument.AccountAveragePositionPriceWithAmountBuy = new Object();
            instrument.AccountAveragePositionPriceWithAmountSell = new Object();
            instrument.AccountPositionAmountBuy = new Object();
            instrument.AccountPositionAmountSell = new Object();
        }
    }

    let IBKeys = Object.keys(this.InstrumentBalancerTable);

    for (let i = 0, len = IBKeys.length; i < len; i++)
    {
        let bal = this.InstrumentBalancerTable[IBKeys[i]];
        let instrument = bal.instrument;

        instrument.amountPosition = bal.AmountPosition;
        instrument.totalPositionAmount = bal.TotalPositionAmount;
        instrument.averagePositionPrice = bal.GetAveragePositionPrice();
        instrument.orderAmount = bal.OrderAmount;
        instrument.TotalProfitLoss = bal.TotalProfitLoss;

        //instrument.AccountAmountPosition = bal.AccountAmountPosition;
        instrument.AccountAveragePositionPrice = bal.GetAveragePositionPriceByAccount();
        //instrument.AccountOrderAmount = bal.AccountOrderAmount;
        instrument.AccountTotalPositionAmount = bal.AccountTotalPositionAmount;
        //instrument.AccountTotalPositionTicks = bal.AccountTotalPositionTicks;
        instrument.AccountTotalProfitLoss = bal.AccountTotalProfitLoss;
        instrument.AccountAveragePositionPriceWithAmountBuy = bal.AccountPositionPriceMultiplyAmountBuy;
        instrument.AccountAveragePositionPriceWithAmountSell = bal.AccountPositionPriceMultiplyAmountSell;
        instrument.AccountPositionAmountBuy = bal.AccountPositionAmountBuy;
        instrument.AccountPositionAmountSell = bal.AccountPositionAmountSell;
    }

    this.OnRecalculatedBalances.Raise();
}


_DataCache.prototype.CalculateOptionValue = function (instr, quantity, buySell, currentPrice, assetName)
{
    if (instr == null || !instr.isOptionSymbol || (instr.OptionTradingStyle != OptionTradingStyle.Premium && instr.OptionTradingStyle != OptionTradingStyle.MarkToMarket))
        return 0;

    let amount = (buySell === OperationType.Buy ? 1 : -1) * quantity * instr.LotSize * instr.GetTickCost();

    return currentPrice * amount * this.CrossRateCache.GetCrossPriceExp1Exp2(instr.Exp2, assetName);
}

_DataCache.prototype.AccountOperationRequest = function (data)
{
    return Connection.AccountOperationRequest(data);
}

_DataCache.prototype.TryGetCrossratesPlan = function (id)
{
    return this.CrossratesPlans.GetPlanById(id);
}
_DataCache.prototype.TryGetRiskPlan = function (id)
{
    return this.riskPlanDict[id] || null;
}

_DataCache.prototype.TryGetPlan = function (id)
{
    return null; /*TODO*/
}
_DataCache.prototype.TryGetCommissionPlan = function (id)
{
    return this.CommissionPlans[id] || null;
}
_DataCache.prototype.TryGetSwapPlan = function (id)
{
    return null; /*TODO*/
}
_DataCache.prototype.TryGetChargingPlan = function (id)
{
    return null; /*TODO*/
}

//Метод мусорка ебаная
_DataCache.prototype.FindUnderlier = function (mess, underlierName, route, tradebleID)
{
    let baseInstr = this.getInstrumentByTradable_ID(tradebleID, route);
    if (baseInstr)
        //все ок, нашли точную пару
        return baseInstr;

    if (mess.ForwardBaseInstrumentID_ && this.ContractsCache[route])
        baseInstr = this.ContractsCache[route][mess.ForwardBaseInstrumentID_];

    if (baseInstr)
        return baseInstr;

    if (mess.ForwardBaseInstrumentID_)
        baseInstr = this.InstrumentsByIdCache[mess.ForwardBaseInstrumentID_];

    if (baseInstr)
    {
        baseInstr = this.getInstrumentByTradable_ID(baseInstr.InstrumentTradableID, baseInstr.Route);
        if (baseInstr)
            return baseInstr;
    }

    let aggrArr = this.GetAggregatedInstrument(mess.UnderlierDescription)
    if (aggrArr && aggrArr.length)
    {
        baseInstr = aggrArr[0]
        return baseInstr
    }

    return null;
}
_DataCache.prototype.GetInstrumentsByInstrumentId = function (id)
{
    return this.InstrumentsByIdCache[id] || null;
}

_DataCache.prototype.GetInstrumentsByContractID = function (id)  // return insArr where ins.NeedToHide == false && ins.ContractID == id, where id is method argument
{
    if (this._cachedInsListByContractID[id])
        return this._cachedInsListByContractID[id]

    let allIns = this.Instruments
    for (let insID in allIns)
    {
        let ins = allIns[insID]
        if (ins.ContractID == id && !ins.NeedToHide())
        {
            if (!this._cachedInsListByContractID[id])
                this._cachedInsListByContractID[id] = []

            this._cachedInsListByContractID[id].push(ins)
        }
    }

    return this._cachedInsListByContractID[id]
}

_DataCache.prototype.userHasSameCurrencyOnAllAccounts = function ()
{
    return this._userHasSameCurrencyOnAllAccounts;
}

_DataCache.prototype.cacheUserHasSameCurrencyOnAllAccounts = function ()
{
    const baseCurrencies = Object.values(this.Accounts).map(account => account.BaseCurrency);
    const uniqueCurrencies = new Set(baseCurrencies);

    this._userHasSameCurrencyOnAllAccounts = uniqueCurrencies.size === 1;
}

_DataCache.prototype.getUserBaseCurrency = function ()       // правила получения валюты аккаунта: https://ibb.co/HVtD0gw
{
    let accs = this.Accounts,
        accKeys = Object.keys(accs),
        currency = ''

    if (accKeys.length == 1 && accs[accKeys[0]].AccountType === AccountType.MultiAsset)
        return this.baseCurrency                            // пункт 2 выполняется тут

    for (let i = 0; i < accKeys.length; i++)
    {
        let accCurrency = accs[accKeys[i]].BaseCurrency
        if (currency.length && accCurrency != currency)     // у юзера несколько аккаунтов с разными валютами
        {
            let positions = this.PositionDict,
                posKeys = Object.keys(positions)

            currency = ''

            for (let j = 0; j < posKeys.length; j++)
            {
                let positionCurrency = positions[posKeys[j]].Account.BaseCurrency
                if (currency.length && positionCurrency != currency)
                    return this.baseCurrency                // пункт 4 (у юзера несколько аккаунтов с разными валютами и позиции открыты в разных валютах -> вовзвращем валюту сервера)
                else
                    currency = positionCurrency
            }
            //на случай отсутствия позиций
            return currency || this.baseCurrency            // пункт 5 выполняется тут (все позиции открыты в одной валюте ее и возвращаем) 
        }
        else
            currency = accCurrency
    }

    return currency                                         // пункт 1,3 выполняется тут
}

_DataCache.prototype.getFilledOrdersCurrency = function ()       // правила получения валюты аккаунта: https://ibb.co/HVtD0gw
{
    let accs = this.Accounts,
        accKeys = Object.keys(accs),
        currency = ''

    if (accKeys.length == 1 && accs[accKeys[0]].AccountType === AccountType.MultiAsset)
        return this.baseCurrency                            // пункт 2 выполняется тут

    let accsMask = {};

    if (accKeys.length > 1)
    {
        let len = 0;
        let keys = null;
        let useArray = true;
        if (this.filledOrdersArray.length)
            len = this.filledOrdersArray.length
        else
        {
            keys = Object.keys(this.newTradesDict);
            useArray = false;
            len = keys.length;
        }

        for (let j = 0; j < len; j++)
        {
            let obj = useArray ? this.filledOrdersArray[j] : this.newTradesDict[keys[j]];
            let filledOrder = obj.Account.BaseCurrency
            accsMask[filledOrder] = true;
        }

        keys = Object.keys(accsMask)
        if (keys.length > 1)
            return this.baseCurrency;  // пункт 4 (у юзера несколько аккаунтов с разными валютами и позиции открыты в разных валютах -> вовзвращем валюту сервера)
        else if (keys.length)
            currency = keys[0]; // пункт 5 выполняется тут (все позиции открыты в одной валюте ее и возвращаем)
        else
        {
            accsMask = {}; //3 выполняется тут (все позиции открыты в одной валюте ее и возвращаем)
            for (let i = 0; i < accKeys.length; i++)
                accsMask[accs[accKeys[i]].BaseCurrency] = true;
            let keys = Object.keys(accsMask)
            if (keys.length > 1)
                return this.baseCurrency;
            else if (keys.length)
                currency = keys[0];
        }
    }
    else
        currency = accs[accKeys[0]].BaseCurrency // пункт 1 выполняется тут

    return currency
}

_DataCache.prototype.ProcessBrokerMessage = function (msg)
{
    if (!msg) return

    let histBM = new HistoricalBM()
    histBM.UpdateByBM(msg)

    this.OnProcessBrokerMessage.Raise(histBM);

    if (this.Loaded && msg.ShowPopUp === false)
    {
        this.OnBMCounterMsgReceived.Raise(msg.ID, NotificationsCounterChangedEvent.AddOne)
        return  // Приходит 51 месседж с POP_UP = false, сразу же кладем его в панель Notifications как непрочитанный и увеличиваем счетчик. TerceraBrokerMessageScreen - не отображаем
    }

    if (msg.ShowPopUp !== false)
        brokerMessageScreenHandler.Show(msg)
}

_DataCache.prototype.SendChangeExerciseStatusRequest = function (positionID, status)
{
    return Connection.vendor.SendExerciseRequest(positionID, status)
}


_DataCache.prototype.ExerciseOptionsByPosition = function (position, placedFrom)
{
    if (!position || !position.PositionId) return

    this.GenerateExerciseRequestMessageDealTicket(position, false, placedFrom)

    return this.SendChangeExerciseStatusRequest(position.PositionId, Position.OPTION_EXERCISE_STATUS.PENDING_EXERCISE)
}

_DataCache.prototype.CancelExerciseByPosition = function (position, placedFrom)
{
    if (!position || !position.PositionId) return

    this.GenerateExerciseRequestMessageDealTicket(position, true, placedFrom)

    return this.SendChangeExerciseStatusRequest(position.PositionId, Position.OPTION_EXERCISE_STATUS.EXERCISED)
}

_DataCache.prototype.SendMarginRequest = function (accountId, instrumentID, routeID, amount, price, stopPrice, orderType, productType, leverage)
{
    return Connection.vendor.SendMarginRequest(accountId, instrumentID, routeID, amount, price, stopPrice, orderType, productType, leverage)
}

_DataCache.prototype.SubscribeToNewsIfNeed = function (caller = null)                         // Подписка на новости происходит при: 1) Открытии панели на воркспейсе. 2) Логине(перелогине) - если панель уже есть на воркспейсе.
{
    if (this.FirstNewsSubscription)
        this.FirstNewsSubscription.push(caller)
    if (this.SubscribedToNewsRoute)
        return false                                                                 // чтобы заново подписаться нужно сперва выполнить отписку

    this.SubscribedToNewsRoute = {}
    this.FirstNewsSubscription = [caller];
    this.SendNewsSubscribeMessage(SubscribeType.SUBSCRIBE)

    return true
};

_DataCache.prototype.UnsubscribeToNewsIfNeed = function (fromClosePanel)
{
    if (!this.SubscribedToNewsRoute)
        return                                                                  // подписки нет - отписываться не от чего

    if (!fromClosePanel || workspaceManagerHandler.numberOfPanelByType(PanelNames.NewsPanel) == 1)    // Отписка от новостей происходит при: 1) Логаут из приложения (fromClosePanel == false) 2) Закрытии последней открытой панели News (в процессе dispose потому кол-во NewsPanel еще равно единице)
    {
        this.SendNewsSubscribeMessage(SubscribeType.UNSUBSCRIBE)
        this.SubscribedToNewsRoute = null
        this.News = new Object();                                               // эта очистка необходима при переходе между Workspace-ами
        this.FirstNewsSubscription = null;
    }
};

_DataCache.prototype.SendNewsSubscribeMessage = function (subscribeType)
{
    let newsRoutesIDs = this.getNewsRoutesIDs()

    for (let i = 0; i < newsRoutesIDs.length; i++)
        Connection.vendor.SendNewsSubscribeMessage([newsRoutesIDs[i]], subscribeType)
};

_DataCache.prototype.ProcessNewsSubscribeResponse = function (msg)
{
    if (!msg || !msg.RouteIdsArr || !msg.RouteIdsArr.length)
        return

    let routeID = msg.RouteIdsArr[0],
        newSubscribe = false

    if (this.SubscribedToNewsRoute) 
    {
        this.SubscribedToNewsRoute[routeID] = !this.SubscribedToNewsRoute[routeID]
        if (this.SubscribedToNewsRoute[routeID])
            newSubscribe = true
    }

    if (newSubscribe)
        this.SendNewsRequestMessage(routeID)
            .then((result) =>
            {
                if (this.FirstNewsSubscription)
                    for (let c of this.FirstNewsSubscription)
                        c();
                return true;
            })
};

_DataCache.NEWS_REQUEST_NUMBER = 1000;                                    // Запрашиваемое количество новостей в панеле = 1000 штук по каждому новостному роуту - хардкод. Каждая последующая новость которая приходит в клиент в реал-тайм, перезаписывает более старую.

_DataCache.prototype.SendNewsRequestMessage = function (routeID, from, to)
{
    let newsNumber = _DataCache.NEWS_REQUEST_NUMBER;
    let startTime = from ? from : new Date();
    let endTime = to ? to : new Date();

    if (!from)
    {
        startTime.setHours(0); // Запрашиваются новости для Web приложения за сегодня
        startTime.setMinutes(0);
        startTime.setSeconds(0)
        startTime.setMilliseconds(0);
    }

    return Connection.vendor.SendNewsRequestMessage(routeID, newsNumber, startTime, endTime)
        .then((msg) =>
        {
            this.News = new Object();
            this.ProcessNewsMessage(msg, true, true);
            return true;
        })
};

_DataCache.prototype.ProcessNewsMessage = function (msg, needClear = false, skipEvent = false)
{
    if (!msg || !msg.NewsGroupsArr || !msg.NewsGroupsArr.length)
        return

    let news = msg.NewsGroupsArr
    for (let i = news.length - 1; i >= 0; i--)
    {
        let newsIt = news[i];
        let newsID = newsIt.getNewsID()
        let routeID = newsIt.getRouteID()

        let needCreate = false;

        if (needClear)
        {
            this.News[routeID] = null;
            needClear = false;
        }

        if (!this.News[routeID])
        {
            this.News[routeID] = [];
            this.News[routeID].Cash = {};
            needCreate = true;
        }
        let newsStorItem = this.News[routeID];
        let newsObj = newsStorItem.Cash[newsID];

        if (!needCreate && newsObj)
        {
            newsObj.UpdateByMsg(newsIt);
            continue;
        }

        newsObj = new News(newsIt)

        if (newsStorItem.length == _DataCache.NEWS_REQUEST_NUMBER)
        {
            let removedNews = newsStorItem.splice(0, 1)[0];
            this.OnRemoveNewsEvent.Raise(removedNews.GetID());
        }

        newsStorItem.Cash[newsID] = newsObj;
        newsStorItem.push(newsObj)

        if (!skipEvent)
            this.OnAddNewsEvent.Raise(newsObj);
    }
};

_DataCache.prototype.SendTradingSignalRequest = function ()
{
    this.OnTradingSignalRequestSend.Raise()     // для обновления счетчика идей если перешли на юзера где идей нет

    return Connection.vendor.SendTradingSignalRequest()
};

_DataCache.prototype.ProcessTradingSignalMessage = function (msg)
{
    if (!msg)
        return

    this.getInstrumentByInstrumentTradableID_NFL(msg.TradableInstrumentId, msg.RouteId).then(
        function (ins)
        {
            let trSystem = this.tradingSystems[msg.TradingSystemId],
                idea = new Idea(msg, ins, trSystem),
                ideaID = idea.tradingSignalId

            if (ins && !this.tradingIdeas[ideaID])
            {
                // this.GenerateDealTicketFromTradingIdea(idea)    // comment out due to #112700

                this.tradingIdeas[ideaID] = idea
                this.OnTradingIdeaAdd.Raise(idea)
            }
        }.bind(this))
}

_DataCache.prototype.ProcessTradingSignalCancelMessage = function (msg)
{
    if (!msg) return

    let ideaID = msg.TradingSignalId,
        idea = this.tradingIdeas[ideaID]

    if (idea)
    {
        this.OnTradingIdeaCancel.Raise(idea)

        delete this.tradingIdeas[ideaID]
    }
}

_DataCache.prototype.ProcessTradingSystemMessage = function (msg)
{
    if (!msg)
        return

    let tradingSystem = new TradingSystem(msg),
        id = tradingSystem.tradingSystemID

    let needUpdateRiskDisclosureText = false    // для уже подтвержденных или новых тор.систем придет SubscriptionStrategyMessage по которому произойдет обновление текста, но в случае изменения текста RD неподтвержденной системы нужно обновить текст
    if (this.tradingSystems[id])
    {
        let oldTradingSystemData = this.tradingSystems[id]
        needUpdateRiskDisclosureText = !oldTradingSystemData.accepted && oldTradingSystemData.riskDisclosureText != tradingSystem.riskDisclosureText
    }

    this.tradingSystems[id] = tradingSystem

    // this.GenerateDealTicketFromTradingIdea(tradingSystem, true)  // commented out due to #116976

    if (needUpdateRiskDisclosureText)
    {
        this.tradingSystems[id].accepted = false

        this.OnTradingSystemsChange.Raise(id)
    }
}

_DataCache.prototype.ProcessSubscriptionStrategyMessage = function (msg)
{
    if (!msg)
        return

    let tradingSystems = this.tradingSystems,
        trSystemID = msg.TradingSystemID,
        trSystemStatusAccepted = msg.StrategySubscriptionStatus == StrategySubscriptionStatus.Accepted,
        trSystemStatusDeleted = msg.StrategySubscriptionStatus == StrategySubscriptionStatus.Deleted

    if (!tradingSystems || !tradingSystems[trSystemID])
        return

    if (trSystemStatusDeleted)
        this.DeleteTradingSystem(trSystemID)
    else
    {
        tradingSystems[trSystemID].accepted = trSystemStatusAccepted

        if (!trSystemStatusAccepted)    // #116976
            this.GenerateDealTicketFromTradingIdea(tradingSystems[trSystemID], true)
    }

    this.OnTradingSystemsChange.Raise()
};

_DataCache.prototype.ProcessTradingSystemRemoveMessage = function (msg)
{
    if (!msg || msg.ID === null)
        return

    this.DeleteTradingSystem(msg.ID)
};

_DataCache.prototype.DeleteTradingSystem = function (trSystemID)
{
    let tradingSystems = this.tradingSystems

    if (!tradingSystems || !tradingSystems[trSystemID])
        return

    this.GenerateDealTicketFromTradingIdea(tradingSystems[trSystemID], true, true)

    delete this.tradingSystems[trSystemID]

    this.deleteIdeasForTradingSystem(trSystemID)

    this.OnTradingSystemUnsubscribe.Raise(trSystemID)
}

_DataCache.prototype.ProcessCustodialPlanMessage = function (msg)
{
    this.CustodialPlans.Add(msg)
}

_DataCache.prototype.ProcessCrossratesPlanMessage = function (msg)
{
    this.CrossratesPlans.Add(msg)
}

_DataCache.prototype.ProcessFundingRateMarkupPlanMessage = function (msg)
{
    this.FundingRateMarkupPlans.Add(msg);
}

_DataCache.prototype.ProcessRiskRuleWarningMessge = function (msg)
{
    let header = DirectRiskWarningMessage.RiskRuleWarningHeader;
    let userTitle = Resources.getResource("dealticket.RiskRuleWarning.UserName");
    let accTitle = Resources.getResource("dealticket.RiskRuleWarning.AccountName");
    let message = Resources.getResource("AdditionalProperty.Message");
    if (!msg.AccountID)
        return;
    let acc = this.GetAccountByNumber(msg.AccountID.toString());
    let newMsg = new DirectReportMessage();
    newMsg.Name = header;
    if (acc !== null)
    {
        newMsg.Data = new Array(3);
        newMsg.Data[0] = [userTitle, acc.userLogin];
        newMsg.Data[1] = [accTitle, acc.FullAccString];
    }

    newMsg.Data[2] = [message, msg.Text];
    newMsg.TypeId = msg.RiskRuleType;

    if (newMsg.TypeId === DirectRiskWarningMessage.PositionLossLimitRiskRuleType)
    {
        var strs = msg.Text.match(/\d+/);
        let posId = "";
        if (strs && strs.length)
            posId = strs[0];
        msg.Text = Resources.getResource("AdditionalProperty.Position closed because the loss limit was reached").replace("{0}", posId);
        newMsg.Data[2] = [message, msg.Text];

        riskWarningMessageScreenHandler.Show(newMsg);
    }

    this.HandleReportMessageDirect(newMsg);
}

_DataCache.prototype.deleteIdeasForTradingSystem = function (trSystemID)      // удаление идей по ID торговой системы (для удаления идей при отписке от системы)
{
    let ideas = this.tradingIdeas,
        ideaIDs = Object.keys(ideas)

    for (let i = 0; i < ideaIDs.length; i++)
    {
        let id = ideaIDs[i],
            idea = ideas[id]

        if (idea.tradingSystemId == trSystemID)
            delete this.tradingIdeas[id]
    }
}

_DataCache.prototype.SendSubscriptionStrategyRequest = function (tradingSystemID)
{
    return Connection.vendor.SendSubscriptionStrategyRequest(tradingSystemID)
};

_DataCache.prototype.SendTradingSignalSeenRequest = function (tradingSignalId)
{
    this.OnTradingIdeaFirstOpen.Raise()

    return Connection.vendor.SendTradingSignalSeenRequest(tradingSignalId)
};

_DataCache.prototype.ProcessTradingSignalSeenMessage = function (msg)    // когда идею открывают на другом клиенте
{
    if (!msg) return

    let ideaID = msg.TradingSignalId,
        idea = this.tradingIdeas[ideaID]

    if (idea)
    {
        this.OnTradingIdeaFirstOpen.Raise()

        idea.wasNotRead = false
    }
}

_DataCache.prototype.ProcesSubscribeResponseMessage = function (msg)
{
    if (!msg) return

    let id = msg.InstrumentId,
        route = msg.Route,
        ins = id && route ? this.getInstrument(String(id), route) : null

    if (ins)
    {
        let delay = msg.Delay,
            canGetSnapshot = msg.CanGetSnapshot,
            lvl1Subscription = msg.Service === DirectQuoteMessage.QUOTE_LEVEL1,
            hasLvl1SubscriptionErrorCode = msg.ErrorCode !== null

        if (ins.CanGetSnapshot() === null || lvl1Subscription)
        {
            ins.updateDelayAndCanGetSnapshot(delay, canGetSnapshot, hasLvl1SubscriptionErrorCode)

            let subscriptionItem = this.FQuoteCache.getSubscriptionItem(ins)
            if (subscriptionItem && subscriptionItem.HasContiniousContractItem)
            {
                let continiousContractItem = subscriptionItem.InstrumentContiniousContractItem
                if (continiousContractItem && continiousContractItem.instrument)
                    continiousContractItem.instrument.updateDelayAndCanGetSnapshot(delay, canGetSnapshot, hasLvl1SubscriptionErrorCode)   // #100762 comment
            }
        }
    }
};

_DataCache.prototype.SendSnapshotRequestMessage = function (tradableID, routeID)
{
    return Connection.vendor.SendSnapshotRequestMessage(tradableID, routeID)
};

_DataCache.prototype.ProcessSnapshotResponse = function (msg)
{
    if (!msg)
        return

    let tId = String(msg.TradableId),
        routeID = msg.RouteId,
        ins = this.getInstrument(tId, routeID)

    this.OnSnapshotResponseReceived.Raise()

    if (ins)
        snapshotScreenHandler.show(ins, msg)
};

_DataCache.prototype.getAvailableNumberOfSnapshotRequests = function ()
{
    return this.getRuleNumberValueForAccount(RulesSet.AVAILABLE_NUMBER_OF_SNAPSHOT_REQUESTS, null, 0);
}

_DataCache.prototype.ProcessCommissionPlanMessage = function (msg)
{
    let commissionPlan = new CommissionPlan(this)
    commissionPlan.Update(msg)

    this.CommissionPlans[msg.Id] = commissionPlan

    this.UpdateAccountsCommissionPlan()

    this.OnCommissionPlanUpdate.Raise(msg.Id)
}

_DataCache.prototype.SendBrokerMessageReport = function (inputMessage)    // закоментовано, згодом відновлено згідно з #110904 (для urgent BM було додано логіювання у #84587, судячи з усього зараз ця логіка вже є застарілою і непотрібною) 
{
    return Connection.vendor.SendBrokerMessageReport(inputMessage, TextMessageType.STATUS_CHANGE)
};

_DataCache.prototype.SendBrokerResponseMessage = function (brokerMsgID, value, userID, userName, responseType = null, clusterNode)
{
    if (responseType === BrokerMessageResponseType.WAS_READ)
        this.OnBMCounterMsgReceived.Raise(brokerMsgID, NotificationsCounterChangedEvent.RemoveOne)

    return Connection.vendor.SendBrokerResponseMessage(brokerMsgID, value, userID, userName, responseType, clusterNode)
};

_DataCache.prototype.SendBrokerMessageHistoryRequest = function (userID)
{
    return Connection.vendor.SendBrokerMessageHistoryRequest(userID)
}

_DataCache.prototype.ProcessBMCounterMessage = function (msg)
{
    if (msg && !MathUtils.IsNullOrUndefined(msg.UnreadIDs))
        this.OnBMCounterMsgReceived.Raise(msg.UnreadIDs)
}

_DataCache.prototype.ProcessPriceLimitsMessage = function (msg)
{
    if (!msg)
        return

    let insArr = this.getInstrumentsByTradableID(msg.TradableId)
    for (let i = 0; i < insArr.length; i++)
    {
        let ins = insArr[i]
        if (msg.HighLimit !== null)
            ins.Limits.HightLimit = msg.HighLimit
        if (msg.LowLimit !== null)
            ins.Limits.LowLimit = msg.LowLimit
    }
}

_DataCache.prototype.SendInformationMessage = function (storage)
{
    for (let i = 0; i < storage.length; i++)
    {
        if (Connection && Connection.vendor)
            Connection.vendor.SendInformationMessage(storage[i])
    }
};

_DataCache.prototype.addOrderHistoryMessage = function (msg)
{
    let ins = this.getInstrumentByTradable_ID(msg.InstrumentTradableID, msg.Route)
    if (!ins)
    {
        this.AddDeferredMessage(msg);
        return
    }

    let orderHistory = null
    try
    {
        switch (msg.EventType)
        {
            case OrderExecutionType.PENDING_NEW:
            case OrderExecutionType.PENDING_EXECUTION:
            case OrderExecutionType.PENDING_CANCEL:
            case OrderExecutionType.PENDING_REPLACE:
                return;
        }
        orderHistory = OrderHistory.fromMessage(msg, this)
    }
    catch (e)
    {
        ErrorInformationStorage.GetException(e);
        // console.error(e)
    }

    if (!orderHistory)
        return

    if (msg.EventType === OrderExecutionType.REMOVED)
    {
        this.RemovedOrderHistory(msg);
        this.RemovedOrderHistoryEvent.Raise(orderHistory)
        return;
    }

    let arr = this.orderHistoryArray
    arr.push(orderHistory)

    // TODO. Ugly.
    // разрешаю иметь не более 1000 ордеров в истории
    if (arr.length > 1100)
        arr.splice(0, 100)

    this.AddOrderHistoryEvent.Raise(orderHistory)
}

_DataCache.prototype.RemovedOrderHistory = function (msg)
{
    let arr = this.orderHistoryArray
    let i = 0;
    while (arr[i])
    {
        if (arr[i].FOrderNumber === msg.FOrderNumber)
            arr.splice(i, 1)
        else
            i++
    }
}

_DataCache.prototype.RemovedTradeHistory = function (ordId)
{
    let arr = this.filledOrdersArray
    let i = 0;
    while (arr[i])
    {
        if (arr[i].OrderId === ordId)
            arr.splice(i, 1)
        else
            i++
    }
}

_DataCache.prototype.getOrderHistoryArray = function ()
{
    return this.orderHistoryArray
}

_DataCache.prototype.ReSubscribe = function ()
{
    let ok = Object.keys(this.OrderDict);
    let l = ok.length;
    for (let i = 0; i < l; i++)
        this.OrderDict[ok[i]].SubscribeToQuotes();

    let pk = Object.keys(this.PositionDict);
    l = pk.length;
    for (let i = 0; i < l; i++)
        this.PositionDict[pk[i]].SubscribeToQuotes();
    // https://tp.traderevolution.com/entity/105525
    pk = Object.keys(this.PositionDictCorporateAction);
    l = pk.length;
    for (let i = 0; i < l; i++)
        this.PositionDictCorporateAction[pk[i]].SubscribeToQuotes();
}

_DataCache.prototype.getReport = function (key, startTimeTicks, endTimeTicks)
{
    if (key == AllowedReport.REPORT_OrderBook && this.OrderHistoryReportCompleted)
        return Promise.resolve(this.orderHistoryArray);

    if (key == AllowedReport.REPORT_TRADES && this.FilledOrdersReportCompleted)
        return Promise.resolve(this.filledOrdersArray);

    if (key == AllowedReport.REPORT_STATEMENT && this.StatementReportCompleted)
        return Promise.resolve(this.StatementArray);

    if (key == AllowedReport.REPORT_MAMACCOUNTSUMMARY && this.MamSummaryReportCompleted)
        return Promise.resolve(this.MamSummaryArray);

    let report = this.reportDict[AllowedReport.REPORT_KEY_PREFIX + key] || this.reportDict[AllowedReport.TECHNICAL_REPORT_KEY_PREFIX + key]
    if (!report)
        return Promise.resolve([]);

    let pTable = {};
    pTable.reportTypeObj = {};
    pTable.mainParamDict = {};
    pTable.reportTypeObj = report;
    //pTable.locale = App.Resources.currentLang;        // https://tp.traderevolution.com/entity/88828

    pTable.mainParamDict.instruments = "";
    pTable.mainParamDict.login = "";
    pTable.mainParamDict.fromDate = startTimeTicks;
    pTable.mainParamDict.toDate = endTimeTicks;

    let IsShowLots = 'true'
    if (key == AllowedReport.REPORT_STATEMENT)
    {
        let inLots = GeneralSettings.View.displayAmountInLots()
        IsShowLots = inLots ? 'true' : 'false'
    }

    pTable.mainParamDict.IsShowLots = IsShowLots;

    pTable.IsTechnical = true;

    return Connection.vendor.GenerateReportPromise(report.keyForServer, pTable)
        .then(this.parseReport.bind(this, key))
        .catch(function ()
        {
            let ex = new CustomErrorClass("DataCache error", "DataCache.getReport", "getReport -> GenerateReportPromise");
            ErrorInformationStorage.GetException(ex);

            console.error('Report fetch error.')
            return [];
        })
}

_DataCache.prototype.parseReport = function (key, msg)
{
    if (!msg)
        return [];

    let data = msg.Code === DirectMessageCode.CODE_REPORT_GROUP_RESPONSE ?
        this.getRowsAndColumnsFromReportResponse(msg) :         // new scheme from DirectMsg
        this.getRowsAndColumnsFromOldXMLMessage(msg)            // old scheme from msg with xmlString

    let rows = data.rows,
        columns = data.columns,
        objArr = []

    let needReqIns = key === AllowedReport.REPORT_OrderBook || key === AllowedReport.REPORT_TRADES || key === AllowedReport.REPORT_MAMACCOUNTSUMMARY;
    let hasEmptyInstr = false;

    for (let i = 0, len = rows.length; i < len; i++)
    {
        let r = rows[i];
        let C = r.Cells;
        if (C[0].Label === AllowedReport.TOTAL_ROW_LABEL)
            continue;

        let item = null

        switch (key)
        {
            case AllowedReport.REPORT_OrderBook:
                item = new OrderHistory()
                break;
            case AllowedReport.REPORT_TRADES:
                item = new NewTrade(this, null)
                break;
            case AllowedReport.REPORT_STATEMENT:
                item = new Statement()
                break;
            case AllowedReport.REPORT_MAMACCOUNTSUMMARY:
                item = new AccountOperationReport();
                break;
        }

        let eDay = 0, eMonth = 0, eYear = 0, putCall = -1, strikeprice = -1;
        let InsName = "";
        for (let j = 0, len_j = columns.length; j < len_j; j++)
        {
            let Label = C[j].Label.trim();
            let Value = C[j].Value;
            switch (columns[j].Name)
            {
                //                |
                // Orders history |
                //                V
                case "order_book_account":
                    let account = this.GetAccountByIdOrName(Label);
                    if (account == null)
                        account = this.GetAccountByNumber(Label);
                    item.Account = account;
                    break;
                case "order_book_symbol": InsName = Label; break;
                case "order_book_orderid": item.OrderNumber = Label; break;
                case "order_book_route": item.Route = Label; break;
                case "order_book_modified_by": item.TraderId = Label; break;
                case "order_book_event": item.eventTypeRaw = AllowedReport.EventFromString(Label); break;
                case "order_book_quantity": item.Quantity = Value; break;
                case "order_book_price": item.Price = Value; break;
                case "order_book_stop_price": item.StopLimit = Value; break;
                case "order_book_disclosed_qty": item.DisclosedQty = Value; break;
                case "order_book_datetime": item.Date = item.Time = new Date(parseInt(Label)); break;
                case "order_book_side": item.BuySell = OrderExecutor.getBuySell(Label); break;
                case "order_book_type": item.OrderType = OrderExecutor.getOrderTypeByName(Label); break;
                case "order_book_tif":
                    let tifParts = Label.split(' '),
                        tifType = OrderTif[tifParts[0]],
                        date = tifParts[1] ? tifParts[1] : null,
                        tif = new TIF(tifType, date)

                    item.TimeInForce = tif;
                    break;
                case "tradingmode": item.ProductType = ProductType[Label]; break;
                case "external_order_id": item.ExternalOrderId = Label; break; //TODO 88232 заменил item.ExternalId на item.ExternalOrderId
                case "expday": item.ExpDay = Label ? Label : eDay; break;
                case "expmonth": item.ExpMonth = Label ? Label : eMonth; break;
                case "expyear": item.ExpYear = Label ? Label : eYear; break;
                case "derivativetype": item.PutCall = Label ? Label : putCall; break;
                case "strikeprice": item.StrikePrice = Label ? Label : strikeprice; break;
                case "order_book_symbol_type": item.SymbolType = Label; break;

                //               |
                // Filled orders |
                //               V
                case "account":
                    let acc = this.GetAccountByIdOrName(Label);
                    if (acc == null)
                        acc = this.GetAccountByNumber(Label);
                    item.Account = acc;
                    break;
                case "sender": item.Login = Label; break;
                case "instrumentname": InsName = Label; break;
                case "orderid": item.OrderId = Label; break;
                case "tradeid": item.TradeId = Label; break;
                case "routename": item.Route = Label; break;
                case "amount": item.Amount = Value; break;
                case "price": item.Price = Value; break;
                case "crossprice": item.OpenCrossPrise = Value; break;
                case "time": item.Time = new Date(parseInt(Label));; break;
                case "profit": item.PnL = Value; break;
                case "type": item.OrderType = OrderExecutor.getOrderTypeByName(Label); break;
                case "executionfee": item.Comission = -Value; break;    //#89723
                case "extprice": item.ExternalPrice = Value; break;
                case "tradevolume": item.TradeVolume = Value; break;  //???????????????
                case "tradableinstrumentid": item.InstrumentTradableID = Value; break;
                case "operation": item.BuySell = OrderExecutor.getBuySell(Label); break;
                case "externalid": item.ExternalTradeId = Label; break; //TODO 88232 заменил item.ExtId на item.ExternalTradeId                
                case "rebates": item.Rebates = Value; break;
                case "execvenue": item.ExecutionVenue = Label; break;
                case "exchange": item.TradingExchange = Label; break;
                case "symbol_type": item.SymbolType = Label; break;
                //            |
                // MAM Summary|
                //            V
                case "accountid": item.AccountId = Value; break;
                case "account_name": item.AccountName = Value; break;
                case "date": item.Date = Value; break;
                case "operationtypeid": item.OperationTypeId = parseInt(Value); break;
                case "operationtype_name": item.OperationTypeName = Value; break;
                case "tradableinstrument_name": item.TradableInstrumentName = Value; break;
                case "open_price": item.OpenPrice = Value; break;
                case "close_price": item.ClosePrice = Value; break;
                case "currency_name": item.CurrencyName = Label; break;
                case "side": item.IsBuy = Value === "Buy"; break;
                //            |
                // Common     |
                //            V
                case "leverage": item.Leverage = Value; break;
                case "description": item.Description = Label; break;
                //OR
                case "Description": item.Description = Label; break;
                //            |
                // Statement  |
                //            V
                case "createdat": item.Date = new Date(parseInt(Label)); break;
                case "operationtype": item.OperationType = Label; break;
                case "accountoperationid": item.OperationID = Label; break;
                case "openprice": item.OpenPrice = Label; break;
                case "closeprice": item.ClosePrice = Label; break;
                case "opentime": item.TradeOpeningDate = new Date(parseInt(Label)); break;
                case "quantity": item.Quantity = Value; break;
                case "currency": item.Currency = Label; break;
                case "crossprice": item.FXrate = Label; break;

                case "order_book_bought": item.boughtString = Label; break;
                case "order_book_sold": item.soldString = Label; break;

                case "trades_bought": item.boughtString = Label; break;
                case "trades_sold": item.soldString = Label; break;

                // default: console.log(columns[j].Name + "    " + Label);
            }
        }
        let _Route = this.getRouteByTextName(item.Route);

        let insStringName = InsName

        let trId = item.InstrumentTradableID ? item.InstrumentTradableID : InsName
        let rId = _Route ? _Route.RouteId : item.Route
        if (rId)
            InsName = InstrumentUtils.GetFullName(trId.toString(), rId.toString());
        item.Instrument = this.getInstrumentByName(InsName);
        if (item.Instrument == null)
        {
            if (insStringName)
                item.InstrumentStr = insStringName;
            hasEmptyInstr = true;
        }

        if (key === AllowedReport.REPORT_TRADES)
            if (item.OpenCrossPrise !== 0)
            {
                item.PnL = item.PnL / item.OpenCrossPrise;
                item.Comission = item.Comission / item.OpenCrossPrise;
                // rebates
            }

        if (item.BuySell === -1)
            continue;

        objArr.push(item);
    }

    objArr.sort(function (a, b) { return a.Date > b.Date ? 1 : -1; })


    let insPromises = [];
    let reqsObjArr = {};

    let finishFunc = (key, objArr) =>
    {
        if (key == AllowedReport.REPORT_OrderBook)
        {
            this.orderHistoryArray = objArr;
            this.OrderHistoryReportCompleted = true;
        }
        else if (key == AllowedReport.REPORT_TRADES)
        {
            this.filledOrdersArray = objArr;
            this.FilledOrdersReportCompleted = true;
            this.filledOrdersCurrency = this.getFilledOrdersCurrency();
        }
        else if (key == AllowedReport.REPORT_STATEMENT)
        {
            this.StatementArray = objArr;
            this.StatementReportCompleted = true;
        }
        else if (key == AllowedReport.REPORT_MAMACCOUNTSUMMARY)
        {
            this.MamSummaryArray = objArr;
            this.MamSummaryReportCompleted = true;
        }
    };

    if (needReqIns && hasEmptyInstr)
    {
        for (let i = 0; i < objArr.length; i++)
        {
            let item = objArr[i];
            if (item.Instrument)
                continue;

            let trId = item.InstrumentTradableID
            let _Route = this.getRouteByTextName(item.Route);
            let rId = _Route ? _Route.RouteId : item.Route
            if (!trId)
                continue;

            let InsName = InstrumentUtils.GetFullName(trId.toString(), rId?.toString());

            let exist = !!reqsObjArr[InsName]
            if (!exist)
                reqsObjArr[InsName] = new Array();

            let arr = reqsObjArr[InsName];
            arr.push(item)

            reqsObjArr[trId] = arr;

            if (exist)
                continue

            let tmpR = this.getInstrumentByInstrumentTradableID_NFL(trId, rId, null, true)
            insPromises.push(tmpR)
        }

        return Promise.all(insPromises).then(function (instruments)
        {
            for (let i = 0; i < instruments.length; i++)
            {
                let ins = instruments[i]
                if (!ins)
                    continue;

                let key = ins.GetInteriorID();

                let arrI = reqsObjArr[key] || reqsObjArr[ins.InstrumentTradableID]
                if (!arrI)
                    continue

                for (let j = 0; j < arrI.length; j++)
                    arrI[j].Instrument = ins
            }

            finishFunc(key, objArr);

            return objArr;
        })
    }

    finishFunc(key, objArr);

    return objArr;
}

_DataCache.prototype.getRowsAndColumnsFromOldXMLMessage = function (reportResponseMessage)   // From XMLString
{
    if (!reportResponseMessage)
        return [];
    // TODO. Remove jQuery.
    let xml = $.parseXML(reportResponseMessage.xmlString);
    let table = xml.childNodes[0];
    let th = table.getElementsByTagName('th')[0]
    let trArr = table.getElementsByTagName('tr')

    if (!th)
        return [];

    let columns = [];

    let thChildren = th.childNodes
    for (let i = 0, len = thChildren.length; i < len; i++)
    {
        let headerItem = thChildren[i]
        let type = null
        if (!headerItem.getAttribute)
            continue;

        switch (headerItem.getAttribute('type'))
        {
            case 'FLOAT':
            case 'NUMBER':
                type = QuickTableComparingType.Double
                break
            case 'INTEGER':
                type = QuickTableComparingType.Int
                break
            case 'DATE':
                type = QuickTableComparingType.DateTime
                break
            default:
                type = QuickTableComparingType.String
        }

        columns.push({
            Type: type,
            Name: headerItem.textContent
        })
    }

    let rows = []

    for (let i = 0, len = trArr.length; i < len; i++)
    {
        let tr = trArr[i]
        let cells = []
        rows.push({ Cells: cells })

        let tdArr = tr.childNodes
        for (let j = 0, j_len = tdArr.length; j < j_len; j++)
        {
            let td = tdArr[j]
            let rawVal = td.textContent
            let val = this.getReportValueByType(rawVal, columns[j].Type)

            cells.push({ Label: rawVal.toString(), Value: val })
        }
    }

    return {
        rows: rows,
        columns: columns
    }
}

_DataCache.prototype.getRowsAndColumnsFromReportResponse = function (msg)
{
    let table = msg.Tables && msg.Tables.length ? msg.Tables[0] : null

    if (!table) return null

    let columns = table.ReportColumns,
        rows = [],
        reportRows = table.ReportRows,
        key = table.ReportName;

    for (let i = 0, len = reportRows.length; i < len; i++)
    {
        let reportRow = reportRows[i]
        let cells = []
        rows.push({ Cells: cells })

        for (let j = 0; j < reportRow.length; j++)
        {
            let rawVal = reportRow[j]
            let val = this.getReportValueByType(rawVal, columns[j].Type)

            cells.push({ Label: rawVal.toString(), Value: val })
        }
    }

    return {
        rows: rows,
        columns: columns
    }
}

_DataCache.prototype.getReportValueByType = function (rawVal, type)
{
    let val = null

    switch (type)
    {
        case QuickTableComparingType.Double:
            val = parseFloat(rawVal)
            break
        case QuickTableComparingType.Int:
        case QuickTableComparingType.Long:
            val = parseInt(rawVal)
            break
        default:
            val = rawVal
    }

    return val
}

let MarginCounterLocal = {};
MarginCounterLocal.GetProfitLossWithUnsettledProfitProperties = function (position, profit)
{
    if (profit == 0 || position == null || position.Account == null || position.Instrument == null || position.Account.RiskPlan == null)
        return profit;

    let rp = position.Account.RiskPlan;

    if (!rp)
        return profit;

    var riskSettings = rp.GetRisksForInstrument(position.Instrument.GetInteriorID());

    if (riskSettings == null)
        return profit;

    var item = riskSettings.cache[position.ProductType];

    // Profit
    if (profit > 0)
    {
        if (item != null && item.CalculateMarginType !== null && (item.CalculateMarginType == MarginTypes.MARGIN_FULL || item.CalculateMarginType == MarginTypes.MARGIN_STOCK))
            return 0;

        if (position.Instrument.OptionTradingStyle == OptionTradingStyle.Premium)
            return position.BuySell == OperationType.Buy ? profit : 0;

        if (position.Instrument.OptionTradingStyle == OptionTradingStyle.MarkToMarket)
        {
            if (position.BuySell == OperationType.Sell)
                return 0;
            else
            {
                if (item.MarginMode !== null && item.MarginMode == MarginModes.USE_PROFIT_LOSS)
                    return profit;
                else
                    return 0;
            }
        }

        if (item != null && item.MarginMode !== null && (item.MarginMode == MarginModes.USE_PROFIT_IN_MARGIN_AVAILABLE || item.MarginMode == MarginModes.USE_PROFIT_LOSS))
            return profit;

        // // #62561
        // if (item == null || (item.UseUnsettledProfitAndOpenProfitInMarginAvailable != null && item.UseUnsettledProfitAndOpenProfitInMarginAvailable))
        //     return profit;
    }
    // Loss
    else
    {
        if (position.Instrument.OptionTradingStyle == OptionTradingStyle.MarkToMarket)
            return position.BuySell == OperationType.Sell ? profit : 0;
        else
        {
            if (item != null && item.CalculateMarginType !== null)
            {
                if (item.CalculateMarginType == MarginTypes.MARGIN_FULL)
                    return position.BuySell == OperationType.Sell ? profit : 0;

                if (item.CalculateMarginType == MarginTypes.MARGIN_STOCK)
                    return 0;
            }

            if (position.Instrument.OptionTradingStyle == OptionTradingStyle.Premium)
                return position.BuySell == OperationType.Sell ? profit : 0;
        }
        return profit; // #62561
    }

    return 0;
}

_DataCache.prototype.IsSLTP = function (order)
{
    return !order.Active || (order.BoundTo != null && this.getPositionByBindTo(order.BoundTo) != null);
}

/// <summary>
/// Поиск ассетбаланса, правильный его пользовать везде
/// </summary>
_DataCache.prototype.GetAssetBalanceCorrect = function (account, instrument, isBuy)
{
    if (account === null)
        return null;

    return account.GetAssetBalanceCorrect(instrument, isBuy);
}

/// <summary>
/// найти parent позицию для сл или тп, основываясь на ссылке bindto
/// для ван позишн - нужно искать через getPositionByOpenOrderId.
/// для мультипозиций - можно искать getPositionByOpenOrderId и через positions[]
/// </summary>
_DataCache.prototype.getPositionByBindTo = function (BoundTo)
{
    if (!BoundTo || BoundTo == "-1")
        return null;
    /// Почему я не использую getPositionByOpenOrderId вместо этого метода
    /// 1. чтобы не сломать логику поиска ордеров/позиций
    /// 2. тут positions[] отрабатывает быстрее чем getPositionByOpenOrderId, а у нас большинство поз - мульти.
    let pos = this.PositionDict[BoundTo];
    // if (!pos)
    //     pos = this.getPositionByOpenOrderId(BoundTo);
    return pos || null;
}
//TODO
// DataCache.prototype.getPositionByOpenOrderId = function (key)
// {
//     Position pos = null;
//     positionsByOpenOrderID.TryGetValue(key, out pos);
//     return pos;
// }

// positionsByOpenOrderID это наш PositionDict

_DataCache.prototype.ProcessSourceMappingMessage = function (msg)
{
    this.SourceCollection.UpdateByMessage(msg)
}

_DataCache.prototype.GetSourceName = function (id)
{
    return this.SourceCollection.GetSourceName(id)
}

_DataCache.prototype.GetDataSourceInstrument = function (instrument)
{
    if (!instrument || instrument.IsEmpty)
        return null

    if (instrument.DataSourceInstrument)
        return instrument.DataSourceInstrument

    let InstrumentId = instrument.InstrumentTradableID,
        route = instrument.Route,
        dataSourceTrInsID = instrument.GetDataSourceTradableId(),
        dataSourceRouteID = instrument.GetDataSourceRouteId()

    if (dataSourceTrInsID == -1)
        dataSourceTrInsID = InstrumentId
    if (dataSourceRouteID == -1)
        dataSourceRouteID = route

    let dsInteriorID = dataSourceTrInsID + InstrumentUtils.SEPARATOR + dataSourceRouteID,
        dsIns = this.Instruments[dsInteriorID] || null

    // if (dsIns && dsIns.NeedToHide())    // подписываемся на котировки по видимому инструменту
    //     dsIns = this.Instruments[dataSourceTrInsID + DataCache.SEPARATOR + dsIns.TradableRoutesArray[0]]

    if (dsIns)
        instrument.DataSourceInstrument = dsIns      // кэшируем

    return dsIns
}

_DataCache.prototype.GetInstrumentsByDataSource = function (instrument)
{
    if (!instrument || instrument.IsEmpty)
        return null

    let interiorID = instrument.GetInteriorID()

    return this.InstrumentsBySourceRouteIdTradableId[interiorID] || []
}

_DataCache.prototype.SendDataSourceSubscriptionMessage = function (instrument, toSubscribe, quoteType)
{
    if (!instrument || !instrument.IsDataSourceEnable())
        return

    let dsIns = this.GetDataSourceInstrument(instrument)

    let InstrumentId = instrument.InstrumentTradableID,
        dataSourceTrInsID = dsIns.InstrumentTradableID,
        dataSourceRouteID = dsIns.Route,
        subscriptionActionType = toSubscribe ? SubscribeType.SUBSCRIBE : SubscribeType.UNSUBSCRIBE

    if (Connection.vendor)      // эта проверка необходима для работы Unit-тестов
        Connection.vendor.SendDataSourceSubscriptionMessage(dataSourceTrInsID, dataSourceRouteID, quoteType, subscriptionActionType, InstrumentId)
};

_DataCache.prototype.IsPortfolioAllowed = function (account)
{
    if (!account)
        return false

    return this.isAllowedForAccount(RulesSet.FUNCTION_PORTFOLIO_MANAGEMENT, account)
}

_DataCache.prototype.IsPortfolioAllowedForAnyAccount = function ()
{
    let pCache = this.PortfolioCache,
        accs = this.getAccounts()

    for (let accID in accs)
        if (this.IsPortfolioAllowed(accs[accID]) && pCache.IsInvestorAccount(accID))
            return true

    return false
}

_DataCache.prototype.AllAccountsAreInvestors = function ()
{
    let pCache = this.PortfolioCache,
        accs = this.getAccounts()

    if (!Object.keys(accs).length)
        return false;

    for (let accID in accs)
        if (!this.IsPortfolioAllowed(accs[accID]) || !pCache.IsInvestorAccount(accID))
            return false

    return true
}

_DataCache.prototype.NeedRedirectToPortfolio = function ()         // возвращает true или false в зависимости от того произошел ли редирект на портфолио
{
    if (!ApplicationInfo.myportfolio && this.AllAccountsAreInvestors())
    {
        LocalStorage.setTokenForPortfolio(Connection.vendor.loginParams.accessToken);
        window.location.href = window.location.href + 'myportfolio'

        return true
    }

    return false
}

_DataCache.prototype.AllowCustomSLTPTriggerPriceForUser = function ()         // #109798
{
    return this.isAllowedForMyUser(RulesSet.FUNCTION_USER_ALLOW_CUSTOM_SLTP_TRIGGER_PRICE)
}

_DataCache.prototype.AllowCustomSLTPTriggerPriceForInstrumentType = function (insType = null)         // #109798
{
    if (insType == null)
        return false

    let instrumentTypesList = []
    this.FindInstrumentTypes(insType, instrumentTypesList)

    for (let key in this.Instruments)
    {
        let ins = this.Instruments[key]
        if (ins.IsAllowCustomSLTPTriggerPrice && instrumentTypesList.indexOf(ins.InstrType) >= 0)
            return true
    }

    return false
}

_DataCache.prototype.AllowLoggingChanges = function ()         // #111072
{
    return this.isAllowedForMyUser(RulesSet.ALLOW_CHANGES_LOGGING)
}

_DataCache.prototype.GetAllInstrumentsExceptExpired = function ()         // return object like this.Instruments but with no options whose expiration date is in the past of all  #113208
{
    let result = {}, allIns = this.Instruments

    for (let id in allIns)
    {
        let ins = allIns[id]
        if (!ins.ExpDate || ins.ExpDateReal.getTime() >= Date.now())
            result[id] = ins
    }

    return result
}

_DataCache.prototype.GenerateDealTicketFromTradingIdea = function (data, isTradingSystem, isUnsubscribe)
{
    if (!data)  // data - идея или система
        return

    isTradingSystem = !!isTradingSystem
    isUnsubscribe = !!isUnsubscribe

    let textMsg = Resources.getResource(isTradingSystem ?
        (isUnsubscribe ? 'panel.Ideas.UnsubscribeDealTicketText' : 'panel.Ideas.SubscribeDealTicketText') :
        'panel.Ideas.DealTicketText') + data.name   // и у идеи и у системы есть name

    let message = { Label: 'Message', Value: textMsg },
        header = Resources.getResource('panel.Ideas.DealTicketHeader')
    this.NewMessage(new DirectReportMessage(header, [[message.Label, message.Value]]))
}

_DataCache.prototype.GenerateExerciseRequestMessageDealTicket = function (pos, isCancelRequest, placedFrom)
{
    if (!pos)
        return

    let account = {
        Label: Resources.getResource('reports.Account'),
        Value: pos.Account ? pos.Account.FullAccString : null
    }

    let ins = {
        Label: Resources.getResource('InstrumentDetailsPanel.Symbol'),
        Value: pos.Instrument ? pos.Instrument.DisplayName() : null
    }

    let posID = {
        Label: Resources.getResource('reports.posId'),
        Value: pos.PositionId ? pos.PositionId : null
    }

    // let date = {
    //     Label: Resources.getResource('screen.reports.reportHeaders.date'),
    //     Value: Resources.getResource('reports.' + Utils.getKeyByValue(OrderExecutor.PlacedFrom, placedFrom))
    // }

    let placedFromData = {
        Label: Resources.getResource('reports.Send from'),
        Value: Resources.getResource('reports.' + Object.getKeyByValue(PlacedFrom, placedFrom))
    }

    let msgRows = [account, ins, posID, placedFromData],
        len = msgRows.length,
        resultDataArr = []

    for (let i = 0; i < len; i++)
    {
        let lbl = msgRows[i];

        if (lbl && (lbl.Value || lbl.Value === 0))
            resultDataArr.push([lbl.Label, lbl.Value]);
    }

    let header = Resources.getResource(!!isCancelRequest ? 'screen.PositionExerciseCancelDealticket.header' : 'screen.PositionExerciseDealticket.header')

    let reportMessage = new DirectReportMessage(header, resultDataArr)
    reportMessage.SkipOnTicketViewer = true          // чтобы не показывать в TicketViewer 

    this.NewMessage(reportMessage)
}
_DataCache.prototype.GetYouTubeExternalLinks = function ()                                           // #114288
{
    return this.ExternalLinksCache ? this.ExternalLinksCache.getYouTubeExternalLinks() : null;
}

_DataCache.prototype.isAllowedMutualCloseByPosIdArr = function (positionsIDArray)                    // #115483 Mutual closing can be carried out when two positions for the same instrument are selected in the Positions panel.
{
    if (!positionsIDArray || positionsIDArray.length != 2)
        return false;   // Two positions must be selected;

    const pos1 = this.getPositionById(positionsIDArray[0]);
    const pos2 = this.getPositionById(positionsIDArray[1]);

    if (pos1.Instrument != pos2.Instrument)
        return false;   // Positions to close must be selected for the same instrument;

    if (pos1.BuySell == pos2.BuySell)
        return false;   // These positions must be oppositely directed (i.e. both Long and Short positions must be selected);

    if (pos1.Account != pos2.Account)
        return false;   // All positions must belong to the same account.

    const partialCloseIsForbidden = !this.isAllowedForAccount(RulesSet.FUNCTION_PARTIAL_CLOSE, pos1.Account);
    if (partialCloseIsForbidden && pos1.Amount != pos2.Amount)
        return false    // In case the partial close is forbidden for this account, then the amount on both sides (Long and Short) must be equal. 

    return true;
}

_DataCache.prototype.getWMAccountDetailsPieChartData = function (acctNumber)
{
    let positionDict = this.PositionDict;
    if (!positionDict)
        return

    let allsumm = 0;
    let groupdata = {};
    let currentAccount = this.GetAccountByNumber(acctNumber);
    let projectedBalance = Account.GetAccountFeature(AccountFeature.ProjectedBalance, currentAccount, currentAccount.assetBalanceDefault);
    if (!projectedBalance)
        return

    for (let posId in positionDict)
    {
        let position = positionDict[posId];
        if (currentAccount.AcctNumber !== position.Account.AcctNumber)
            continue

        let isLong = position.BuySell === OperationType.Buy;
        let instr = position.Instrument;
        let instrType = instr.InstrType;
        if (instr.CFD)
            instrType = InstrumentTypes.EQUITIES_CFD;
        let instrName = instr.getTypeString();
        let productType = position.ProductType;
        let marginType = RiskPlan.GetCalculateMarginTypeNullable(instr, productType);
        let margin = RiskPlan.GetMargin(instr, productType, 0);
        let shortMargin = RiskPlan.GetShortMargin(instr, productType, 0);
        let coef = isLong ? margin : shortMargin;
        let value = position.getPositionValue(true);
        let newValue = 0;

        if (!value)
            continue

        switch (marginType)
        {
            // case MarginTypes.MARGIN_PT_RISK_MODULE:
            // case MarginTypes.MARGIN_PT_RISK_MODULE_EXPOSURE:                
            // case MarginTypes.MARGIN_FIXED:
            // case MarginTypes.TIERED_PRICE_BASED_CUSTOM_COEF:

            case MarginTypes.MARGIN_STOCK: //stok prepaid
            case MarginTypes.MARGIN_FULL: //Securities fully paid
            case MarginTypes.LONG_OPTIONS_FULLY_PAID_MARGIN: //Long options fully paid
                newValue = value;
                break
            case MarginTypes.PRICE_BASED_MULTIPLE_COEF: //Price based multiple coef
            case MarginTypes.MARGIN_PFS: //Price based custom coef
                newValue = value * (coef.Initial || (coef.Leverage / 100));
                break
        }

        if (groupdata[instrType])
        {
            groupdata[instrType].value += newValue;
        }
        else
        {
            let groupsumm = {};
            groupsumm.value = newValue;
            groupsumm.name = instrName;
            groupdata[instrType] = groupsumm;
        }

        allsumm += newValue;
    }

    function sortValue (a, b)
    {
        if (a.value < b.value) return 1;
        if (a.value > b.value) return -1;
        return 0;
    }

    let arrResult = [];
    for (let insId in groupdata)
    {
        let resultValue = (groupdata[insId].value / projectedBalance) * 100;
        let resultObj = { name: groupdata[insId].name, value: resultValue };
        arrResult.push(resultObj);
    }

    let valueCash = ((projectedBalance - allsumm) / projectedBalance) * 100;
    let cashObj = { name: "Cash", value: valueCash };
    arrResult.push(cashObj);

    if (arrResult.length > 1)
    {
        arrResult.sort(sortValue);

        let arrSort = [];
        let sortObj = { name: Resources.getResource("panel.TerceraSymbolLookupDropDownForm.Other"), value: 0 };

        for (let i = 0; i < arrResult.length; i++)
        {
            let index = 4;
            if (arrResult[i].value > 0.01)
            {
                if (index > i)
                    arrSort.push(arrResult[i]);
                else
                    sortObj.value += arrResult[i].value;
            }
        }

        if (sortObj.value !== 0)
            arrSort.push(sortObj);

        arrResult = arrSort;
    }

    return arrResult
}

_DataCache.prototype.PositionExerciseStatusChanged = function (posId)
{
    if (messageBoxHandler?.Show)
    {
        const position = this.getPositionById(posId);
        let infoText = Resources.getResource('screen.PositionExerciseComplete.text')

        infoText = infoText.replace('{1}', position.PositionId).replace('{2}', position.Account.FullAccString)

        let addData = { cantActionBeforeClose: true, hideCloseBtn: true, okText: Resources.getResource('general.messageBox.ok') }   // #94234

        messageBoxHandler.Show(
            Resources.getResource('screen.PositionExerciseComplete.header'),
            infoText,
            messageBoxHandler.msgType.Info, null, null, false, true, null, addData);
    }
    else
        this.OnPositionExerciseStatusChanged.Raise(posId);
}

_DataCache.prototype.SendCloseAccountRequestMessage = function ()
{
    return Connection.vendor.SendCloseAccountRequestMessage();
}

_DataCache.prototype.SendCloseAccountCancelRequestMessage = function (closeAccountRequestID)
{
    return Connection.vendor.SendCloseAccountCancelRequestMessage(closeAccountRequestID);
}

_DataCache.prototype.ProcessCloseAccountResponseMessage = function (msg)
{
    const oldCloseAccountRequestID = this.closeAccountRequestID;

    switch (msg.status)
    {
        case CloseAccountResponseStatus.New:
            this.closeAccountRequestID = msg.closeAccountRequestID;
            break;
        case CloseAccountResponseStatus.Approve:
            this.closeAccountApproved = true;
        case CloseAccountResponseStatus.Rejected:
        case CloseAccountResponseStatus.Cancelled:
            if (msg.closeAccountRequestID === this.closeAccountRequestID)
                this.closeAccountRequestID = null;
            break;
    }

    if (oldCloseAccountRequestID != this.closeAccountRequestID)
        this.OnChangeCloseAccountRequestID.Raise(msg);
}

export const DataCache = new _DataCache();